/**
 * ADJUDON API Client
 * 
 * High-level API wrapper that:
 * - Uses Axios client for real backend calls
 * - Falls back to mock data when ENV.USE_MOCK_DATA is true
 * - Provides type-safe methods for all endpoints
 */

import type * as API from '../types/api';
import { mockData } from './mockData';
import { useMockData, ENV } from '../config/env';
import apiClient, { APIError, onAPIError, TokenManager, uploadFile, downloadFile, circuitBreaker } from './apiClient';
import type { ErrorCategory } from './apiClient';

// Re-export for backwards compatibility
export { APIError, onAPIError, TokenManager, circuitBreaker };
export type { ErrorCategory };

// Helper to check if we should use mock data
const shouldUseMockData = () => useMockData();

// Single source for the methodology version surfaced in calibration mocks.
// Must match backend/services/calibrationService.js CURRENT_METHODOLOGY_VERSION
// so demo-mode UI is indistinguishable from production for this field.
const MOCK_METHODOLOGY = 'phase2-2026-05-09';

// ── Demo-Persona „Nordlicht" — Kalibrierungs-Profil pro KI-System ──────────
// (SSOT: docs/DEMO_PERSONA.md). 9 Monate Betrieb mit gelabelten Outcomes:
// solide ECE/Brier; agent_pricing zeigt die Drift-Schwäche (degraded + 1 offener
// Drift-Alarm) → schlechtere Reliability dort. agent_churn (idle) hat keine Daten.
// 'org' = gewichtetes Aggregat über die Systeme mit Verkehr.
const PERSONA_CALIB: Record<string, { ece: number; brier: number; samples: number; drift: boolean; driftReason: string | null; lastRefit: string }> = {
  org:            { ece: 0.038, brier: 0.082, samples: 41200, drift: false, driftReason: null, lastRefit: '2026-05-30T02:00:00Z' },
  agent_fraud:    { ece: 0.028, brier: 0.064, samples: 12800, drift: false, driftReason: null, lastRefit: '2026-05-30T02:00:00Z' },
  agent_uw_leben: { ece: 0.034, brier: 0.071, samples: 9600,  drift: false, driftReason: null, lastRefit: '2026-05-30T02:00:00Z' },
  agent_pricing:  { ece: 0.091, brier: 0.138, samples: 6100,  drift: true,  driftReason: 'ECE-Anstieg > Schwelle (0.075) über 3 Refits — Verteilungs-Drift Komposit-Tarifierung', lastRefit: '2026-04-18T02:00:00Z' },
  agent_doc:      { ece: 0.022, brier: 0.048, samples: 8200,  drift: false, driftReason: null, lastRefit: '2026-05-30T02:00:00Z' },
  agent_chat:     { ece: 0.041, brier: 0.089, samples: 4500,  drift: false, driftReason: null, lastRefit: '2026-05-29T02:00:00Z' },
};
const personaCalib = (agentId: string) => PERSONA_CALIB[agentId] ?? PERSONA_CALIB.org;
// agent_churn (idle, unklassifiziert) hat keine gelabelten Outcomes → leeres Bild.
const isEmptyCalibAgent = (agentId: string) => agentId === 'agent_churn';

class AdjudonAPI {
  /**
   * Normalize API responses so frontend works with both:
   * - direct payloads
   * - envelope payloads: { success, data, ... }
   */
  private normalizeResponse<T>(raw: any): T {
    if (!raw || typeof raw !== 'object') {
      return raw as T;
    }

    if (Object.prototype.hasOwnProperty.call(raw, 'success')) {
      if (Object.prototype.hasOwnProperty.call(raw, 'data')) {
        return raw.data as T;
      }

      const stripped = { ...raw };
      delete (stripped as any).success;
      delete (stripped as any).message;
      delete (stripped as any).error;
      delete (stripped as any).code;
      if (Object.keys(stripped).length > 0) {
        return stripped as T;
      }
    }

    return raw as T;
  }

  /**
   * Generic GET request
   */
  private async get<T>(endpoint: string, params?: any): Promise<T> {
    const response = await apiClient.get<T>(endpoint, { params });
    return this.normalizeResponse<T>(response.data);
  }

  /**
   * Generic POST request
   */
  private async post<T>(endpoint: string, data?: any): Promise<T> {
    const response = await apiClient.post<T>(endpoint, data);
    return this.normalizeResponse<T>(response.data);
  }

  /**
   * Generic PUT request
   */
  private async put<T>(endpoint: string, data?: any): Promise<T> {
    const response = await apiClient.put<T>(endpoint, data);
    return this.normalizeResponse<T>(response.data);
  }

  /**
   * Generic PATCH request
   */
  private async patch<T>(endpoint: string, data?: any): Promise<T> {
    const response = await apiClient.patch<T>(endpoint, data);
    return this.normalizeResponse<T>(response.data);
  }

  /**
   * Generic DELETE request
   */
  private async delete<T>(endpoint: string, data?: unknown): Promise<T> {
    // BUG-001-Fix: axios traegt DELETE-Bodies via config.data — vorher wurde
    // das zweite Argument still verworfen (deleteMyAccount sandte currentPassword/
    // confirmation NIE -> 400 in Produktion).
    const response = await apiClient.delete<T>(endpoint, data !== undefined ? { data } : undefined);
    return this.normalizeResponse<T>(response.data);
  }
  
  // For now, all methods just delegate to mock data
  // This can be replaced with real axios calls when backend is ready
  
  // ============================================================================
  // AUTHENTICATION
  // ============================================================================

  async getCurrentUser(): Promise<API.User> {
    if (shouldUseMockData()) {
      return mockData.getCurrentUser();
    }
    return this.get<API.User>('/api/auth/me');
  }

  async login(credentials: API.LoginRequest): Promise<API.LoginResponse> {
    if (shouldUseMockData()) {
      return mockData.login(credentials);
    }
    return this.post<API.LoginResponse>('/api/auth/login', credentials);
  }

  async requestSignup(data: { name: string; email: string; organizationName: string }): Promise<{ message: string }> {
    if (shouldUseMockData()) {
      return { message: "Mock email sent" };
    }
    return this.post<{ message: string }>('/api/auth/request-signup', data);
  }

  async completeSignup(data: { token: string; password: string }): Promise<API.LoginResponse> {
    if (shouldUseMockData()) {
      return mockData.signup({ name: 'Mock User', email: 'mock@example.com', password: data.password, company: 'Mock Org' });
    }
    return this.post<API.LoginResponse>('/api/auth/complete-signup', data);
  }

  async logout(): Promise<void> {
    if (shouldUseMockData()) {
      return mockData.logout();
    }
    return this.post('/api/auth/logout');
  }

  async forgotPassword(email: string): Promise<{ success: boolean }> {
    if (shouldUseMockData()) {
      return mockData.forgotPassword(email);
    }
    return this.post<{ success: boolean }>('/api/auth/forgot-password', { email });
  }

  async resetPassword(data: { token: string; password: string }): Promise<{ success: boolean }> {
    if (shouldUseMockData()) {
      return mockData.resetPassword(data);
    }
    return this.post<{ success: boolean }>('/api/auth/reset-password', data);
  }

  async updateProfile(data: { name?: string; timezone?: string }): Promise<API.User> {
    if (shouldUseMockData()) {
      return mockData.getCurrentUser();
    }
    return this.patch<API.User>('/api/auth/profile', data);
  }

  async changePassword(data: { currentPassword: string; newPassword: string }): Promise<{ message?: string; token?: string }> {
    // Backend revokes `user.sessionRevokedAt = now` AND issues a fresh
    // token in the response (authController:1016-1019). Without
    // capturing the new token, the frontend keeps using the OLD one
    // → next request fails 401 because protect() middleware sees the
    // OLD token was issued before sessionRevokedAt → user gets
    // surprise-logged-out from this device after a "successful"
    // password change. Return-type widened so the hook can persist
    // the new token before the surprise-logout window.
    if (shouldUseMockData()) {
      return { message: 'Password changed' };
    }
    return this.post<{ message?: string; token?: string }>('/api/auth/change-password', data);
  }

  async verify2FA(data: { tempToken: string; code: string }): Promise<API.LoginResponse> {
    if (shouldUseMockData()) {
      return mockData.verify2FA(data);
    }
    return this.post<API.LoginResponse>('/api/auth/mfa/login', data);
  }

  async resend2FA(): Promise<{ success: boolean }> {
    if (shouldUseMockData()) {
      return mockData.resend2FA();
    }
    return this.post<{ success: boolean }>('/api/auth/mfa/resend');
  }

  async verifyEmail(data: { token: string }): Promise<{ success: boolean }> {
    if (shouldUseMockData()) {
      return mockData.verifyEmail(data);
    }
    return this.post<{ success: boolean }>('/api/auth/verify-email', data);
  }

  async resendVerificationEmail(email?: string): Promise<{ success: boolean }> {
    if (shouldUseMockData()) {
      return mockData.resendVerificationEmail();
    }
    // Endpoint is PUBLIC + rate-limited; post-signup users carry no JWT
    // so we pass the email in the body. Authenticated callers can omit
    // the body and the backend will resolve via req.user.
    return this.post<{ success: boolean }>(
      '/api/auth/resend-verification',
      email ? { email } : undefined,
    );
  }

  // completeOnboarding / resetOnboarding / getOnboardingState /
  // saveOnboardingStep removed 2026-05-02 — workspace-onboarding wizard
  // deleted. Per-agent onboarding lives on the agent endpoints
  // (getOnboardingDraft / updateOnboardingProgress /
  // completeAgentOnboarding).

  // NEW: Avatar Upload Method
  async uploadAvatar(file: File): Promise<{ url: string }> {
    if (shouldUseMockData()) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return { url: URL.createObjectURL(file) };
    }
    const response = await uploadFile('/api/upload/avatar', file, 'avatar');
    return response.data;
  }

  // ============================================================================
  // ORGANIZATION
  // ============================================================================

  async getOrganization(): Promise<API.Organization> {
    if (shouldUseMockData()) {
      return mockData.getOrganization();
    }
    return this.get<API.Organization>('/api/org/settings');
  }

  async updateOrganization(updates: Partial<API.Organization>): Promise<API.Organization> {
    if (shouldUseMockData()) {
      const current = await this.getOrganization();
      return { ...current, ...updates };
    }
    return this.patch<API.Organization>('/api/org/settings', updates);
  }

  async updateOrgSetup(data: Record<string, any>): Promise<{ success: boolean }> {
    if (shouldUseMockData()) {
      return { success: true };
    }
    return this.patch<{ success: boolean }>('/api/org/setup', data);
  }

  // SSO Test Connection — verifies an OIDC IdP config against the live
  // discovery doc + JWKS BEFORE saving. Reads candidate config from the
  // body so admins can validate typo-corrected values without persisting.
  // Backend always returns 200 with `{ ok: boolean, ... }` for successful
  // probes; non-200 means the probe itself failed (auth/plan/validation).
  async testSsoConnection(payload: API.SsoTestPayload): Promise<API.SsoTestResult> {
    if (shouldUseMockData()) {
      // Demo-mode: simulate a happy-path discovery against the form's
      // current input so the admin can rehearse the UX flow without
      // a real IdP backing it.
      await new Promise((r) => setTimeout(r, 600));
      if (payload.provider === 'oidc') {
        const base = (payload.issuerUrl || 'https://idp.example')
          .replace(/\/.well-known\/openid-configuration\/?$/i, '')
          .replace(/\/+$/, '');
        return {
          ok: true,
          provider: 'oidc',
          discovered: {
            auth: `${base}/oauth2/v1/authorize`,
            token: `${base}/oauth2/v1/token`,
            jwks: `${base}/oauth2/v1/keys`,
          },
          latencyMs: 612,
        };
      }
      // SAML
      return {
        ok: true,
        provider: 'saml',
        discovered: {
          entityId: 'https://idp.example/exk-mock',
          ssoUrl: 'https://idp.example/sso/saml',
          x509Cert: 'MOCK_BASE64_CERT_BODY_REPLACED_AT_RUNTIME_BY_REAL_IDP',
        },
        latencyMs: 745,
      };
    }
    return this.post<API.SsoTestResult>('/api/org/sso/test', { ssoConfig: payload });
  }

  // SCIM 2.0 token rotation (WS-5). Backend returns the raw token exactly
  // once; we never persist it client-side. Admin must paste into IdP.
  async rotateScimToken(): Promise<API.ScimTokenResponse> {
    if (shouldUseMockData()) {
      return {
        token: 'adj_scim_mock-1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcd',
        last4: 'abcd',
        generatedAt: new Date().toISOString(),
      };
    }
    return this.post<API.ScimTokenResponse>('/api/org/scim/token', {});
  }

  async revokeScimToken(): Promise<{ revoked: true }> {
    if (shouldUseMockData()) return { revoked: true };
    return this.delete<{ revoked: true }>('/api/org/scim/token');
  }

  // SSO Activity Log (WS-4) — last N SSO-related audit-log entries for the
  // current org. Cursor-paginated.
  async getSsoActivity(params: {
    limit?: number;
    cursor?: string;
    status?: 'success' | 'failure';
    action?: string;
    provider?: 'oidc' | 'saml';
    q?: string;
    from?: string;
    to?: string;
  } = {}): Promise<API.SsoActivityResponse> {
    if (shouldUseMockData()) {
      const now = Date.now();
      const sample: API.SsoActivityEntry[] = [
        {
          id: 'mock-1',
          timestamp: new Date(now - 5 * 60 * 1000).toISOString(),
          action: 'auth.login',
          status: 'success',
          userEmail: 'alice@acme.com',
          detail: 'Enterprise SSO login via OIDC',
          sso: { provider: 'oidc', idpDomain: 'acme.com', idpHost: 'idp.example', claimedEmail: 'alice@acme.com', externalUserId: 'sub-123', reason: null },
        },
        {
          id: 'mock-2',
          timestamp: new Date(now - 30 * 60 * 1000).toISOString(),
          action: 'auth.sso.failure',
          status: 'failure',
          userEmail: 'unknown',
          detail: 'SAML assertion validation failed',
          sso: { provider: 'saml', idpDomain: 'acme.com', idpHost: 'acme.okta.com', claimedEmail: null, externalUserId: null, reason: 'assertion_invalid' },
        },
      ];
      return { entries: sample, nextCursor: null };
    }
    const qs = new URLSearchParams();
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.cursor) qs.set('cursor', params.cursor);
    if (params.status) qs.set('status', params.status);
    if (params.action) qs.set('action', params.action);
    if (params.provider) qs.set('provider', params.provider);
    if (params.q && params.q.trim()) qs.set('q', params.q.trim());
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get<API.SsoActivityResponse>(`/api/org/sso/activity${suffix}`);
  }

  async uploadOrgLogo(file: File): Promise<{ logoUrl: string }> {
    if (shouldUseMockData()) {
      return { logoUrl: URL.createObjectURL(file) };
    }
    const response = await uploadFile('/api/org/logo', file, 'logo');
    return response.data;
  }

  async purgeAllTraces(): Promise<{ deletedCount: number }> {
    if (shouldUseMockData()) {
      return { deletedCount: 0 };
    }
    return this.delete<{ deletedCount: number }>('/api/org/traces');
  }

  async deleteOrganization(): Promise<void> {
    if (shouldUseMockData()) {
      return;
    }
    return this.delete<void>('/api/org');
  }

  async deleteCustomDomain(domainId: string): Promise<void> {
    if (shouldUseMockData()) {
      return;
    }
    return this.delete<void>(`/api/domains/${domainId}`);
  }

  async getCustomDomains(): Promise<any[]> {
    if (shouldUseMockData()) {
      return [];
    }
    return this.get<any[]>('/api/domains');
  }

  async addCustomDomain(data: { domain: string }): Promise<any> {
    if (shouldUseMockData()) {
      return { _id: Date.now().toString(), domain: data.domain, txtRecord: 'mock-txt', status: 'pending' };
    }
    return this.post<any>('/api/domains', data);
  }

  async verifyCustomDomain(domainId: string): Promise<any> {
    if (shouldUseMockData()) {
      return { status: 'active' };
    }
    return this.post<any>(`/api/domains/${domainId}/verify`);
  }

  // ============================================================================
  // WORKSPACES
  // ============================================================================

  async getWorkspaces(): Promise<any[]> {
    if (shouldUseMockData()) {
      return mockData.getWorkspaces ? mockData.getWorkspaces() : [];
    }
    return this.get<any[]>('/api/workspaces');
  }

  async createWorkspace(data: { name: string }): Promise<any> {
    if (shouldUseMockData()) {
      return { id: Date.now().toString(), name: data.name };
    }
    return this.post<any>('/api/workspaces', data);
  }

  async updateWorkspace(workspaceId: string, data: { name: string; isActive?: boolean }): Promise<any> {
    if (shouldUseMockData()) {
      return { id: workspaceId, ...data };
    }
    return this.patch<any>(`/api/workspaces/${workspaceId}`, data);
  }

  async deleteWorkspace(workspaceId: string): Promise<void> {
    if (shouldUseMockData()) {
      return;
    }
    return this.delete<void>(`/api/workspaces/${workspaceId}`);
  }

  // ============================================================================
  // SCHEDULED REPORTS
  // ============================================================================

  async getScheduledReports(): Promise<any[]> {
    return this.get<any[]>('/api/reports/schedule');
  }

  async createScheduledReport(data: {
    name: string;
    frequency: 'daily' | 'weekly' | 'monthly';
    recipients: string[];
    includeMetrics?: boolean;
    includeTopAgents?: boolean;
    includeAlerts?: boolean;
    includeLowConf?: boolean;
  }): Promise<any> {
    return this.post<any>('/api/reports/schedule', data);
  }

  async updateScheduledReport(id: string, updates: Record<string, any>): Promise<any> {
    return this.patch<any>(`/api/reports/schedule/${id}`, updates);
  }

  async deleteScheduledReport(id: string): Promise<void> {
    return this.delete<void>(`/api/reports/schedule/${id}`);
  }

  async sendReportNow(id: string): Promise<{ message: string }> {
    return this.post<{ message: string }>(`/api/reports/send-now/${id}`, {});
  }

  async downloadComplianceReport(): Promise<void> {
    const filename = `adjudon-compliance-${new Date().toISOString().slice(0, 10)}.pdf`;
    await downloadFile('/api/reports/compliance', filename);
  }

  // ============================================================================
  // ANALYTICS
  // ============================================================================
  
  async getAnalytics(params?: { dateFrom?: string; dateTo?: string }): Promise<API.AnalyticsResponse> {
    if (shouldUseMockData()) {
      return mockData.getAnalytics(params);
    }
    return this.get<API.AnalyticsResponse>('/api/analytics', params);
  }

  async getAnalyticsComparison(period: '7d' | '30d' | '90d' = '30d'): Promise<{
    period: string;
    totalDecisions: { current: number; previous: number; change: number };
    avgConfidence: { current: number; previous: number; change: number };
    blockedActions: { current: number; previous: number; change: number };
    humanReviews: { current: number; previous: number; change: number };
  }> {
    return this.get<any>('/api/analytics/comparison', { period });
  }

  // ============================================================================
  // COMPLIANCE OVERVIEW (posture aggregation served on the Overview page)
  // ============================================================================

  async getCompliancePosture(): Promise<API.CompliancePosture | null> {
    // Mock data has no compliance posture; sandbox/scale orgs always get null
    // from the backend anyway, so the demo dashboard simply hides the card.
    if (shouldUseMockData()) {
      return null;
    }
    return this.get<API.CompliancePosture | null>('/api/compliance-overview/posture');
  }

  // Server-authoritative combined readiness for ONE framework (Status tab single
  // source). When `entityId` is given the numbers are that Aufsichtsobjekt's; when
  // omitted the backend returns the consolidated group roll-up (+ per-entity
  // breakdown). Returns null on absent feature/org → the Status tab falls back to
  // its client-side derivation.
  async getFrameworkReadiness(framework: string, entityId?: string | null): Promise<API.FrameworkReadiness | null> {
    if (shouldUseMockData()) {
      return mockData.getFrameworkReadiness(framework, entityId ?? null);
    }
    const q = entityId ? `?entity=${encodeURIComponent(entityId)}` : '';
    return this.get<API.FrameworkReadiness | null>(
      `/api/compliance-overview/readiness/${encodeURIComponent(framework)}${q}`,
    );
  }

  async getReadinessHistory(months = 12, entityId?: string | null): Promise<API.ReadinessHistoryResponse | null> {
    if (shouldUseMockData()) {
      return mockData.getReadinessHistory(months);
    }
    const params = new URLSearchParams({ months: String(months) });
    if (entityId) params.set('entity', entityId);
    return this.get<API.ReadinessHistoryResponse | null>(`/api/compliance-overview/readiness-history?${params}`);
  }

  async getAllFrameworksReadiness(entityId?: string | null): Promise<API.AllFrameworksReadinessResponse | null> {
    if (shouldUseMockData()) {
      return mockData.getAllFrameworksReadiness(entityId ?? null);
    }
    const q = entityId ? `?entity=${encodeURIComponent(entityId)}` : '';
    return this.get<API.AllFrameworksReadinessResponse | null>(`/api/compliance-overview/readiness${q}`);
  }

  // The org's regulated entities ("Aufsichtsobjekte") for the entity selector.
  async getEntities(): Promise<API.RegulatedEntity[]> {
    if (shouldUseMockData()) {
      return mockData.getEntities();
    }
    const res = await this.get<{ entities: API.RegulatedEntity[] }>('/api/entities');
    return res?.entities ?? [];
  }

  // Per-framework coverage signal for the Overview readiness grid. Honest
  // derivation from real org data; backend fails open per-framework.
  async getFrameworkCoverage(): Promise<API.FrameworkCoverage> {
    if (shouldUseMockData()) {
      return mockData.getFrameworkCoverage();
    }
    return this.get<API.FrameworkCoverage>('/api/compliance/framework-coverage');
  }

  async getCPI(): Promise<API.CPIResponse> {
    if (shouldUseMockData()) {
      return mockData.getCPI();
    }
    return this.get<API.CPIResponse>('/api/cpi');
  }

  async ingestFeedback(traceId: string, action: 'approve' | 'override', correctedDecision?: string, feedbackNotes?: string): Promise<any> {
    if (shouldUseMockData()) {
      return { message: "Mock feedback ingested" };
    }
    return this.post<any>('/api/cpi/ingest', { traceId, action, correctedDecision, feedbackNotes });
  }

  // ── Phase 2 Calibration API ──────────────────────────────────────────────
  // The orgId path param is required by the backend (Cardinal Rule #1
  // defense-in-depth: URL :orgId MUST match req.organizationId). The caller
  // (Dashboard component) MUST pass an explicit orgId resolved from
  // useCurrentUser() — there is no localStorage user document to fall back to.
  // Earlier versions of this file read localStorage.getItem('adjudon_user')
  // which is never written anywhere in the frontend; that fallback caused
  // every calibration request to URL-encode the literal string "self", which
  // the backend rejected with FORBIDDEN_CROSS_ORG (403). Pass orgId.
  //
  // Mock methodology version — single source of truth so the mocks below
  // can't drift apart. Bumping the backend's CURRENT_METHODOLOGY_VERSION
  // (backend/services/calibrationService.js) requires updating this string
  // so demo-mode displays match production.

  async getCalibrationReliability(orgId: string, agentId: string = 'org', bins: 10 | 20 = 10): Promise<API.CalibrationReliabilityResponse> {
    if (shouldUseMockData()) {
      const end = new Date();
      const start = new Date(Date.now() - 90 * 86400000);
      if (isEmptyCalibAgent(agentId)) {
        return { bins: [], totalSamples: 0, methodology: MOCK_METHODOLOGY, window: { start: start.toISOString(), end: end.toISOString() }, agentId };
      }
      const p = personaCalib(agentId);
      // Overconfidence-Faktor: gut kalibriert ≈ 0; agent_pricing (Drift) leicht überkonfident.
      const overconf = p.drift ? 0.045 : 0.012;
      // Verkehr konzentriert sich in den oberen Konfidenz-Bins (typisch für reife Modelle).
      const binWeights = [0.01, 0.01, 0.02, 0.03, 0.05, 0.08, 0.12, 0.18, 0.24, 0.26];
      const binList: API.CalibrationReliabilityBin[] = Array.from({ length: bins }).map((_, i) => {
        const lo = i / bins;
        const hi = (i + 1) / bins;
        const mid = (lo + hi) / 2;
        const w = binWeights[Math.min(binWeights.length - 1, Math.floor(mid * binWeights.length))];
        const sampleSize = Math.max(0, Math.round(p.samples * w));
        if (sampleSize === 0) {
          return { confidenceLow: +lo.toFixed(2), confidenceHigh: +hi.toFixed(2), meanConfidence: null, accuracy: null, sampleSize: 0, ciLower: null, ciUpper: null };
        }
        const meanConfidence = +Math.min(0.999, mid + 0.01).toFixed(4);
        // Accuracy liegt nahe der mittleren Konfidenz, abzüglich Overconfidence in hohen Bins.
        const acc = Math.max(0, Math.min(1, meanConfidence - overconf * (mid > 0.7 ? 1 : 0.3)));
        const ci = 1.96 * Math.sqrt((acc * (1 - acc)) / sampleSize);
        return {
          confidenceLow: +lo.toFixed(2),
          confidenceHigh: +hi.toFixed(2),
          meanConfidence,
          accuracy: +acc.toFixed(4),
          sampleSize,
          ciLower: +Math.max(0, acc - ci).toFixed(4),
          ciUpper: +Math.min(1, acc + ci).toFixed(4),
        };
      });
      const totalSamples = binList.reduce((s, b) => s + b.sampleSize, 0);
      return { bins: binList, totalSamples, methodology: MOCK_METHODOLOGY, window: { start: start.toISOString(), end: end.toISOString() }, agentId };
    }
    return this.get<API.CalibrationReliabilityResponse>(`/api/calibration/${encodeURIComponent(orgId)}/agents/${encodeURIComponent(agentId)}/reliability?bins=${bins}`);
  }

  async getCalibrationMetrics(orgId: string, agentId: string = 'org', windowDays: '30d' | '90d' | '365d' = '90d'): Promise<API.CalibrationMetricsResponse> {
    if (shouldUseMockData()) {
      const windowEnd = new Date().toISOString();
      if (isEmptyCalibAgent(agentId)) {
        return {
          persisted: null,
          live: { brier: null, ece: null, sampleSize: 0, window: windowDays, windowStart: new Date(Date.now() - 90 * 86400000).toISOString(), windowEnd },
          methodology: MOCK_METHODOLOGY,
          agentId,
        };
      }
      const p = personaCalib(agentId);
      const days = windowDays === '30d' ? 30 : windowDays === '365d' ? 365 : 90;
      // Live-Fenster zeigt das anteilige Sample; 30d etwas frischer/leicht höher kalibriert.
      const liveSamples = Math.round(p.samples * (days / 270));
      const liveDrift = p.drift ? 0.006 : -0.002; // bei Drift driftet auch das Live-Fenster nach oben
      return {
        persisted: {
          brier: +p.brier.toFixed(4),
          ece: +p.ece.toFixed(4),
          sampleSize: p.samples,
          mapType: 'beta',
          shrinkageWeight: 0.15,
          lastRefitAt: p.lastRefit,
          driftDetected: p.drift,
          driftReason: p.driftReason,
        },
        live: {
          brier: +Math.max(0, p.brier + liveDrift * 1.4).toFixed(4),
          ece: +Math.max(0, p.ece + liveDrift).toFixed(4),
          sampleSize: liveSamples,
          window: windowDays,
          windowStart: new Date(Date.now() - days * 86400000).toISOString(),
          windowEnd,
        },
        methodology: MOCK_METHODOLOGY,
        agentId,
      };
    }
    return this.get<API.CalibrationMetricsResponse>(`/api/calibration/${encodeURIComponent(orgId)}/agents/${encodeURIComponent(agentId)}/metrics?window=${windowDays}`);
  }

  async getCalibrationConformal(orgId: string, agentId: string = 'org', alpha: number = 0.1): Promise<API.CalibrationConformalResponse> {
    if (shouldUseMockData()) {
      if (isEmptyCalibAgent(agentId)) {
        return { qHat: null, alpha, coverage: null, sampleSize: 0, methodology: MOCK_METHODOLOGY, agentId, note: 'Noch keine gelabelten Outcomes für dieses System.' };
      }
      const p = personaCalib(agentId);
      // Empirische Abdeckung nahe am Ziel (1−alpha); bei Drift leicht unter Ziel.
      const target = 1 - alpha;
      const coverage = +Math.max(0, Math.min(1, target - (p.drift ? 0.018 : -0.004))).toFixed(4);
      // qHat (Konformitäts-Schwelle) korreliert mit der Modell-Unsicherheit.
      const qHat = +(0.08 + p.brier * 1.6).toFixed(4);
      return { qHat, alpha, coverage, covered: Math.round(coverage * p.samples), sampleSize: p.samples, methodology: MOCK_METHODOLOGY, agentId };
    }
    return this.get<API.CalibrationConformalResponse>(`/api/calibration/${encodeURIComponent(orgId)}/agents/${encodeURIComponent(agentId)}/conformal?alpha=${alpha}`);
  }

  async getCalibrationBiasStratified(orgId: string, agentId: string = 'org', windowDays: '30d' | '90d' | '365d' = '90d'): Promise<API.CalibrationBiasStratifiedResponse> {
    if (shouldUseMockData()) {
      const days = windowDays === '30d' ? 30 : windowDays === '365d' ? 365 : 90;
      const window = { days, start: new Date(Date.now() - days * 86400000).toISOString(), end: new Date().toISOString() };
      if (isEmptyCalibAgent(agentId)) {
        return { stratification: [], totalSamples: 0, groupCount: 0, maxBetweenGroupEceGap: null, methodology: MOCK_METHODOLOGY, window };
      }
      const p = personaCalib(agentId);
      // Stratifizierung nach Altersgruppe (geschützte Klasse) — Bias-Monitoring
      // ist org-weit aktiv (≥2 Gruppen). Größte Gruppe leicht schlechtere ECE →
      // sichtbare, aber moderate Lücke (passt zu Vorfall #5: Underwriting-Bias-Verdacht).
      const groups = [
        { group: 'Altersgruppe 18–35', share: 0.28, eceDelta: -0.004 },
        { group: 'Altersgruppe 36–55', share: 0.44, eceDelta: -0.001 },
        { group: 'Altersgruppe 56+',   share: 0.28, eceDelta: +0.021 },
      ];
      const stratification: API.CalibrationBiasStratificationRow[] = groups.map((g) => {
        const sampleSize = Math.round(p.samples * g.share);
        const ece = +Math.max(0, p.ece + g.eceDelta).toFixed(4);
        const brier = +Math.max(0, p.brier + g.eceDelta * 1.3).toFixed(4);
        return { group: g.group, sampleSize, brier, ece };
      });
      // Aggregat-Zeile (wird in BiasMonitoringSetupPage aus den „realen" Gruppen herausgefiltert).
      stratification.unshift({ group: '__aggregate__', sampleSize: p.samples, brier: +p.brier.toFixed(4), ece: +p.ece.toFixed(4) });
      const real = stratification.filter((r) => r.group !== '__aggregate__');
      const eces = real.map((r) => r.ece ?? 0);
      const maxBetweenGroupEceGap = +(Math.max(...eces) - Math.min(...eces)).toFixed(4);
      return {
        stratification,
        totalSamples: p.samples,
        groupCount: real.length,
        maxBetweenGroupEceGap,
        methodology: MOCK_METHODOLOGY,
        window,
      };
    }
    return this.get<API.CalibrationBiasStratifiedResponse>(`/api/calibration/${encodeURIComponent(orgId)}/agents/${encodeURIComponent(agentId)}/bias-stratified?window=${windowDays}`);
  }

  async getCalibrationArticle13Ifu(orgId: string, agentId: string = 'org'): Promise<API.CalibrationArticle13IfuResponse> {
    if (shouldUseMockData()) {
      // Persona: Art. 13(3)(a)–(f)-Gebrauchsanweisung — Genauigkeits-/Aufsichts-/
      // Logging-Sektionen auto-gefüllt + plattform-attestiert; 2 Anbieter-/Betreiber-
      // Felder noch offen (deployer-input-required → „Eingabe nötig"). Deutsch.
      const SYS_NAME: Record<string, string> = {
        org: 'Alle KI-Systeme (Organisations-Aggregat)',
        agent_fraud: 'Fraud Detector',
        agent_uw_leben: 'Underwriting Assistent Leben',
        agent_pricing: 'Pricing Engine Komposit',
        agent_doc: 'Dokument-Klassifizierer',
        agent_chat: 'Kunden-Chat-Assistent',
        agent_churn: 'Churn-Prädiktor',
      };
      const sysName = SYS_NAME[agentId] ?? 'KI-System';
      const json: Record<string, unknown> = {
        system: sysName,
        article: 'EU AI Act Art. 13(3)(a)–(f)',
        generatedAt: new Date().toISOString(),
        roleNote: 'Adjudon füllt die plattform-attestierbaren Felder (Genauigkeit, Aufsicht, Logging) automatisch. Anbieter- und Betreiber-spezifische Angaben bleiben in Ihrer Verantwortung — kein Konformitätsnachweis durch Adjudon (§5 UWG).',
        ifuCompleteness: { ready: false, deployerActionsOutstanding: 2 },
        instructionsForUse: [
          { title: '(a) Identität & Kontakt des Anbieters', status: 'deployer-input-required', requiresDeployerAction: true },
          { title: '(b) Eigenschaften, Fähigkeiten & Leistungsgrenzen', status: 'auto-filled', requiresDeployerAction: false },
          { title: '(b) Genauigkeit, Robustheit & Cybersicherheit (Kennzahlen)', status: 'platform-attested', requiresDeployerAction: false },
          { title: '(b) Bekannte/vorhersehbare Risiken bei bestimmungsgemäßer Nutzung', status: 'deployer-input-required', requiresDeployerAction: true },
          { title: '(c) Änderungen des Systems über den Lebenszyklus', status: 'auto-filled', requiresDeployerAction: false },
          { title: '(d) Menschliche Aufsicht (Art. 14) — Maßnahmen', status: 'platform-attested', requiresDeployerAction: false },
          { title: '(e) Erwartete Rechen-/Hardware-Ressourcen & Lebensdauer', status: 'auto-filled', requiresDeployerAction: false },
          { title: '(f) Protokollierung & Aufbewahrung (Art. 12)', status: 'platform-attested', requiresDeployerAction: false },
        ],
      };
      const markdown = [
        `# Gebrauchsanweisung (Art. 13) — ${sysName}`,
        '',
        '> Auto-generiert. Genauigkeits-, Aufsichts- und Logging-Sektionen sind plattform-attestiert; mit „Eingabe nötig" markierte Felder müssen Anbieter/Betreiber ausfüllen.',
        '',
        '## (a) Anbieter — *Eingabe nötig*',
        '## (b) Fähigkeiten & Leistungsgrenzen — automatisch',
        '## (b) Genauigkeit & Robustheit — plattform-attestiert',
        '## (d) Menschliche Aufsicht — plattform-attestiert',
        '## (f) Protokollierung (Art. 12) — plattform-attestiert',
        '',
        '_Kein Konformitätsnachweis — Konformität entsteht durch Ihren Betrieb (§5 UWG)._',
      ].join('\n');
      return { json, markdown };
    }
    return this.get<API.CalibrationArticle13IfuResponse>(`/api/calibration/${encodeURIComponent(orgId)}/agents/${encodeURIComponent(agentId)}/article-13-ifu`);
  }

  // EU AI Act Art. 26/27 advisory: which KI-Systeme have an approved FRIA.
  async getFriaCoverage(): Promise<API.FriaCoverage> {
    if (shouldUseMockData()) {
      return mockData.getFriaCoverage();
    }
    const res = await this.get<API.FriaCoverage | null>('/api/fria/coverage');
    return res ?? {
      items: [],
      agents: [],
      summary: { totalSystems: 0, approvedSystems: 0, pendingSystems: 0, totalAgents: 0, coveredAgents: 0, uncoveredAgents: 0, allCovered: false },
    };
  }

  async getCalibrationTrend(orgId: string, agentId: string = 'org', windowDays: number = 30): Promise<API.CalibrationTrendResponse> {
    if (shouldUseMockData()) {
      // Mirror the backend gap-fill so the frontend's chart renders axes
      // + drift threshold in demo mode just like it does against a real
      // backend with zero labelled outcomes.
      const trend: API.CalibrationTrendDay[] = Array.from({ length: windowDays }).map((_, i) => {
        const d = new Date();
        d.setUTCDate(d.getUTCDate() - (windowDays - 1 - i));
        const dayKey = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
        return { date: dayKey, brier: null, ece: null, sampleSize: 0 };
      });
      return { trend, methodology: MOCK_METHODOLOGY, windowDays };
    }
    return this.get<API.CalibrationTrendResponse>(`/api/calibration/${encodeURIComponent(orgId)}/agents/${encodeURIComponent(agentId)}/trend?window=${windowDays}`);
  }

  async triggerCalibrationDriftCheck(orgId: string, agentId: string = 'org', decisionType?: string): Promise<API.CalibrationDriftCheckResponse> {
    if (shouldUseMockData()) {
      return { triggered: [], metricsBefore: null, metricsAt: null };
    }
    const qs = decisionType ? `?decisionType=${encodeURIComponent(decisionType)}` : '';
    return this.post<API.CalibrationDriftCheckResponse>(`/api/calibration/${encodeURIComponent(orgId)}/agents/${encodeURIComponent(agentId)}/drift-check${qs}`, {});
  }

  async getAgents(): Promise<API.Agent[]> {
    if (shouldUseMockData()) {
      return mockData.getAgents();
    }
    return this.get<API.Agent[]>('/api/agents');
  }

  async getAgent(id: string): Promise<API.Agent> {
    if (shouldUseMockData()) {
      return mockData.getAgent(id);
    }
    return this.get<API.Agent>(`/api/agents/${id}`);
  }

  async createAgent(agent: API.AgentCreateRequest): Promise<API.AgentCreateResponse> {
    if (shouldUseMockData()) {
      return mockData.createAgent(agent);
    }
    return this.post<API.AgentCreateResponse>('/api/agents', agent);
  }

  async updateAgent(id: string, updates: Partial<API.Agent>): Promise<API.Agent> {
    if (shouldUseMockData()) {
      return mockData.updateAgent(id, updates);
    }
    return this.patch<API.Agent>(`/api/agents/${id}`, updates);
  }

  async deleteAgent(id: string): Promise<void> {
    if (shouldUseMockData()) {
      return mockData.deleteAgent(id);
    }
    return this.delete(`/api/agents/${id}`);
  }

  async updateAgentPolicies(id: string, policies: API.AgentPolicies): Promise<API.Agent> {
    if (shouldUseMockData()) {
      return mockData.updateAgentPolicies(id, policies);
    }
    return this.patch<API.Agent>(`/api/agents/${id}/policies`, policies);
  }

  // EU AI Act Art. 50(2) / Annex IV — per-agent model metadata (e.g. outputType,
  // which drives the auto-watermark trigger when set to 'generative').
  async updateAgentModelMetadata(id: string, patch: Partial<API.AgentModelMetadata>): Promise<API.Agent> {
    if (shouldUseMockData()) {
      // Store-backed: schreibt in den Agent-Store, damit die Klassifizierung
      // den Refetch überlebt (der frühere Inline-Mock baute ein synthetisches
      // Merge-Objekt — nach invalidateQueries(['agents']) war der Patch weg).
      return mockData.updateAgentModelMetadata(id, patch);
    }
    return this.patch<API.Agent>(`/api/agents/${id}/model-metadata`, patch);
  }

  async regenerateAgentKey(id: string): Promise<{ apiKey: string; apiKeyPrefix: string }> {
    if (shouldUseMockData()) {
      return mockData.regenerateAgentKey(id);
    }
    return this.post<{ apiKey: string; apiKeyPrefix: string }>(`/api/agents/${id}/regenerate-key`);
  }

  // ── Agent Onboarding (AgentOnboardingPage) ──────────────────────────────
  // Read-only: returns the user's resumable draft or { agent: null }. The
  // agent is created on Step 1 submit via the regular createAgent flow
  // with `onboarding: true` — no auto-create on read.
  async getOnboardingDraft(): Promise<API.AgentOnboardingDraftResponse> {
    if (shouldUseMockData()) {
      return mockData.getOnboardingDraft();
    }
    return this.get<API.AgentOnboardingDraftResponse>('/api/agents/onboarding/draft');
  }

  async discardOnboardingDraft(id: string): Promise<API.DiscardOnboardingDraftResponse> {
    if (shouldUseMockData()) {
      return mockData.discardOnboardingDraft(id);
    }
    return this.delete<API.DiscardOnboardingDraftResponse>(`/api/agents/${id}/onboarding`);
  }

  async updateOnboardingProgress(
    id: string,
    payload: API.UpdateOnboardingProgressRequest,
  ): Promise<API.Agent> {
    if (shouldUseMockData()) {
      return mockData.updateOnboardingProgress(id, payload);
    }
    return this.patch<API.Agent>(`/api/agents/${id}/onboarding`, payload);
  }

  async completeAgentOnboarding(id: string): Promise<API.Agent> {
    if (shouldUseMockData()) {
      return mockData.completeAgentOnboarding(id);
    }
    return this.post<API.Agent>(`/api/agents/${id}/onboarding/complete`);
  }

  // ── Phase-1 Workspace Onboarding (SYNTHESIS_2026-05-05 §3) ──────────────
  // These cover the dashboard-level activation flow (welcome → checklist
  // → DPA-Accept → first-trace milestone). Distinct from the per-agent
  // wizard above.

  async getOnboardingState(): Promise<API.OnboardingStateResponse> {
    if (shouldUseMockData()) {
      return mockData.getOnboardingState();
    }
    return this.get<API.OnboardingStateResponse>('/api/onboarding/state');
  }

  async emitOnboardingEvent(payload: API.EmitOnboardingEventRequest): Promise<API.OnboardingEvent> {
    if (shouldUseMockData()) {
      return mockData.emitOnboardingEvent(payload);
    }
    return this.post<API.OnboardingEvent>('/api/onboarding/events', payload);
  }

  async acceptDpa(payload: API.AcceptDpaRequest): Promise<API.AcceptDpaResponse> {
    if (shouldUseMockData()) {
      return mockData.acceptDpa(payload);
    }
    return this.post<API.AcceptDpaResponse>('/api/onboarding/dpa-accept', payload);
  }

  async getDpaVerification(): Promise<API.DpaVerificationResponse> {
    if (shouldUseMockData()) {
      return mockData.getDpaVerification();
    }
    return this.get<API.DpaVerificationResponse>('/api/onboarding/dpa-verification');
  }

  async seedDemoData(payload: { persona?: string }): Promise<API.SeedDemoResponse> {
    if (shouldUseMockData()) {
      return mockData.seedDemoData(payload);
    }
    return this.post<API.SeedDemoResponse>('/api/onboarding/seed-demo', payload);
  }

  async getLegalDocs(): Promise<API.LegalDocMeta[]> {
    if (shouldUseMockData()) {
      return mockData.getLegalDocs();
    }
    return this.get<API.LegalDocMeta[]>('/api/onboarding/legal-docs');
  }

  async getLegalDoc(doc: string, locale: string): Promise<API.LegalDoc> {
    if (shouldUseMockData()) {
      return mockData.getLegalDoc(doc, locale);
    }
    return this.get<API.LegalDoc>(`/api/onboarding/legal-docs/${doc}/${locale}`);
  }

  async getAgentAnalytics(id: string): Promise<API.AgentAnalyticsResponse> {
    if (shouldUseMockData()) {
      return mockData.getAgentAnalytics(id);
    }
    return this.get<API.AgentAnalyticsResponse>(`/api/agents/${id}/analytics`);
  }

  // ML-BOM (CycloneDX 1.7) — bypasses normalizeResponse so the envelope meta
  // (digest, urn) survives, like getReviewQueue. Mock-guarded: the old
  // MlBomInspectionView called the raw apiClient directly → 500-poll on every
  // demo render (same bug class as the ChainIntegrityBanner fix).
  async getAgentMlBom(id: string): Promise<API.AgentMlBomResult> {
    if (shouldUseMockData()) {
      return mockData.getAgentMlBom(id);
    }
    const response = await apiClient.get(`/api/agents/${id}/ml-bom`);
    const raw = response.data as unknown as { data?: API.AgentMlBom; digest?: string; urn?: string };
    return {
      bom: (raw?.data as API.AgentMlBom | undefined) ?? null,
      digest: typeof raw?.digest === 'string' ? raw.digest : '',
      urn: typeof raw?.urn === 'string' ? raw.urn : '',
    };
  }

  async refreshAgentMlBom(id: string): Promise<{ digest: string; urn: string }> {
    if (shouldUseMockData()) {
      return mockData.refreshAgentMlBom(id);
    }
    return this.post<{ digest: string; urn: string }>(`/api/agents/${id}/ml-bom/refresh`, {});
  }

  async getTraces(params?: API.ListParams): Promise<API.TracesListResponse> {
    if (shouldUseMockData()) {
      return mockData.getTraces(params);
    }
    // Bypass `normalizeResponse` so the `pagination` envelope meta survives and
    // `data` (the array) maps onto `traces` — the standard `this.get<T>`
    // collapses `{ success, data, pagination }` down to `data` and would drop
    // pagination, leaving `.traces` undefined (same envelope-unwrap as
    // getReviewQueue). The decision-log page was a stub, so this latent live
    // bug was never hit until now.
    const response = await apiClient.get('/api/traces', { params });
    const raw = response.data as unknown as {
      data?: unknown;
      pagination?: API.TracesListResponse['pagination'];
    };
    const traces = Array.isArray(raw?.data)
      ? (raw.data as API.Trace[])
      : Array.isArray(raw)
        ? (raw as unknown as API.Trace[])
        : [];
    const pagination = raw?.pagination ?? {
      page: 1,
      limit: traces.length,
      total: traces.length,
      pages: 1,
      hasMore: false,
    };
    return { traces, pagination };
  }

  async getTrace(id: string): Promise<API.Trace> {
    if (shouldUseMockData()) {
      throw new Error('Not implemented - mock mode');
    }
    return this.get<API.Trace>(`/api/traces/${id}`);
  }

  async searchMemory(query: string, minScore?: number, topK?: number): Promise<API.Trace[]> {
    if (shouldUseMockData()) {
      const res = await mockData.getTraces();
      return res.traces.slice(0, topK || 5) as unknown as API.Trace[]; // Mock response
    }
    // Return format is { data: Trace[], count: number } but this.post extracts data automatically if it's the standard wrapper
    // Wait, the standard wrapper has data. If the backend returns ok(res, results), `results` is the array.
    return this.post<API.Trace[]>('/api/traces/memory', { query, minScore, topK });
  }

  async exportTraces(params?: API.FilterParams): Promise<void> {
    if (shouldUseMockData()) {
      return;
    }
    const search = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).length > 0) {
        search.set(k, String(v));
      }
    });
    const endpoint = `/api/traces/export${search.toString() ? `?${search.toString()}` : ''}`;
    await downloadFile(endpoint, `adjudon-traces-${new Date().toISOString().slice(0, 10)}.csv`);
  }

  async bulkDeleteTraces(payload: { ids?: string[] } & API.FilterParams): Promise<{ deletedCount: number }> {
    if (shouldUseMockData()) {
      return { deletedCount: 0 };
    }
    return this.post<{ deletedCount: number }>('/api/traces/bulk-delete', payload);
  }

  async bulkUpdateTraceStatus(ids: string[], status: 'flagged' | 'approved' | 'escalated' | 'pending' | 'success'): Promise<{ updatedCount: number }> {
    if (shouldUseMockData()) {
      return { updatedCount: ids.length };
    }
    return this.post<{ updatedCount: number }>('/api/traces/bulk-status', { ids, status });
  }

  async getReviewQueue(params?: API.FilterParams): Promise<API.ReviewQueueResponse> {
    if (shouldUseMockData()) {
      const items = await mockData.getReviewQueue();
      return { items, total: items.length, limit: items.length, truncated: false };
    }
    // Bypass `normalizeResponse` so envelope meta (total, limit, truncated)
    // survives — the standard `this.get<T>` collapses `{ success, data, ... }`
    // down to `data` and would discard the truncation flag.
    const response = await apiClient.get('/api/review-queue', { params });
    const raw = response.data as unknown;
    if (raw && typeof raw === 'object' && 'data' in (raw as Record<string, unknown>)) {
      const env = raw as { data?: unknown; total?: unknown; limit?: unknown; truncated?: unknown };
      const items = Array.isArray(env.data) ? (env.data as API.ReviewItem[]) : [];
      const total = typeof env.total === 'number' ? env.total : items.length;
      const limit = typeof env.limit === 'number' ? env.limit : items.length;
      const truncated = !!env.truncated;
      return { items, total, limit, truncated };
    }
    // Legacy / unwrapped — preserve as items, no meta available.
    const items = Array.isArray(raw) ? (raw as API.ReviewItem[]) : [];
    return { items, total: items.length, limit: items.length, truncated: false };
  }

  async reviewDecision(id: string, action: API.ReviewActionRequest): Promise<API.ReviewItem> {
    if (shouldUseMockData()) {
      return mockData.reviewDecision(id, action);
    }
    return this.post<API.ReviewItem>(`/api/review-queue/${id}/decision`, action);
  }

  async bulkReviewDecision(ids: string[], decision: 'approve' | 'reject', reason?: string): Promise<{ processed: number; skipped: number; decision: string }> {
    if (shouldUseMockData()) {
      return mockData.bulkReviewDecision(ids, decision, reason);
    }
    return this.post<{ processed: number; skipped: number; decision: string }>('/api/review-queue/bulk-decision', { ids, decision, reason });
  }

  async assignReviewItem(id: string, assignedTo?: string): Promise<API.ReviewItem> {
    return this.patch<API.ReviewItem>(`/api/review-queue/${id}/assign`, assignedTo ? { assignedTo } : {});
  }

  async escalateReviewItem(id: string, reason: string, escalateTo?: string): Promise<API.ReviewItem> {
    if (shouldUseMockData()) {
      return mockData.escalateReviewItem(id, reason);
    }
    return this.post<API.ReviewItem>(`/api/review-queue/${id}/escalate`, { reason, escalateTo });
  }

  async getPolicies(): Promise<API.Policy[]> {
    if (shouldUseMockData()) {
      return mockData.getPolicies();
    }
    return this.get<API.Policy[]>('/api/policies');
  }

  async createPolicy(policy: Omit<API.Policy, 'id' | 'createdAt' | 'matchCount'>): Promise<API.Policy> {
    if (shouldUseMockData()) {
      return mockData.createPolicy(policy);
    }
    return this.post<API.Policy>('/api/policies', policy);
  }

  async updatePolicy(id: string, updates: Partial<API.Policy>): Promise<API.Policy> {
    if (shouldUseMockData()) {
      return mockData.updatePolicy(id, updates);
    }
    return this.patch<API.Policy>(`/api/policies/${id}`, updates);
  }

  async deletePolicy(id: string): Promise<void> {
    if (shouldUseMockData()) {
      return mockData.deletePolicy(id);
    }
    return this.delete(`/api/policies/${id}`);
  }

  async getPolicyTemplates(): Promise<API.PolicyTemplate[]> {
    if (shouldUseMockData()) return mockData.getPolicyTemplates();
    return this.get<API.PolicyTemplate[]>('/api/policies/templates');
  }

  async createPolicyFromTemplate(templateId: string, name?: string): Promise<API.Policy> {
    if (shouldUseMockData()) {
      return mockData.createPolicyFromTemplate(templateId, name);
    }
    return this.post<API.Policy>('/api/policies/from-template', { templateId, name });
  }

  async getAlerts(): Promise<API.Alert[]> {
    if (shouldUseMockData()) {
      return mockData.getAlerts();
    }
    return this.get<API.Alert[]>('/api/alerts');
  }

  async createAlert(alert: Omit<API.Alert, 'id' | 'createdAt' | 'triggerCount'>): Promise<API.Alert> {
    if (shouldUseMockData()) {
      throw new Error('Not implemented - mock mode');
    }
    return this.post<API.Alert>('/api/alerts', alert);
  }

  async updateAlert(id: string, updates: Partial<API.Alert>): Promise<API.Alert> {
    if (shouldUseMockData()) {
      throw new Error('Not implemented - mock mode');
    }
    return this.patch<API.Alert>(`/api/alerts/${id}`, updates);
  }

  async deleteAlert(id: string): Promise<void> {
    if (shouldUseMockData()) {
      throw new Error('Not implemented - mock mode');
    }
    return this.delete(`/api/alerts/${id}`);
  }

  async testAlert(id: string): Promise<{ success: boolean; message?: string }> {
    if (shouldUseMockData()) {
      throw new Error('Not implemented - mock mode');
    }
    return this.post<{ success: boolean; message?: string }>(`/api/alerts/${id}/test`);
  }

  async checkCpiAlerts(): Promise<{ triggered: number; details: any[] }> {
    if (shouldUseMockData()) {
      return { triggered: 0, details: [] };
    }
    return this.post<{ triggered: number; details: any[] }>('/api/alerts/check-cpi', {});
  }

  async getWebhooks(): Promise<API.Webhook[]> {
    if (shouldUseMockData()) {
      return mockData.getWebhooks();
    }
    return this.get<API.Webhook[]>('/api/webhooks');
  }

  async createWebhook(webhook: API.WebhookCreateRequest): Promise<API.Webhook> {
    if (shouldUseMockData()) {
      return mockData.createWebhook(webhook);
    }
    return this.post<API.Webhook>('/api/webhooks', webhook);
  }

  async updateWebhook(id: string, updates: Partial<API.Webhook>): Promise<API.Webhook> {
    if (shouldUseMockData()) {
      return mockData.updateWebhook(id, updates);
    }
    return this.patch<API.Webhook>(`/api/webhooks/${id}`, updates);
  }

  async deleteWebhook(id: string): Promise<void> {
    if (shouldUseMockData()) {
      return mockData.deleteWebhook(id);
    }
    return this.delete(`/api/webhooks/${id}`);
  }

  // Live-Erfolg: { statusCode, ok } (webhookController.testWebhook); Zustell-
  // Fehler: { success:false, ... } (HTTP 200). `success` bleibt optional, damit
  // beide Shapes konsumierbar sind.
  async testWebhook(id: string): Promise<{ ok?: boolean; statusCode?: number; success?: boolean }> {
    if (shouldUseMockData()) {
      return mockData.testWebhook(id);
    }
    return this.post<{ ok?: boolean; statusCode?: number; success?: boolean }>(`/api/webhooks/${id}/test`);
  }

  async getTeamMembers(): Promise<API.TeamMember[]> {
    if (shouldUseMockData()) {
      return mockData.getTeamMembers();
    }
    const raw = await this.get<any[]>('/api/team/members');
    return raw.map(m => ({
      ...m,
      id: m.id || m._id?.toString(),
      status: m.status ?? (m.isActive === false ? 'suspended' : 'active'),
    })) as API.TeamMember[];
  }

  async inviteTeamMember(invite: API.TeamInviteRequest): Promise<API.TeamMember> {
    if (shouldUseMockData()) {
      return mockData.inviteTeamMember(invite);
    }
    return this.post<API.TeamMember>('/api/team/invite', invite);
  }

  async updateTeamMember(id: string, updates: Partial<API.TeamMember>): Promise<API.TeamMember> {
    if (shouldUseMockData()) {
      return mockData.updateTeamMember(id, updates);
    }
    if (updates.role) {
      return this.patch<API.TeamMember>(`/api/team/members/${id}/role`, { role: updates.role });
    }
    if (updates.status === 'suspended') {
      return this.patch<API.TeamMember>(`/api/team/members/${id}/status`);
    }
    if (updates.status === 'active') {
      return this.patch<API.TeamMember>(`/api/team/members/${id}/reactivate`);
    }
    throw new Error('Unsupported team member update payload');
  }

  async removeTeamMember(id: string): Promise<void> {
    if (shouldUseMockData()) {
      return mockData.removeTeamMember(id);
    }
    return this.delete(`/api/team/members/${id}`);
  }

  async getBillingPlans(): Promise<API.BillingPlan[]> {
    if (shouldUseMockData()) {
      return mockData.getBillingPlans();
    }
    return this.get<API.BillingPlan[]>('/api/billing/plans');
  }

  async getBilling(): Promise<API.BillingResponse> {
    if (shouldUseMockData()) {
      return mockData.getBilling();
    }
    return this.get<API.BillingResponse>('/api/billing');
  }

  async createCheckoutSession(plan: string): Promise<{ url: string }> {
    if (shouldUseMockData()) {
      throw new Error('Not implemented - mock mode');
    }
    return this.post<{ url: string }>('/api/billing/checkout', { planId: plan });
  }

  async getBillingPortalUrl(): Promise<{ url: string }> {
    if (shouldUseMockData()) {
      throw new Error('Not implemented - mock mode');
    }
    return this.post<{ url: string }>('/api/billing/portal');
  }

  async getApiKeys(): Promise<API.APIKey[]> {
    if (shouldUseMockData()) {
      return mockData.getApiKeys();
    }
    const raw = await this.get<any[]>('/api/auth/keys');
    // Live contract: list returns `preview` (masked) + `scope.readOnly`, not `key`/`permissions`.
    // Map to the frontend APIKey shape so the register renders the masked key + access level.
    return raw.map(k => ({
      ...k,
      id: k.id || k._id?.toString(),
      key: k.key ?? k.preview,
      permissions: k.permissions ?? (k.scope ? (k.scope.readOnly ? ['read'] : ['read', 'write']) : undefined),
    })) as API.APIKey[];
  }

  /**
   * Exchange the current JWT for a short-lived (30s) single-use stream token.
   * Use this token in SSE/WebSocket URLs instead of the full JWT to avoid
   * token exposure in server logs and browser history.
   */
  async getStreamToken(): Promise<{ streamToken: string }> {
    return this.post<{ streamToken: string }>('/api/auth/stream-token', {});
  }

  async createAPIKey(name: string, permissions: string[]): Promise<API.APIKey> {
    if (shouldUseMockData()) {
      return mockData.createAPIKey(name, permissions);
    }
    // Page sends ['read'] for a read-only key, ['read','write'] otherwise → map to scope.readOnly.
    const created = await this.post<any>('/api/auth/keys', {
      name,
      scope: { readOnly: !permissions.includes('write') },
    });
    return { ...created, id: created.id || created._id?.toString() } as API.APIKey;
  }

  async revokeAPIKey(keyId: string): Promise<void> {
    if (shouldUseMockData()) {
      return mockData.revokeAPIKey(keyId);
    }
    return this.delete(`/api/auth/keys/${keyId}`);
  }


  async getAuditLog(params?: API.PaginationParams): Promise<API.AuditLogResponse> {
    if (shouldUseMockData()) {
      return mockData.getAuditLog(params);
    }
    return this.get<API.AuditLogResponse>('/api/audit', params);
  }

  async exportAuditLog(params?: {
    format?: 'csv' | 'pdf';
    action?: string;
    userId?: string;
    workspaceId?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{ totalCount?: number; truncated?: boolean }> {
    const { format, ...filters } = params || {};
    const fmt = format || 'csv';
    const endpoint = fmt === 'pdf' ? '/api/audit/export/pdf' : '/api/audit/export';
    const filename = `activity-log-${new Date().toISOString().slice(0, 10)}.${fmt}`;
    // Pass through any active filters so the export reflects what the
    // user is actually looking at — without this 'Export CSV' silently
    // ignored the filter bar.
    return downloadFile(endpoint, filename, { params: filters });
  }

  async getNotifications(): Promise<API.Notification[]> {
    if (shouldUseMockData()) {
      return mockData.getNotifications();
    }
    return this.get<API.Notification[]>('/api/notifications');
  }

  async markNotificationAsRead(id: string): Promise<void> {
    if (shouldUseMockData()) {
      throw new Error('Not implemented - mock mode');
    }
    return this.patch(`/api/notifications/${id}/read`);
  }

  // Marks every notification as read in a single request — replaces the
  // dashboard's pre-R3 N+1 loop that fired one PATCH per unread row.
  async markAllNotificationsAsRead(): Promise<void> {
    if (shouldUseMockData()) {
      throw new Error('Not implemented - mock mode');
    }
    return this.patch('/api/notifications/read-all');
  }

  // True "Clear all" — DELETEs every notification for the current user
  // in their org. Pre-R3 this name pointed at /read-all (mark-as-read),
  // which mislabelled the dashboard's "Clear all" button.
  async clearAllNotifications(): Promise<{ deletedCount: number }> {
    if (shouldUseMockData()) {
      return { deletedCount: 0 };
    }
    return this.delete<{ deletedCount: number }>('/api/notifications');
  }

  async deleteNotification(id: string): Promise<void> {
    if (shouldUseMockData()) {
      return;
    }
    return this.delete(`/api/notifications/${id}`);
  }

  async broadcastNotification(data: { message: string; type?: string }): Promise<{ sent: number }> {
    return this.post<{ sent: number }>('/api/notifications/broadcast', data);
  }

  // ============================================================================
  // BUG REPORTS
  // ============================================================================

  async createBugReport(data: API.CreateBugReportInput): Promise<API.BugReportSummary> {
    if (shouldUseMockData()) {
      // Demo mode: simulate latency + success without sending email.
      await new Promise(r => setTimeout(r, 300));
      return {
        id: `bug_demo_${Date.now()}`,
        status: 'open',
        createdAt: new Date().toISOString(),
      };
    }
    return this.post<API.BugReportSummary>('/api/bug-reports', data);
  }

  async getBugReports(params?: { page?: number; limit?: number; status?: API.BugReportStatus; severity?: API.BugReportSeverity }): Promise<API.BugReportListResponse> {
    if (shouldUseMockData()) {
      return { items: [], pagination: { page: 1, limit: 25, total: 0, hasMore: false } };
    }
    const search = new URLSearchParams();
    if (params?.page)     search.set('page', String(params.page));
    if (params?.limit)    search.set('limit', String(params.limit));
    if (params?.status)   search.set('status', params.status);
    if (params?.severity) search.set('severity', params.severity);
    const qs = search.toString();
    return this.get<API.BugReportListResponse>(`/api/bug-reports${qs ? `?${qs}` : ''}`);
  }

  async getBugReport(id: string): Promise<API.BugReport> {
    return this.get<API.BugReport>(`/api/bug-reports/${id}`);
  }

  async updateBugReportStatus(id: string, data: API.UpdateBugReportStatusInput): Promise<API.BugReport> {
    return this.patch<API.BugReport>(`/api/bug-reports/${id}/status`, data);
  }

  async healthCheck(): Promise<{ status: string }> {
    return { status: 'ok' };
  }

  // ============================================================================
  // ENTERPRISE SECURITY & AUTHENTICATION
  // ============================================================================

  async getLoginActivity(params?: { timeRange?: string }): Promise<API.LoginActivity[]> {
    if (shouldUseMockData()) {
      return mockData.getLoginActivity(params);
    }
    return this.get<API.LoginActivity[]>('/api/auth/login-activity', params);
  }

  async revokeSession(auditLogId: string): Promise<void> {
    if (shouldUseMockData()) {
      await new Promise(resolve => setTimeout(resolve, 500));
    } else {
      return this.post('/api/auth/revoke-session', { auditLogId });
    }
  }

  /**
   * Revoke every active session for this user, INCLUDING the current
   * one. Backend bumps `user.sessionRevokedAt = now` (invalidating all
   * JWTs issued before that timestamp) AND blacklists the caller's
   * current token. After this resolves, the caller is signed out — the
   * hook layer handles redirect to the login page.
   *
   * UX rationale for nuking the current session too: it removes the
   * "did this device get spared?" ambiguity. After Sign out everywhere
   * you sign in fresh, period. Same pattern as Google / GitHub.
   */
  async revokeAllSessions(): Promise<{ message: string }> {
    if (shouldUseMockData()) {
      await new Promise(resolve => setTimeout(resolve, 500));
      return { message: 'All sessions revoked.' };
    }
    return this.post<{ message: string }>('/api/auth/revoke-sessions');
  }

  // ─── Self-service Profile endpoints ─────────────────────────────────

  async getConnectedAccounts(): Promise<API.ConnectedAccountsResponse> {
    if (shouldUseMockData()) {
      return { accounts: [], hasPassword: true, enterpriseManaged: false };
    }
    return this.get<API.ConnectedAccountsResponse>('/api/auth/me/connected-accounts');
  }

  async disconnectAccount(provider: string): Promise<{ provider: string; disconnected: true }> {
    if (shouldUseMockData()) {
      return { provider, disconnected: true };
    }
    return this.delete<{ provider: string; disconnected: true }>(
      `/api/auth/me/connected-accounts/${encodeURIComponent(provider)}`
    );
  }

  async requestEmailChange(
    newEmail: string,
    currentPassword: string
  ): Promise<API.EmailChangeRequestResponse> {
    if (shouldUseMockData()) {
      return {
        message: 'Verification email sent (mock)',
        newEmail,
        expiresAt: new Date(Date.now() + 86_400_000).toISOString(),
      };
    }
    return this.post<API.EmailChangeRequestResponse>('/api/auth/me/email-change', {
      newEmail,
      currentPassword,
    });
  }

  async verifyEmailChange(token: string): Promise<{ email: string; message: string }> {
    if (shouldUseMockData()) {
      return { email: 'new@example.com', message: 'Email updated (mock)' };
    }
    return this.post<{ email: string; message: string }>('/api/auth/me/email-change/verify', {
      token,
    });
  }

  /**
   * GDPR Art. 20 — Streams a JSON file download.
   * Caller (the React hook) triggers browser save-as via a hidden link.
   * Uses raw fetch (not the typed wrapper) because the response is a
   * binary blob, not the JSON envelope our other endpoints return.
   */
  async exportMyData(): Promise<Blob> {
    if (shouldUseMockData()) {
      return new Blob(
        [JSON.stringify({ mock: true, note: 'enable real API for full export' }, null, 2)],
        { type: 'application/json' }
      );
    }
    const url = `${ENV.API_BASE_URL}/api/auth/me/data-export`;
    const token = localStorage.getItem('adjudon_token');
    const response = await fetch(url, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`Data export failed (${response.status}): ${text || response.statusText}`);
    }
    return await response.blob();
  }

  /**
   * GDPR Art. 17 — Self-deletion of the calling user's account.
   * The 409 OWNERSHIP_TRANSFER_REQUIRED path is surfaced as a normal
   * thrown error; the calling hook inspects err.code to render the
   * blocking-orgs list.
   */
  async deleteMyAccount(
    currentPassword: string,
    confirmation: 'DELETE'
  ): Promise<{ deleted: true; membershipsRemoved: number; auditEntriesNullified: number }> {
    if (shouldUseMockData()) {
      return { deleted: true, membershipsRemoved: 0, auditEntriesNullified: 0 };
    }
    return this.delete<{ deleted: true; membershipsRemoved: number; auditEntriesNullified: number }>(
      '/api/auth/me',
      { currentPassword, confirmation }
    );
  }

  // E-13: Refresh JWT — returns a new token if the current one is still valid
  async refreshSession(): Promise<{ token: string }> {
    return this.post<{ token: string }>('/api/auth/refresh');
  }

  /**
   * Step-up reauth — verify password, return fresh JWT.
   * Called by StepUpModal when a sensitive action returned
   * `code: "REAUTH_REQUIRED"`. On success the caller should retry
   * the original mutation with the new token already in TokenManager.
   */
  async reauth(password: string): Promise<{ token: string }> {
    return this.post<{ token: string }>('/api/auth/reauth', { password });
  }

  async getSecuritySettings(): Promise<API.SecuritySettings> {
    if (shouldUseMockData()) {
      return mockData.getSecuritySettings();
    }
    return this.get<API.SecuritySettings>('/api/auth/security-settings');
  }

  async setup2FA(): Promise<{ qrCode: string }> {
    if (shouldUseMockData()) {
      return { qrCode: '' };
    }
    return this.post<{ qrCode: string }>('/api/auth/mfa/setup');
  }

  async enable2FA(code: string): Promise<{ success: boolean; backupCodes?: string[] }> {
    if (shouldUseMockData()) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return { success: true, backupCodes: [] };
    }
    return this.post<{ success: boolean; backupCodes?: string[] }>('/api/auth/mfa/enable', { code });
  }

  async disable2FA(password: string): Promise<{ success: boolean }> {
    if (shouldUseMockData()) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return { success: true };
    }
    return this.post<{ success: boolean }>('/api/auth/mfa/disable', { password });
  }

  async generateBackupCodes(): Promise<API.BackupCodesResponse> {
    if (shouldUseMockData()) {
      return mockData.generateBackupCodes();
    }
    return this.post<API.BackupCodesResponse>('/api/auth/backup-codes/generate');
  }

  async removeTrustedDevice(deviceId: string): Promise<void> {
    if (shouldUseMockData()) {
      await new Promise(resolve => setTimeout(resolve, 500));
    } else {
      return this.delete(`/api/auth/trusted-devices/${deviceId}`);
    }
  }

  async requestAccountUnlock(email?: string): Promise<{ success: boolean }> {
    if (shouldUseMockData()) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return { success: true };
    }
    // Endpoint is PUBLIC + rate-limited; locked users cannot send a JWT, so
    // we pass the email in the body. The backend always returns generic
    // success to avoid account enumeration.
    return this.post<{ success: boolean }>(
      '/api/auth/account/unlock-request',
      email ? { email } : undefined,
    );
  }

  async updatePreferences(preferences: Record<string, unknown>): Promise<void> {
    return this.patch('/api/auth/me/preferences', preferences);
  }

  // Backend re-issues the JWT with the new `expiresIn` so the change
  // takes effect immediately. Caller must swap the returned token into
  // TokenManager — the `useUpdateSessionTimeout` hook does this.
  async updateSessionTimeout(sessionTimeout: string): Promise<{ sessionTimeout: string; token: string }> {
    return this.patch<{ sessionTimeout: string; token: string }>(
      '/api/auth/me/session-timeout',
      { sessionTimeout },
    );
  }

  async updateConsentPreferences(preferences: Record<string, boolean>): Promise<void> {
    if (shouldUseMockData()) {
      await new Promise(resolve => setTimeout(resolve, 500));
    } else {
      return this.post('/api/consent/preferences', preferences);
    }
  }

  async getConsentHistory(): Promise<API.ConsentHistoryEntry[]> {
    if (shouldUseMockData()) {
      return mockData.getConsentHistory();
    }
    return this.get<API.ConsentHistoryEntry[]>('/api/consent/history');
  }

  // Enterprise OIDC SSO
  async initiateSSO(email: string): Promise<{ authUrl: string }> {
    if (shouldUseMockData()) {
      await new Promise(resolve => setTimeout(resolve, 800));
      return { authUrl: `https://sso.example.com/oidc/auth?email=${encodeURIComponent(email)}` };
    }
    return this.post<{ authUrl: string }>('/api/auth/sso/initiate', { email });
  }

  async verifyDomain(domain: string): Promise<API.DomainVerificationResponse> {
    if (shouldUseMockData()) {
      return mockData.verifyDomain(domain);
    }
    return this.post<API.DomainVerificationResponse>('/api/domains', { domain });
  }

  // Magic Link
  async sendMagicLink(email: string): Promise<{ message: string }> {
    if (shouldUseMockData()) {
      return { message: 'Magic link sent to your email' };
    }
    return this.post<{ message: string }>('/api/auth/magic-link', { email });
  }

  async verifyMagicLink(token: string): Promise<API.LoginResponse> {
    if (shouldUseMockData()) {
      return mockData.login({ email: 'user@example.com', password: '' });
    }
    return this.post<API.LoginResponse>('/api/auth/magic-link/verify', { token });
  }

  // WebAuthn/Passkeys
  async getPasskeyRegistrationOptions(): Promise<API.PasskeyRegistrationOptions> {
    if (shouldUseMockData()) {
      return mockData.getPasskeyRegistrationOptions();
    }
    return this.get<API.PasskeyRegistrationOptions>('/api/auth/passkeys/registration/options');
  }

  async verifyPasskeyRegistration(credential: any): Promise<{ success: boolean }> {
    if (shouldUseMockData()) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return { success: true };
    }
    return this.post<{ success: boolean }>('/api/auth/passkeys/registration/verify', credential);
  }

  async getPasskeyAuthenticationOptions(): Promise<API.PasskeyAuthenticationOptions> {
    if (shouldUseMockData()) {
      return mockData.getPasskeyAuthenticationOptions();
    }
    return this.get<API.PasskeyAuthenticationOptions>('/api/auth/passkeys/authentication/options');
  }

  async verifyPasskeyAuthentication(credential: any): Promise<API.LoginResponse> {
    if (shouldUseMockData()) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return mockData.login({ email: 'user@example.com', password: '' });
    }
    return this.post<API.LoginResponse>('/api/auth/passkeys/authentication/verify', credential);
  }

  async listPasskeys(): Promise<API.PasskeyCredential[]> {
    if (shouldUseMockData()) {
      return mockData.listPasskeys();
    }
    return this.get<API.PasskeyCredential[]>('/api/auth/passkeys');
  }

  async removePasskey(id: string): Promise<void> {
    if (shouldUseMockData()) {
      await new Promise(resolve => setTimeout(resolve, 500));
    } else {
      return this.delete(`/api/auth/passkeys/${id}`);
    }
  }

  async renamePasskey(id: string, name: string): Promise<void> {
    if (shouldUseMockData()) {
      await new Promise(resolve => setTimeout(resolve, 500));
    } else {
      return this.patch(`/api/auth/passkeys/${id}`, { name });
    }
  }

  // Password History — returns isReused=true when the candidate password matches the
  // current password or any of the user's last-N stored password hashes.
  async checkPasswordHistory(password: string): Promise<{ isReused: boolean }> {
    if (shouldUseMockData()) {
      return mockData.checkPasswordHistory(password);
    }
    return this.post<{ isReused: boolean }>('/api/auth/password-history/check', { password });
  }

  // Anomalies & Drift Detection
  async getAnomalies(params?: { sinceHours?: number; baselineDays?: number }): Promise<unknown[]> {
    if (shouldUseMockData()) {
      return mockData.getAnomalies(params) as Promise<unknown[]>;
    }
    return this.get<unknown[]>('/api/analytics/anomalies', params);
  }

  // Decision Patterns (Mining)
  async getDecisionPatterns(params?: { days?: number; agentId?: string }): Promise<any> {
    if (shouldUseMockData()) {
      return mockData.getDecisionPatterns(params);
    }
    // The backend (`analyticsRoutes.js GET /decision-patterns`) reads
    // `dateFrom` / `dateTo`, NOT `days` — passing `days` directly was
    // silently dropped server-side, so the range selector (7/30/90) was a
    // no-op and the response always reflected the default 30-day window.
    // Convert `days` → ISO `dateFrom` here so the wire contract matches.
    const queryParams: { dateFrom?: string; agentId?: string } = {};
    if (params?.days && Number.isFinite(params.days) && params.days > 0) {
      const from = new Date();
      from.setDate(from.getDate() - params.days);
      from.setHours(0, 0, 0, 0);
      queryParams.dateFrom = from.toISOString();
    }
    if (params?.agentId) queryParams.agentId = params.agentId;
    return this.get<any>('/api/analytics/decision-patterns', queryParams);
  }

  // Feedback & Corrections Analytics
  async getFeedbackAnalytics(params?: { days?: number }): Promise<any> {
    if (shouldUseMockData()) {
      return mockData.getFeedbackAnalytics(params);
    }
    return this.get<any>('/api/cpi/analytics', params);
  }

  // Usage & Quota Tracking
  async getUsageCurrent(): Promise<any> {
    if (shouldUseMockData()) {
      return mockData.getUsageCurrent();
    }
    const raw = await this.get<any>('/api/usage/current');
    // Backend returns flat fields; normalize to nested `traces` object for all consumers
    if (raw && !raw.traces) {
      raw.traces = {
        used: raw.thisMonth ?? 0,
        limit: raw.limit ?? 0,
        remaining: raw.remaining ?? 0,
        percentage: raw.percentUsed ?? 0,
      };
    }
    return raw;
  }

  async getUsageHistory(): Promise<any> {
    if (shouldUseMockData()) {
      return mockData.getUsageHistory();
    }
    return this.get<any>('/api/usage/history');
  }

  // Super Admin
  async getAdminStats(): Promise<API.AdminPlatformStats> {
    return this.get<API.AdminPlatformStats>('/api/admin/stats');
  }

  async getAdminMRR(): Promise<{ mrr: number | null; currency: string; trialing: number; warning?: string }> {
    return this.get<any>('/api/admin/mrr');
  }

  async getAdminActivity(params?: { limit?: number }): Promise<{ entries: API.AdminActivityEntry[] }> {
    return this.get<{ entries: API.AdminActivityEntry[] }>('/api/admin/activity', params);
  }

  async getMaintenanceStatus(): Promise<{ maintenanceMode: boolean; message: string }> {
    return this.get<any>('/api/admin/maintenance');
  }

  async toggleMaintenanceMode(payload: { enable: boolean; message?: string }): Promise<{ maintenanceMode: boolean; message: string }> {
    return this.post<any>('/api/admin/maintenance', payload);
  }

  async getAdminOrganizations(params?: { page?: number; limit?: number; search?: string }): Promise<API.AdminOrganization[]> {
    return this.get<API.AdminOrganization[]>('/api/admin/orgs', params);
  }

  async getAdminUsers(params?: { page?: number; limit?: number; search?: string }): Promise<API.AdminUser[]> {
    return this.get<API.AdminUser[]>('/api/admin/users', params);
  }

  async setAdminOrganizationStatus(id: string, isActive: boolean): Promise<{ orgId: string; isActive: boolean }> {
    return this.patch<{ orgId: string; isActive: boolean }>(`/api/admin/orgs/${id}/status`, { isActive });
  }

  async setAdminOrganizationPlan(id: string, plan: string): Promise<{ orgId: string; plan: string }> {
    return this.patch<{ orgId: string; plan: string }>(`/api/admin/orgs/${id}/plan`, { plan });
  }

  async setAdminUserStatus(id: string, isActive: boolean): Promise<{ userId: string; isActive: boolean }> {
    return this.patch<{ userId: string; isActive: boolean }>(`/api/admin/users/${id}/status`, { isActive });
  }

  async getAdminOrganization(id: string): Promise<any> {
    return this.get<any>(`/api/admin/orgs/${id}`);
  }

  async deleteAdminOrganization(id: string): Promise<void> {
    return this.delete<void>(`/api/admin/orgs/${id}`);
  }

  async impersonateAdminUser(id: string): Promise<{ token: string; user: any }> {
    return this.post<{ token: string; user: any }>(`/api/admin/users/${id}/impersonate`);
  }

  async getDemoRequests(params?: { status?: string }): Promise<any[]> {
    return this.get<any[]>('/api/demo-requests', params);
  }

  async updateDemoRequest(id: string, updates: { status: string; notes?: string }): Promise<any> {
    return this.patch<any>(`/api/demo-requests/${id}`, updates);
  }


  // API Key management
  async updateApiKey(id: string, updates: { name: string }): Promise<API.APIKey> {
    if (shouldUseMockData()) {
      throw new Error('Not implemented - mock mode');
    }
    return this.patch<API.APIKey>(`/api/auth/keys/${id}`, updates);
  }

  // Multi-org
  async getUserOrgs(): Promise<any[]> {
    if (shouldUseMockData()) {
      return [];
    }
    return this.get<any[]>('/api/auth/me/orgs');
  }

  async switchOrg(orgId: string): Promise<{ token: string }> {
    if (shouldUseMockData()) {
      throw new Error('Not implemented - mock mode');
    }
    return this.post<{ token: string }>('/api/auth/switch-org', { orgId });
  }

  async createOrg(data: { name: string; industry?: string; region?: string; billingEmail?: string }): Promise<{ token: string; organizationId: string; organizationName: string }> {
    if (shouldUseMockData()) {
      throw new Error('Not implemented - mock mode');
    }
    return this.post('/api/org', data);
  }

  // Audit chain verification — response shape matches the backend
  // /api/audit/verify handler exactly. Previous return type
  // `{ valid, details }` was a fictional shape that never matched the
  // real `{ verified, totalEntries, lastHash, error }` envelope, so
  // every consumer reading `result.valid` got `undefined` and silently
  // treated the chain as invalid even when it wasn't.
  async verifyAuditChain(): Promise<{
    verified: boolean;
    totalEntries: number;
    lastHash: string | null;
    error?: string;
  }> {
    if (shouldUseMockData()) {
      return { verified: true, totalEntries: 0, lastHash: null };
    }
    return this.get('/api/audit/verify');
  }

  // Team invites
  async getTeamInvites(): Promise<any[]> {
    if (shouldUseMockData()) {
      return mockData.getTeamInvites();
    }
    // Backend (teamController.getInvites) projiziert `_id` — wie getTeamMembers
    // auf `id` normalisieren, sonst laufen Resend/Revoke gegen /undefined.
    const raw = await this.get<any[]>('/api/team/invites');
    return raw.map((i) => ({ ...i, id: i.id || i._id?.toString() }));
  }

  async revokeTeamInvite(id: string): Promise<void> {
    if (shouldUseMockData()) {
      return mockData.revokeTeamInvite(id);
    }
    return this.delete<void>(`/api/team/invites/${id}`);
  }

  async resendInvite(id: string): Promise<{ message: string }> {
    if (shouldUseMockData()) {
      return { message: 'Invite resent (mock)' };
    }
    return this.post<{ message: string }>(`/api/team/invites/${id}/resend`);
  }

  // Agent compare & versions — backend expects GET ?agentId=X&v1=Y&v2=Z
  async compareAgents(params: { agentId: string; v1: string; v2: string }): Promise<any> {
    if (shouldUseMockData()) {
      return mockData.compareAgentVersions(params.agentId, params.v1, params.v2);
    }
    const q = new URLSearchParams({ agentId: params.agentId, v1: params.v1, v2: params.v2 }).toString();
    return this.get<any>(`/api/analytics/agent-compare?${q}`);
  }

  async getAgentVersions(id: string): Promise<any[]> {
    if (shouldUseMockData()) {
      return mockData.getAgentVersions(id);
    }
    return this.get<any[]>(`/api/agents/${id}/versions`);
  }

  async getSavedViews(params?: { workspaceId?: string }): Promise<any[]> {
    return this.get<any[]>('/api/saved-views', params);
  }

  async createSavedView(payload: { name: string; filters: Record<string, any>; workspaceId?: string }): Promise<any> {
    return this.post<any>('/api/saved-views', payload);
  }

  async updateSavedView(id: string, payload: { name?: string; filters?: Record<string, any> }): Promise<any> {
    return this.patch<any>(`/api/saved-views/${id}`, payload);
  }

  async deleteSavedView(id: string): Promise<void> {
    return this.delete<void>(`/api/saved-views/${id}`);
  }

  // Team Invites
  async validateInviteToken(token: string): Promise<{ organizationName: string; email: string; role: string }> {
    return this.get(`/api/team/invites/validate/${token}`);
  }

  async acceptInvite(token: string, data: { name?: string; password: string }): Promise<API.LoginResponse> {
    return this.post<API.LoginResponse>(`/api/team/invites/${token}/accept`, data);
  }

  async getSystemHealth(): Promise<{ status: string; version: string; uptimeSeconds: number; checks: { database: string } }> {
    return this.get('/health');
  }

  // Founder Analytics (#50 Sub-Task D)
  async getFounderAnalytics(): Promise<API.FounderAnalytics> {
    if (shouldUseMockData()) return mockData.getFounderAnalytics();
    return this.get<API.FounderAnalytics>('/api/analytics/founder');
  }

  // ── In-App Nudges (#7 Activation — Block D) ─────────────────────────
  async getActiveNudges(): Promise<API.NudgesResponse> {
    if (shouldUseMockData()) return mockData.getActiveNudges();
    return this.get<API.NudgesResponse>('/api/users/me/nudges');
  }

  // ── Phase 1 — Hash-Chain Audit ──────────────────────────────────────
  async getHashChainStatus(): Promise<API.HashChainStatus> {
    if (shouldUseMockData()) return mockData.getHashChainStatus();
    return this.get<API.HashChainStatus>('/api/hash-chain/status');
  }

  async verifyHashChain(): Promise<API.HashChainVerifyResult> {
    if (shouldUseMockData()) return mockData.verifyHashChain();
    return this.post<API.HashChainVerifyResult>('/api/hash-chain/verify', {});
  }

  /**
   * Lücken-Bericht — ehrliche Erfassungs-Abdeckung der Kette.
   * Backend: GET /api/hash-chain/coverage (hashChainService.getCoverageReport).
   */
  async getHashChainCoverage(): Promise<API.HashChainCoverage> {
    if (shouldUseMockData()) return mockData.getHashChainCoverage();
    return this.get<API.HashChainCoverage>('/api/hash-chain/coverage');
  }

  async listHashChainEntries(params?: { limit?: number; cursor?: string }): Promise<{ entries: API.HashChainEntry[]; hasMore?: boolean; nextCursor?: string | null }> {
    if (shouldUseMockData()) return mockData.listHashChainEntries(params);
    // Backend (hashChainController.listEntries) liefert { entries, hasMore }.
    return this.get('/api/hash-chain/entries', params);
  }

  async getHashChainEntry(id: string): Promise<API.HashChainEntry> {
    if (shouldUseMockData()) return mockData.getHashChainEntry(id);
    return this.get<API.HashChainEntry>(`/api/hash-chain/entries/${id}`);
  }

  /**
   * Fetch the chain entry for a given DecisionTrace + its Merkle inclusion proof.
   * Backend: GET /api/hash-chain/entry/:traceId → { entry, proof }.
   */
  async getHashChainEntryByTrace(
    traceId: string,
  ): Promise<{ entry: API.HashChainEntry; proof: API.MerkleInclusionProof | null }> {
    if (shouldUseMockData()) return mockData.getHashChainEntryByTrace(traceId);
    return this.get<{ entry: API.HashChainEntry; proof: API.MerkleInclusionProof | null }>(
      `/api/hash-chain/entry/${encodeURIComponent(traceId)}`,
    );
  }

  async listMerkleAnchors(params?: { limit?: number }): Promise<API.MerkleAnchor[]> {
    if (shouldUseMockData()) return mockData.listMerkleAnchors(params);
    // Backend now returns a bare projected array; the previous envelope
    // `{ anchors, from, to }` was confusing the type contract.
    return this.get<API.MerkleAnchor[]>('/api/hash-chain/anchors', params);
  }

  async getMerkleProof(anchorId: string, sequence: number): Promise<API.MerkleInclusionProof> {
    if (shouldUseMockData()) return mockData.getMerkleProof(anchorId, sequence);
    return this.get<API.MerkleInclusionProof>(`/api/hash-chain/anchors/${anchorId}/proof`, { entry: sequence });
  }

  async exportHashChainBundle(params: { fromSequence?: number; toSequence?: number; format?: 'json' | 'jsonl' }): Promise<API.HashChainBundle> {
    if (shouldUseMockData()) return mockData.exportHashChainBundle(params);
    // Backend exposes both `/export` (canonical) and `/bundle` (dashboard
    // alias) — using `/bundle` here matches the React Query hook name
    // (`useExportHashChainBundle`) and aligns with the `getHashChainEntry`
    // / `listHashChainEntries` operational naming convention.
    return this.get<API.HashChainBundle>('/api/hash-chain/bundle', params);
  }

  // ── Phase 1 — Compliance Mapping ────────────────────────────────────
  async getComplianceMapping(framework: string = 'iso-42001'): Promise<API.ComplianceMappingNotes> {
    if (shouldUseMockData()) return mockData.getComplianceMapping(framework);
    return this.get<API.ComplianceMappingNotes>('/api/compliance/mapping', { framework });
  }

  async updateComplianceMappingNote(payload: { clauseId: string; status: API.Iso42001ClauseStatus; evidence: string; framework?: string }): Promise<API.ComplianceMappingNote> {
    if (shouldUseMockData()) return mockData.updateComplianceMappingNote(payload);
    return this.patch<API.ComplianceMappingNote>('/api/compliance/mapping', payload);
  }

  // ── Phase 2 — Incidents (Multi-Clock) ───────────────────────────────
  async listIncidents(): Promise<API.Incident[]> {
    if (shouldUseMockData()) return mockData.listIncidents();
    // Backend list() liefert { incidents: [...] } (ok(res, { incidents })) —
    // hier EINMAL entpacken, damit jeder Konsument ein echtes Array bekommt.
    // (Review-CRITICAL: der Array-Direktzugriff ließ das Cockpit + Fristen-Band
    // gegen das Live-Backend permanent leer laufen; Mock-Shape maskierte es.)
    const raw = await this.get<{ incidents?: API.Incident[] } | API.Incident[]>('/api/incidents');
    return Array.isArray(raw) ? raw : (raw?.incidents ?? []);
  }

  async getIncident(id: string): Promise<API.Incident> {
    if (shouldUseMockData()) return mockData.getIncident(id);
    return this.get<API.Incident>(`/api/incidents/${id}`);
  }

  async createIncident(payload: Partial<API.Incident>): Promise<API.Incident> {
    if (shouldUseMockData()) return mockData.createIncident(payload);
    return this.post<API.Incident>('/api/incidents', payload);
  }

  async updateIncident(id: string, payload: Partial<API.Incident>): Promise<API.Incident> {
    if (shouldUseMockData()) return mockData.updateIncident(id, payload);
    return this.patch<API.Incident>(`/api/incidents/${id}`, payload);
  }

  /**
   * Submission-Tracking EINES Regulator-Eintrags (Az., Zeitpunkt, Behörden-
   * Vorgang) — der generische updateIncident-PATCH verwirft `regulators[]`
   * absichtlich (Whitelist; wholesale-Replace würde die Uhren desyncen).
   */
  async recordIncidentRegulatorSubmission(
    incidentId: string,
    code: string,
    payload: { submittedAt?: string | null; submissionRef?: string; authorityCaseId?: string; responseReceived?: boolean; responseAt?: string | null },
  ): Promise<API.Incident> {
    if (shouldUseMockData()) return mockData.recordIncidentRegulatorSubmission(incidentId, code, payload);
    return this.patch<API.Incident>(
      `/api/incidents/${encodeURIComponent(incidentId)}/regulators/${encodeURIComponent(code)}`,
      payload,
    );
  }

  async closeIncident(id: string, payload: { rootCause: string; remediation: string }): Promise<API.Incident> {
    if (shouldUseMockData()) return mockData.closeIncident(id, payload);
    return this.post<API.Incident>(`/api/incidents/${id}/close`, payload);
  }

  async completeIncidentCheckpoint(incidentId: string, checkpointId: string, payload: { evidenceUrl?: string; notes?: string }): Promise<API.IncidentClock> {
    if (shouldUseMockData()) return mockData.completeIncidentCheckpoint(incidentId, checkpointId, payload);
    return this.post<API.IncidentClock>(`/api/incidents/${incidentId}/checkpoints/${checkpointId}/complete`, payload);
  }

  // ── DORA-Hub — Art. 8 / 17–19 / 28–30 (2026-06-10) ────────────────────

  /** Art. 8 — ICT-Asset-Register der KI-Schicht (nicht plan-gated). */
  async getIctAssetRegister(): Promise<API.IctAssetRegisterResponse> {
    if (shouldUseMockData()) return mockData.getIctAssetRegister();
    return this.get<API.IctAssetRegisterResponse>('/api/ict-asset-register');
  }

  async getIctAssetRegisterMarkdown(): Promise<API.IctAssetRegisterMarkdownResponse> {
    if (shouldUseMockData()) return mockData.getIctAssetRegisterMarkdown();
    return this.get<API.IctAssetRegisterMarkdownResponse>('/api/ict-asset-register/markdown');
  }

  /** Art. 18 — stateless Klassifizierungs-Rechner (RTS 2024/1772). */
  async classifyDoraIncident(criteria: API.DoraClassificationInput): Promise<API.DoraIncidentAssessment> {
    if (shouldUseMockData()) return mockData.classifyDoraIncident(criteria);
    return this.post<API.DoraIncidentAssessment>('/api/incidents/classify', { assessmentType: 'incident', criteria });
  }

  /** Art. 19 — vorbefuellter Melde-Entwurf (roher Markdown, KEIN JSON-Envelope). */
  async getDoraIncidentReport(incidentId: string, stage: API.DoraReportStage): Promise<string> {
    if (shouldUseMockData()) return mockData.getDoraIncidentReport(incidentId, stage);
    const baseUrl = ENV.API_BASE_URL || '';
    const token = localStorage.getItem('adjudon_token');
    const res = await fetch(`${baseUrl}/api/incidents/${encodeURIComponent(incidentId)}/dora-report/${stage}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`DORA-Report fehlgeschlagen: ${res.status}`);
    return res.text();
  }

  // Art. 17(2)/18(2) — Cyber-Bedrohungs-Register (plan-gated multiClockIncidents)
  async listCyberThreats(params?: { status?: string; search?: string; limit?: number }): Promise<API.CyberThreatListResponse> {
    if (shouldUseMockData()) return mockData.listCyberThreats(params);
    return this.get<API.CyberThreatListResponse>('/api/cyber-threats', params);
  }

  async createCyberThreat(payload: { title: string; summary?: string; source?: string; detectedAt?: string }): Promise<API.CyberThreatRecord> {
    if (shouldUseMockData()) return mockData.createCyberThreat(payload);
    return this.post<API.CyberThreatRecord>('/api/cyber-threats', payload);
  }

  async assessCyberThreat(
    id: string,
    payload: { couldAffectCriticalFunctions: boolean; highProbabilityOfMaterialisation: boolean; couldMeetMaterialityThresholds: boolean; decision?: 'significant' | 'not-significant' },
  ): Promise<API.CyberThreatAssessResponse> {
    if (shouldUseMockData()) return mockData.assessCyberThreat(id, payload);
    return this.post<API.CyberThreatAssessResponse>(`/api/cyber-threats/${encodeURIComponent(id)}/assess`, payload);
  }

  async updateCyberThreat(
    id: string,
    payload: { title?: string; summary?: string; source?: string; status?: API.CyberThreatStatus; voluntaryNotification?: { decided: boolean; notifiedAt?: string | null; reference?: string | null } },
  ): Promise<API.CyberThreatRecord> {
    if (shouldUseMockData()) return mockData.updateCyberThreat(id, payload);
    return this.patch<API.CyberThreatRecord>(`/api/cyber-threats/${encodeURIComponent(id)}`, payload);
  }

  // Art. 28–30 — Informationsregister (plan-gated subProcessorRegistry)
  async listDoraArrangements(params?: { status?: string; critical?: string; limit?: number }): Promise<API.DoraArrangementsListResponse> {
    if (shouldUseMockData()) return mockData.listDoraArrangements(params);
    return this.get<API.DoraArrangementsListResponse>('/api/dora-register', params);
  }

  async getDoraRegisterCompliance(): Promise<API.DoraRegisterComplianceResponse> {
    if (shouldUseMockData()) return mockData.getDoraRegisterCompliance();
    return this.get<API.DoraRegisterComplianceResponse>('/api/dora-register/compliance');
  }

  async getDoraRegisterConcentration(): Promise<API.DoraRegisterConcentrationResponse> {
    if (shouldUseMockData()) return mockData.getDoraRegisterConcentration();
    return this.get<API.DoraRegisterConcentrationResponse>('/api/dora-register/concentration');
  }

  async getDoraRegisterYearlyReport(): Promise<API.DoraRegisterYearlyResponse> {
    if (shouldUseMockData()) return mockData.getDoraRegisterYearlyReport();
    return this.get<API.DoraRegisterYearlyResponse>('/api/dora-register/yearly-report');
  }

  async createDoraArrangement(payload: Partial<API.DoraArrangement>): Promise<API.DoraArrangement> {
    if (shouldUseMockData()) return mockData.createDoraArrangement(payload);
    return this.post<API.DoraArrangement>('/api/dora-register', payload);
  }

  async updateDoraArrangement(id: string, payload: Partial<API.DoraArrangement>): Promise<API.DoraArrangement> {
    if (shouldUseMockData()) return mockData.updateDoraArrangement(id, payload);
    return this.patch<API.DoraArrangement>(`/api/dora-register/${encodeURIComponent(id)}`, payload);
  }

  async terminateDoraArrangement(id: string): Promise<API.DoraArrangement> {
    if (shouldUseMockData()) return mockData.terminateDoraArrangement(id);
    return this.post<API.DoraArrangement>(`/api/dora-register/${encodeURIComponent(id)}/terminate`, {});
  }

  async seedDoraAdjudonSelf(): Promise<{ created: boolean }> {
    if (shouldUseMockData()) return mockData.seedDoraAdjudonSelf();
    return this.post<{ created: boolean }>('/api/dora-register/seed-adjudon', {});
  }

  // ── GwG/AML-Hub — § 5 / § 6 / §§ 10–15 (2026-06-10) ──────────────────

  /** § 5 — aktuelle Risiko-Analyse-Version. 404 = noch keine erfasst. */
  async getCurrentRiskAssessment(): Promise<API.GwgRiskAssessmentDetailResponse> {
    if (shouldUseMockData()) return mockData.getCurrentRiskAssessment();
    return this.get<API.GwgRiskAssessmentDetailResponse>('/api/v1/risk-assessments/current');
  }

  async listRiskAssessmentVersions(): Promise<API.GwgRiskAssessmentVersionsResponse> {
    if (shouldUseMockData()) return mockData.listRiskAssessmentVersions();
    return this.get<API.GwgRiskAssessmentVersionsResponse>('/api/v1/risk-assessments/versions');
  }

  /** § 5 — BaFin-Export (Markdown) der aktuellen oder einer bestimmten Version. */
  async exportRiskAssessmentMarkdown(version?: number): Promise<API.GwgRiskAssessmentExportResponse> {
    if (shouldUseMockData()) return mockData.exportRiskAssessmentMarkdown(version);
    return this.get<API.GwgRiskAssessmentExportResponse>('/api/v1/risk-assessments/export', {
      format: 'markdown',
      ...(version !== undefined ? { version } : {}),
    });
  }

  // § 6 — Transaction-Alert-Triage (Detektion macht das AML-System des
  // Versicherers; Adjudon erfasst + tracked + diszipliniert die Bearbeitung).
  async listTransactionAlerts(params?: { status?: string; severity?: string; alertRef?: string; limit?: number }): Promise<API.TransactionAlertListResponse> {
    if (shouldUseMockData()) return mockData.listTransactionAlerts(params);
    return this.get<API.TransactionAlertListResponse>('/api/v1/transaction-alerts', params);
  }

  async transitionTransactionAlert(
    id: string,
    payload: { status: API.TransactionAlertStatus; disposition?: API.TransactionAlertDisposition; assignedToRef?: string },
  ): Promise<API.TransactionAlertDetailResponse> {
    if (shouldUseMockData()) return mockData.transitionTransactionAlert(id, payload);
    return this.post<API.TransactionAlertDetailResponse>(`/api/v1/transaction-alerts/${encodeURIComponent(id)}/transition`, payload);
  }

  async disposeTransactionAlert(id: string, disposition: API.TransactionAlertDisposition): Promise<API.TransactionAlertDetailResponse> {
    if (shouldUseMockData()) return mockData.disposeTransactionAlert(id, disposition);
    return this.post<API.TransactionAlertDetailResponse>(`/api/v1/transaction-alerts/${encodeURIComponent(id)}/dispose`, { disposition });
  }

  // §§ 10–15 — Evidenz-Register (Erfassung läuft über SDK/API der
  // Kunden-Systeme; das Dashboard ist der Prüf-Blick).
  async listEddAssessments(params?: { status?: string; triggerReason?: string; customerRef?: string; limit?: number }): Promise<API.EddListResponse> {
    if (shouldUseMockData()) return mockData.listEddAssessments(params);
    return this.get<API.EddListResponse>('/api/v1/edd', params);
  }

  async listScreenings(params?: { verdict?: string; status?: string; overdue?: string; limit?: number }): Promise<API.ScreeningListResponse> {
    if (shouldUseMockData()) return mockData.listScreenings(params);
    return this.get<API.ScreeningListResponse>('/api/v1/screening', params);
  }

  async listAmlBeneficiaries(params?: { customerRef?: string; status?: string; isPep?: string; limit?: number }): Promise<API.AmlBeneficiaryListResponse> {
    if (shouldUseMockData()) return mockData.listAmlBeneficiaries(params);
    return this.get<API.AmlBeneficiaryListResponse>('/api/v1/beneficiaries', params);
  }

  // ── Solvency II Säule 1 — Modell-Evidenz (Layer B, 2026-06-11) ───────

  /** Art. 120/82/121(3)/124/125 — Evidenz-Aggregat der KI-Komponenten. Gate esgReporting. */
  async getSolvencyPillar1Evidence(params?: { windowDays?: number; format?: 'markdown' }): Promise<API.SolvencyPillar1Evidence> {
    if (shouldUseMockData()) return mockData.getSolvencyPillar1Evidence(params);
    return this.get<API.SolvencyPillar1Evidence>('/api/v1/solvency-pillar1/model-evidence', params);
  }

  // ── VVG/IDD-Hub — § 60 / IDD Art. 19 / Wiedervorlage (2026-06-11) ────
  // Alle drei Module: Gate advisoryDocumentation (governance+).

  async listProductComparisons(params?: { status?: string; advisoryRef?: string; limit?: number }): Promise<API.ProductComparisonListResponse> {
    if (shouldUseMockData()) return mockData.listProductComparisons(params);
    return this.get<API.ProductComparisonListResponse>('/api/v1/product-comparisons', params);
  }

  async createProductComparison(payload: Partial<API.ProductComparison>): Promise<API.ProductComparisonDetailResponse> {
    if (shouldUseMockData()) return mockData.createProductComparison(payload);
    return this.post<API.ProductComparisonDetailResponse>('/api/v1/product-comparisons', payload);
  }

  async updateProductComparison(id: string, payload: Partial<API.ProductComparison>): Promise<API.ProductComparisonDetailResponse> {
    if (shouldUseMockData()) return mockData.updateProductComparison(id, payload);
    return this.patch<API.ProductComparisonDetailResponse>(`/api/v1/product-comparisons/${encodeURIComponent(id)}`, payload);
  }

  async finalizeProductComparison(id: string): Promise<API.ProductComparisonDetailResponse> {
    if (shouldUseMockData()) return mockData.finalizeProductComparison(id);
    return this.post<API.ProductComparisonDetailResponse>(`/api/v1/product-comparisons/${encodeURIComponent(id)}/finalize`, {});
  }

  async deleteProductComparison(id: string): Promise<{ deleted: boolean }> {
    if (shouldUseMockData()) return mockData.deleteProductComparison(id);
    return this.delete<{ deleted: boolean }>(`/api/v1/product-comparisons/${encodeURIComponent(id)}`);
  }

  async listCommissionDisclosures(params?: { status?: string; linkedAdvisoryRef?: string; search?: string; limit?: number }): Promise<API.CommissionDisclosureListResponse> {
    if (shouldUseMockData()) return mockData.listCommissionDisclosures(params);
    return this.get<API.CommissionDisclosureListResponse>('/api/v1/commission-disclosures', params);
  }

  async createCommissionDisclosure(payload: Partial<API.CommissionDisclosure>): Promise<API.CommissionDisclosureDetailResponse> {
    if (shouldUseMockData()) return mockData.createCommissionDisclosure(payload);
    return this.post<API.CommissionDisclosureDetailResponse>('/api/v1/commission-disclosures', payload);
  }

  async updateCommissionDisclosure(id: string, payload: Partial<API.CommissionDisclosure>): Promise<API.CommissionDisclosureDetailResponse> {
    if (shouldUseMockData()) return mockData.updateCommissionDisclosure(id, payload);
    return this.patch<API.CommissionDisclosureDetailResponse>(`/api/v1/commission-disclosures/${encodeURIComponent(id)}`, payload);
  }

  async discloseCommissionDisclosure(id: string): Promise<API.CommissionDisclosureDetailResponse> {
    if (shouldUseMockData()) return mockData.discloseCommissionDisclosure(id);
    return this.post<API.CommissionDisclosureDetailResponse>(`/api/v1/commission-disclosures/${encodeURIComponent(id)}/disclose`, {});
  }

  async acknowledgeCommissionDisclosure(id: string): Promise<API.CommissionDisclosureDetailResponse> {
    if (shouldUseMockData()) return mockData.acknowledgeCommissionDisclosure(id);
    return this.post<API.CommissionDisclosureDetailResponse>(`/api/v1/commission-disclosures/${encodeURIComponent(id)}/acknowledge`, {});
  }

  async archiveCommissionDisclosure(id: string): Promise<API.CommissionDisclosureDetailResponse> {
    if (shouldUseMockData()) return mockData.archiveCommissionDisclosure(id);
    return this.post<API.CommissionDisclosureDetailResponse>(`/api/v1/commission-disclosures/${encodeURIComponent(id)}/archive`, {});
  }

  async listPeriodicReviews(params?: { status?: string; advisoryRef?: string; limit?: number }): Promise<API.PeriodicReviewListResponse> {
    if (shouldUseMockData()) return mockData.listPeriodicReviews(params);
    return this.get<API.PeriodicReviewListResponse>('/api/v1/periodic-reviews', params);
  }

  async createPeriodicReview(payload: { advisoryRef: string; customerRef?: string; nextReviewDue?: string; reviewReason?: string }): Promise<API.PeriodicReviewDetailResponse> {
    if (shouldUseMockData()) return mockData.createPeriodicReview(payload);
    return this.post<API.PeriodicReviewDetailResponse>('/api/v1/periodic-reviews', payload);
  }

  async completePeriodicReview(id: string, payload: { whatChanged: string }): Promise<API.PeriodicReviewDetailResponse> {
    if (shouldUseMockData()) return mockData.completePeriodicReview(id, payload);
    return this.post<API.PeriodicReviewDetailResponse>(`/api/v1/periodic-reviews/${encodeURIComponent(id)}/complete`, payload);
  }

  async reschedulePeriodicReview(id: string, payload: { nextReviewDue: string }): Promise<API.PeriodicReviewDetailResponse> {
    if (shouldUseMockData()) return mockData.reschedulePeriodicReview(id, payload);
    return this.post<API.PeriodicReviewDetailResponse>(`/api/v1/periodic-reviews/${encodeURIComponent(id)}/reschedule`, payload);
  }

  // ── EIOPA-Model-Cards — 9-Felder-Governance-Layer (2026-06-11) ───────

  /** Aktuelle Model-Card eines KI-Systems. 404 MODELCARD_NOT_FOUND = noch keine. */
  async getCurrentModelCard(agentId: string): Promise<API.ModelCardCurrentResponse> {
    if (shouldUseMockData()) return mockData.getCurrentModelCard(agentId);
    return this.get<API.ModelCardCurrentResponse>(`/api/v1/model-cards/${encodeURIComponent(agentId)}/current`);
  }

  async listModelCardVersions(agentId: string): Promise<API.ModelCardVersionsResponse> {
    if (shouldUseMockData()) return mockData.listModelCardVersions(agentId);
    return this.get<API.ModelCardVersionsResponse>(`/api/v1/model-cards/${encodeURIComponent(agentId)}/versions`);
  }

  async exportModelCard(agentId: string): Promise<{ markdown: string }> {
    if (shouldUseMockData()) return mockData.exportModelCard(agentId);
    // Backend akzeptiert exakt 'markdown' (sonst JSON-Branch ohne markdown-Feld).
    return this.get<{ markdown: string }>(`/api/v1/model-cards/${encodeURIComponent(agentId)}/export`, { format: 'markdown' });
  }

  /** Neue (immutable) Version anlegen — prior-Versionen bleiben unverändert. */
  async createModelCardVersion(agentId: string, payload: { fields: Partial<Record<API.ModelCardFieldKey, string>>; changeSummary?: string }): Promise<{ modelCard: API.ModelCardRecord }> {
    if (shouldUseMockData()) return mockData.createModelCardVersion(agentId, payload);
    return this.post<{ modelCard: API.ModelCardRecord }>(`/api/v1/model-cards/${encodeURIComponent(agentId)}`, payload);
  }

  // ── Phase 2 — Auto-Approval Engine ──────────────────────────────────
  async listApprovalPatterns(): Promise<API.ApprovalPattern[]> {
    if (shouldUseMockData()) return mockData.listApprovalPatterns();
    return this.get<API.ApprovalPattern[]>('/api/auto-approval/patterns');
  }

  async getApprovalPattern(id: string): Promise<API.ApprovalPattern> {
    if (shouldUseMockData()) return mockData.getApprovalPattern(id);
    return this.get<API.ApprovalPattern>(`/api/auto-approval/patterns/${id}`);
  }

  async createApprovalPattern(payload: Partial<API.ApprovalPattern>): Promise<API.ApprovalPattern> {
    if (shouldUseMockData()) return mockData.createApprovalPattern(payload);
    return this.post<API.ApprovalPattern>('/api/auto-approval/patterns', payload);
  }

  async pauseApprovalPattern(id: string): Promise<API.ApprovalPattern> {
    if (shouldUseMockData()) return mockData.pauseApprovalPattern(id);
    return this.post<API.ApprovalPattern>(`/api/auto-approval/patterns/${id}/pause`, {});
  }

  async signoffApprovalPattern(id: string, payload: { reviewerRole: 'primary' | 'secondary' }): Promise<API.ApprovalPattern> {
    if (shouldUseMockData()) return mockData.signoffApprovalPattern(id, payload);
    return this.post<API.ApprovalPattern>(`/api/auto-approval/patterns/${id}/signoff`, payload);
  }

  async listAutoApprovalDecisions(params?: { patternId?: string; limit?: number }): Promise<API.AutoApprovalDecision[]> {
    if (shouldUseMockData()) return mockData.listAutoApprovalDecisions(params);
    return this.get<API.AutoApprovalDecision[]>('/api/auto-approval/decisions', params);
  }

  // ── Phase 2 — Deployer Profile (Art. 26/27) ─────────────────────────
  async getDeployerProfile(): Promise<API.DeployerProfile> {
    if (shouldUseMockData()) return mockData.getDeployerProfile();
    return this.get<API.DeployerProfile>('/api/deployer/profile');
  }

  async updateDeployerProfile(payload: Partial<API.DeployerProfile>): Promise<API.DeployerProfile> {
    if (shouldUseMockData()) return mockData.updateDeployerProfile(payload);
    return this.patch<API.DeployerProfile>('/api/deployer/profile', payload);
  }

  async ingestDeployerInstruction(payload: { title: string; body: string; source?: 'provider' | 'internal' }): Promise<API.DeployerInstruction> {
    if (shouldUseMockData()) return mockData.ingestDeployerInstruction(payload);
    return this.post<API.DeployerInstruction>('/api/deployer/instructions', payload);
  }

  async acknowledgeDeployerInstruction(id: string): Promise<API.DeployerInstruction> {
    if (shouldUseMockData()) return mockData.acknowledgeDeployerInstruction(id);
    return this.post<API.DeployerInstruction>(`/api/deployer/instructions/${id}/acknowledge`, {});
  }

  async linkFriaToDeployer(friaId: string): Promise<API.DeployerProfile> {
    if (shouldUseMockData()) return mockData.linkFriaToDeployer(friaId);
    return this.post<API.DeployerProfile>('/api/deployer/fria-links', { friaId });
  }

  // ── EU AI Act Art. 26(7)/(11) — disclosure-notice template ──────────
  async getDeployerDisclosureNotice(params: {
    type: 'affected_persons' | 'workers';
    locale?: 'de' | 'en';
    systemName?: string;
    purpose?: string;
  }): Promise<API.DeployerDisclosureNotice> {
    if (shouldUseMockData()) return mockData.getDeployerDisclosureNotice(params);
    return this.get<API.DeployerDisclosureNotice>('/api/deployer/disclosure-notice', params);
  }

  // ── Controller-Responsibility catalog (⚪ non-delegable duties) ──────
  async getControllerResponsibilities(framework?: string): Promise<API.ControllerResponsibilityStatus> {
    if (shouldUseMockData()) return mockData.getControllerResponsibilities(framework);
    return this.get<API.ControllerResponsibilityStatus>(
      '/api/controller-responsibilities',
      framework ? { framework } : undefined,
    );
  }

  async attestResponsibility(
    key: string,
    body?: API.AttestPayload,
  ): Promise<{ attestation: API.ControllerAttestationRecord }> {
    if (shouldUseMockData()) return mockData.attestResponsibility(key, body);
    return this.post<{ attestation: API.ControllerAttestationRecord }>(
      `/api/controller-responsibilities/${encodeURIComponent(key)}/attest`,
      body || {},
    );
  }

  async revokeResponsibility(key: string, entityId?: string | null): Promise<{ revoked: boolean; key: string }> {
    if (shouldUseMockData()) return mockData.revokeResponsibility(key);
    // entityId pinned via query param so the revoke can't race an entity switch.
    const q = entityId ? `?entity=${encodeURIComponent(entityId)}` : '';
    return this.delete<{ revoked: boolean; key: string }>(
      `/api/controller-responsibilities/${encodeURIComponent(key)}/attest${q}`,
    );
  }

  // Immutable attestation history for one obligation (audit trail).
  async getResponsibilityHistory(key: string): Promise<API.ControllerAttestationEvent[]> {
    if (shouldUseMockData()) return mockData.getResponsibilityHistory(key);
    const res = await this.get<{ history: API.ControllerAttestationEvent[] }>(
      `/api/controller-responsibilities/${encodeURIComponent(key)}/history`,
    );
    return res?.history ?? [];
  }

  // EU AI Act Art. 14 etc. — downloadable fill-in worksheet for an obligation.
  async getControllerResponsibilityTemplate(
    key: string,
    system?: string,
  ): Promise<API.ControllerResponsibilityTemplate> {
    if (shouldUseMockData()) return mockData.getControllerResponsibilityTemplate(key, system);
    return this.get<API.ControllerResponsibilityTemplate>(
      `/api/controller-responsibilities/${encodeURIComponent(key)}/template`,
      system ? { system } : undefined,
    );
  }

  // ── GwGMeldV / goAML-XML Verdachtsmeldung (Art. GwGMeldV / goAML-XML) ────────
  async getGoAmlProfile(): Promise<API.GoAmlProfileResponse> {
    if (shouldUseMockData()) return mockData.getGoAmlProfile();
    return this.get<API.GoAmlProfileResponse>('/api/goaml-profile');
  }

  async updateGoAmlProfile(payload: Partial<API.GoAmlReportingProfile>): Promise<API.GoAmlProfileResponse> {
    if (shouldUseMockData()) return mockData.updateGoAmlProfile(payload);
    return this.put<API.GoAmlProfileResponse>('/api/goaml-profile', payload);
  }

  // Generates the goAML-XML draft (JSON envelope incl. the xml string + completeness).
  // The raw-XML download (?format=xml) is produced client-side from result.xml.
  async getGoAmlExport(suspicionId: string, reportType?: 'VMA' | 'VMT'): Promise<API.GoAmlExportResult> {
    if (shouldUseMockData()) return mockData.getGoAmlExport(suspicionId, reportType);
    const res = await this.get<{ goaml: API.GoAmlExportResult }>(
      `/api/suspicion-cases/${encodeURIComponent(suspicionId)}/goaml-export`,
      reportType ? { reportType } : undefined,
    );
    return res.goaml;
  }

  // ── Phase 2 — FRIA Wizard ───────────────────────────────────────────
  async listFRIAs(): Promise<API.FRIA[]> {
    if (shouldUseMockData()) return mockData.listFRIAs();
    return this.get<API.FRIA[]>('/api/fria');
  }

  async getFRIA(id: string): Promise<API.FRIA> {
    if (shouldUseMockData()) return mockData.getFRIA(id);
    return this.get<API.FRIA>(`/api/fria/${id}`);
  }

  async createFRIA(payload: Partial<API.FRIA>): Promise<API.FRIA> {
    if (shouldUseMockData()) return mockData.createFRIA(payload);
    return this.post<API.FRIA>('/api/fria', payload);
  }

  async updateFRIA(id: string, payload: Partial<API.FRIA>): Promise<API.FRIA> {
    if (shouldUseMockData()) return mockData.updateFRIA(id, payload);
    return this.patch<API.FRIA>(`/api/fria/${id}`, payload);
  }

  async submitFRIAForReview(id: string): Promise<API.FRIA> {
    if (shouldUseMockData()) return mockData.submitFRIAForReview(id);
    return this.post<API.FRIA>(`/api/fria/${id}/submit`, {});
  }

  // Generic FRIA lifecycle transition (e.g. review -> draft = Zurueckweisung).
  async transitionFRIA(id: string, status: string): Promise<API.FRIA> {
    if (shouldUseMockData()) return mockData.getFRIA(id);
    return this.post<API.FRIA>(`/api/fria/${id}/transition`, { status });
  }

  async approveFRIA(id: string): Promise<API.FRIA> {
    if (shouldUseMockData()) return mockData.approveFRIA(id);
    return this.post<API.FRIA>(`/api/fria/${id}/approve`, {});
  }

  // EU AI Act Art. 27 — pre-filled insurance FRIA template (Annex III §5(b)/(c)).
  async getInsuranceFriaTemplate(useCase?: string): Promise<API.InsuranceFriaTemplate> {
    if (shouldUseMockData()) return mockData.getInsuranceFriaTemplate(useCase);
    const qs = useCase ? `?useCase=${encodeURIComponent(useCase)}` : '';
    return this.get<API.InsuranceFriaTemplate>(`/api/fria/insurance-template${qs}`);
  }

  // EU AI Act Art. 27 — signed/tamper-evident export (JSON envelope: signed
  // artifact + ES256 JWS signature, or fail-open SHA-256 digest).
  async exportFRIA(id: string): Promise<API.FRIAExportResult> {
    if (shouldUseMockData()) return mockData.exportFRIA(id);
    return this.post<API.FRIAExportResult>(`/api/fria/${id}/export`, {});
  }

  // EU AI Act Art. 27 — signed PDF export. Binary; bypasses the JSON envelope
  // and streams a Blob via the shared axios instance (the auth interceptor
  // attaches the bearer token). The export route is POST, so downloadFile
  // (GET-only) is not usable here.
  async exportFRIAPdf(id: string): Promise<Blob> {
    if (shouldUseMockData()) {
      return new Blob([`%PDF-1.4\n% Adjudon FRIA mock export ${id}\n`], { type: 'application/pdf' });
    }
    const res = await apiClient.post(`/api/fria/${id}/export`, {}, {
      responseType: 'blob',
      params: { format: 'pdf' },
    });
    return res.data as Blob;
  }

  // EU AI Act Art. 11/72/53 — provider technical-documentation pack as JSON
  // (manifest: documentType, generatedAt, sections, attachments) for inline preview.
  async getTechDocPack(kind: 'annex-iv' | 'pmm' | 'gpai', agentId: string): Promise<Record<string, unknown> | null> {
    if (shouldUseMockData()) {
      // Persona: Annex-IV-artiges Manifest je Paket-Typ; generatedAt = jüngst.
      // Sektionen folgen der jeweiligen Rechtsgrundlage (Art. 11 / 72 / 53).
      const generatedAt = new Date(Date.now() - 2 * 86400000).toISOString();
      const PACK: Record<string, { documentType: string; sections: string[]; attachments: string[] }> = {
        'annex-iv': {
          documentType: 'Annex IV — Technische Dokumentation (Art. 11)',
          sections: [
            '1. Allgemeine Beschreibung des KI-Systems',
            '2. Detaillierte Beschreibung der Elemente & Entwicklung',
            '3. Überwachung, Funktionsweise & Kontrolle',
            '4. Leistungskennzahlen (Genauigkeit, Robustheit, Cybersicherheit)',
            '5. Risikomanagementsystem (Art. 9)',
            '6. Änderungen über den Lebenszyklus',
            '7. Liste angewandter harmonisierter Normen',
            '8. EU-Konformitätserklärung (Verweis)',
            '9. Plan zur Beobachtung nach Inverkehrbringen (Art. 72)',
          ],
          attachments: ['ml-bom.cyclonedx.json', 'art13-ifu.md', 'pmm-plan.md'],
        },
        'pmm': {
          documentType: 'Post-Market-Monitoring-Plan (Art. 72)',
          sections: [
            '1. Überwachungsziele & Kennzahlen',
            '2. Datenquellen (laufende Decision-Traces)',
            '3. Drift- & Kalibrierungs-Überwachung',
            '4. Schwellenwerte & Eskalationspfade',
            '5. Korrekturmaßnahmen (Art. 72(3))',
          ],
          attachments: ['pmm-plan.md', 'kalibrierungs-trend.json'],
        },
        'gpai': {
          documentType: 'GPAI-Anbieter-Paket (Art. 53/54/55)',
          sections: [
            '1. Modell-Beschreibung & Trainingsprozess',
            '2. Urheberrechts-Policy (Art. 53(1)(c))',
            '3. Zusammenfassung der Trainingsdaten (Art. 53(1)(d))',
            '4. Systemisches Risiko (Art. 55, falls einschlägig)',
          ],
          attachments: ['ml-bom.cyclonedx.json', 'training-data-summary.md'],
        },
      };
      const m = PACK[kind] ?? PACK['annex-iv'];
      return { manifest: { documentType: m.documentType, generatedAt, sections: m.sections, attachments: m.attachments } };
    }
    return this.get<Record<string, unknown> | null>(`/api/v1/provider/tech-doc/${kind}/${encodeURIComponent(agentId)}`);
  }

  // EU AI Act Art. 11/72/53 — provider technical-documentation packs as Markdown
  // (per KI-System). annex-iv = Annex IV tech-doc, pmm = post-market plan,
  // gpai = GPAI provider pack (enterprise-gated; 403 if not entitled).
  async downloadTechDocMarkdown(kind: 'annex-iv' | 'pmm' | 'gpai', agentId: string): Promise<string> {
    if (shouldUseMockData()) {
      const title: Record<string, string> = {
        'annex-iv': 'Annex IV — Technische Dokumentation (Art. 11)',
        'pmm': 'Post-Market-Monitoring-Plan (Art. 72)',
        'gpai': 'GPAI-Anbieter-Paket (Art. 53)',
      };
      return [
        `# ${title[kind] ?? kind}`,
        '',
        `KI-System: ${agentId}`,
        `Stand: ${new Date().toLocaleDateString('de-DE')}`,
        '',
        '> On-demand erzeugt. Kein Konformitätsnachweis — Konformität entsteht durch Ihren Betrieb (§5 UWG).',
        '',
      ].join('\n');
    }
    const res = await apiClient.get(`/api/v1/provider/tech-doc/${kind}/${encodeURIComponent(agentId)}`, {
      params: { format: 'markdown' },
      responseType: 'text',
    });
    return res.data as string;
  }

  // EU AI Act Art. 72(3) — post-market corrective actions.
  async getCorrectiveActions(status?: string): Promise<API.CorrectiveAction[]> {
    if (shouldUseMockData()) return mockData.getCorrectiveActions();
    const res = await this.get<{ correctiveActions: API.CorrectiveAction[] } | null>('/api/v1/corrective-actions', status ? { status } : undefined);
    return res?.correctiveActions ?? [];
  }
  async createCorrectiveAction(payload: { title: string; source?: string; description?: string; agentId?: string; dueDate?: string }): Promise<API.CorrectiveAction> {
    if (shouldUseMockData()) return mockData.createCorrectiveAction(payload);
    const res = await this.post<{ correctiveAction: API.CorrectiveAction }>('/api/v1/corrective-actions', payload);
    return res.correctiveAction;
  }
  async updateCorrectiveAction(id: string, payload: { status?: string }): Promise<API.CorrectiveAction> {
    if (shouldUseMockData()) return mockData.updateCorrectiveAction(id, payload);
    const res = await this.patch<{ correctiveAction: API.CorrectiveAction }>(`/api/v1/corrective-actions/${encodeURIComponent(id)}`, payload);
    return res.correctiveAction;
  }

  // ── VVG §61/§62 — Advisory Records (Insurance Documentation) ─────────
  async listAdvisories(params?: { status?: string; search?: string; customerRef?: string; limit?: number }): Promise<API.AdvisoryListResponse> {
    if (shouldUseMockData()) return mockData.listAdvisories(params);
    return this.get<API.AdvisoryListResponse>('/api/advisory', params);
  }

  async getAdvisory(id: string): Promise<API.AdvisoryDetailResponse> {
    if (shouldUseMockData()) return mockData.getAdvisory(id);
    return this.get<API.AdvisoryDetailResponse>(`/api/advisory/${id}`);
  }

  async createAdvisory(payload: Partial<API.AdvisoryRecord>): Promise<API.AdvisoryRecord> {
    if (shouldUseMockData()) return mockData.createAdvisory(payload);
    return this.post<API.AdvisoryRecord>('/api/advisory', payload);
  }

  async updateAdvisory(id: string, payload: Partial<API.AdvisoryRecord>): Promise<API.AdvisoryRecord> {
    if (shouldUseMockData()) return mockData.updateAdvisory(id, payload);
    return this.patch<API.AdvisoryRecord>(`/api/advisory/${id}`, payload);
  }

  async finalizeAdvisory(id: string, payload?: { advisedAt?: string }): Promise<API.AdvisoryRecord> {
    if (shouldUseMockData()) return mockData.finalizeAdvisory(id, payload);
    return this.post<API.AdvisoryRecord>(`/api/advisory/${id}/finalize`, payload || {});
  }

  async acknowledgeAdvisory(id: string, payload: { method: API.AdvisoryAcknowledgmentMethod; acknowledgedByRef: string; statement: string }): Promise<API.AdvisoryRecord> {
    if (shouldUseMockData()) return mockData.acknowledgeAdvisory(id, payload);
    return this.post<API.AdvisoryRecord>(`/api/advisory/${id}/acknowledge`, payload);
  }

  async archiveAdvisory(id: string): Promise<API.AdvisoryRecord> {
    if (shouldUseMockData()) return mockData.archiveAdvisory(id);
    return this.post<API.AdvisoryRecord>(`/api/advisory/${id}/archive`, {});
  }

  async getAdvisoryProtocol(id: string): Promise<API.AdvisoryProtocolResponse> {
    if (shouldUseMockData()) return mockData.getAdvisoryProtocol(id);
    return this.get<API.AdvisoryProtocolResponse>(`/api/advisory/${id}/protocol`);
  }

  // §62 Textform PDF — the customer-facing downloadable artifact. Binary;
  // bypasses the JSON envelope and streams a Blob via the shared axios
  // instance (the auth interceptor attaches the bearer token).
  async exportAdvisoryProtocolPdf(id: string): Promise<Blob> {
    if (shouldUseMockData()) {
      return new Blob([`%PDF-1.4\n% Adjudon Beratungsprotokoll (§62) mock ${id}\n`], { type: 'application/pdf' });
    }
    const res = await apiClient.get(`/api/advisory/${id}/protocol`, {
      responseType: 'blob',
      params: { format: 'pdf' },
    });
    return res.data as Blob;
  }

  async deleteAdvisory(id: string): Promise<{ deleted: boolean }> {
    if (shouldUseMockData()) return mockData.deleteAdvisory(id);
    return this.delete<{ deleted: boolean }>(`/api/advisory/${id}`);
  }

  // ── Phase 3 — Federated Learning ────────────────────────────────────
  async listPatternFamilies(): Promise<API.PatternFamily[]> {
    if (shouldUseMockData()) return mockData.listPatternFamilies();
    return this.get<API.PatternFamily[]>('/api/federated/families');
  }

  async getPatternFamily(id: string): Promise<API.PatternFamily> {
    if (shouldUseMockData()) return mockData.getPatternFamily(id);
    return this.get<API.PatternFamily>(`/api/federated/families/${id}`);
  }

  async joinPatternFamily(id: string): Promise<API.PatternFamily> {
    if (shouldUseMockData()) return mockData.joinPatternFamily(id);
    return this.post<API.PatternFamily>(`/api/federated/families/${id}/join`, {});
  }

  async leavePatternFamily(id: string): Promise<API.PatternFamily> {
    if (shouldUseMockData()) return mockData.leavePatternFamily(id);
    return this.post<API.PatternFamily>(`/api/federated/families/${id}/leave`, {});
  }

  async listFederatedRounds(familyId: string): Promise<API.FederatedRound[]> {
    if (shouldUseMockData()) return mockData.listFederatedRounds(familyId);
    return this.get<API.FederatedRound[]>(`/api/federated/families/${familyId}/rounds`);
  }

  async kickoffFederatedRound(familyId: string, payload: { epsilon?: number; delta?: number }): Promise<API.FederatedRound> {
    if (shouldUseMockData()) return mockData.kickoffFederatedRound(familyId, payload);
    return this.post<API.FederatedRound>(`/api/federated/families/${familyId}/rounds`, payload);
  }

  async getFederatedRound(roundId: string): Promise<API.FederatedRound> {
    if (shouldUseMockData()) return mockData.getFederatedRound(roundId);
    return this.get<API.FederatedRound>(`/api/federated/rounds/${roundId}`);
  }

  async submitFederatedClientUpdate(
    roundId: string,
    payload: API.FederatedClientUpdateInput,
  ): Promise<API.FederatedClientUpdate> {
    if (shouldUseMockData()) return mockData.submitFederatedClientUpdate(roundId, payload);
    return this.post<API.FederatedClientUpdate>(`/api/federated/rounds/${roundId}/updates`, payload);
  }

  async runFederatedAggregation(roundId: string): Promise<API.FederatedRound> {
    if (shouldUseMockData()) return mockData.runFederatedAggregation(roundId);
    return this.post<API.FederatedRound>(`/api/federated/rounds/${roundId}/aggregate`, {});
  }

  async signFederatedRoundEvidence(roundId: string): Promise<API.FederatedRound> {
    if (shouldUseMockData()) return mockData.signFederatedRoundEvidence(roundId);
    return this.post<API.FederatedRound>(`/api/federated/rounds/${roundId}/evidence`, {});
  }

  // ── Phase 3 — Privacy Budget ────────────────────────────────────────
  async getPrivacyBudget(): Promise<API.PrivacyBudget> {
    if (shouldUseMockData()) return mockData.getPrivacyBudget();
    return this.get<API.PrivacyBudget>('/api/privacy-budget');
  }

  async updatePrivacyBudgetCaps(payload: { epsilonCap?: number; deltaCap?: number; windowDurationMs?: number }): Promise<API.PrivacyBudget> {
    if (shouldUseMockData()) return mockData.updatePrivacyBudgetCaps(payload);
    return this.patch<API.PrivacyBudget>('/api/privacy-budget/caps', payload);
  }

  // ── Phase 3 — MCP Server ────────────────────────────────────────────
  // Backend mounts MCP routes at /api/mcp-server (server.js:386-387),
  // not /api/mcp. The earlier /api/mcp/* URLs hit 404 on every request,
  // which is why the MCP Server page never loaded.
  async getMCPManifest(): Promise<API.MCPManifest> {
    if (shouldUseMockData()) return mockData.getMCPManifest();
    return this.get<API.MCPManifest>('/api/mcp-server/manifest');
  }

  async readMCPResource(uri: string): Promise<{ uri: string; mimeType: string; text: string }> {
    if (shouldUseMockData()) return mockData.readMCPResource(uri);
    return this.post('/api/mcp-server/resources/read', { uri });
  }

  async callMCPTool(name: string, args: Record<string, any>): Promise<any> {
    if (shouldUseMockData()) return mockData.callMCPTool(name, args);
    return this.post('/api/mcp-server/tools/call', { name, arguments: args });
  }

  // ── Phase 2b — Provenance (C2PA + PROV-O) ───────────────────────────
  async getProvenanceSettings(): Promise<API.ProvenanceSettings> {
    if (shouldUseMockData()) return mockData.getProvenanceSettings();
    return this.get<API.ProvenanceSettings>('/api/provenance/settings');
  }

  async updateProvenanceSettings(payload: { c2paEnabled: boolean }): Promise<API.ProvenanceSettings> {
    if (shouldUseMockData()) return mockData.updateProvenanceSettings(payload);
    return this.put<API.ProvenanceSettings>('/api/provenance/settings', payload);
  }

  async listC2PAManifests(params?: { limit?: number; traceId?: string }): Promise<{ manifests: API.C2PAManifest[]; count: number }> {
    if (shouldUseMockData()) return mockData.listC2PAManifests(params);
    return this.get<{ manifests: API.C2PAManifest[]; count: number }>('/api/provenance/c2pa/manifests', params);
  }

  async issueC2PAManifest(payload: {
    traceId: string;
    contentHash: string;
    label?: string;
    issuer?: string;
    assertions?: Record<string, any>;
  }): Promise<{ manifest: API.C2PAManifest }> {
    if (shouldUseMockData()) return mockData.issueC2PAManifest(payload);
    return this.post<{ manifest: API.C2PAManifest }>('/api/provenance/c2pa/manifests', payload);
  }

  async revokeC2PAManifest(id: string, reason?: string): Promise<{ manifest: API.C2PAManifest }> {
    if (shouldUseMockData()) return mockData.revokeC2PAManifest(id, reason);
    return this.post<{ manifest: API.C2PAManifest }>(`/api/provenance/c2pa/manifests/${id}/revoke`, { reason });
  }

  async verifyC2PAManifest(id: string): Promise<API.C2PAVerifyResult> {
    if (shouldUseMockData()) return mockData.verifyC2PAManifest(id);
    return this.get<API.C2PAVerifyResult>(`/api/provenance/c2pa/manifests/${id}/verify`);
  }

  async getProvOTrace(traceId: string): Promise<{ provo: API.ProvODocument }> {
    if (shouldUseMockData()) return mockData.getProvOTrace(traceId);
    return this.get<{ provo: API.ProvODocument }>(`/api/provenance/provo/trace/${encodeURIComponent(traceId)}`);
  }

  // ── C2PA KMS — admin signing-key management ───────────────────────────
  async getC2PAKmsStatus(): Promise<API.C2PAKmsStatus> {
    if (shouldUseMockData()) return mockData.getC2PAKmsStatus();
    return this.get<API.C2PAKmsStatus>('/api/c2pa-kms/status');
  }

  async rotateC2PAKmsKey(): Promise<API.C2PAKmsRotateResult> {
    if (shouldUseMockData()) return mockData.rotateC2PAKmsKey();
    return this.post<API.C2PAKmsRotateResult>('/api/c2pa-kms/rotate', {});
  }

  // ============================================================================
  // AUDIT TRAIL — Compliance section endpoints (migrated from raw apiClient
  // calls to the typed wrapper so React Query hooks can wrap them uniformly).
  // ============================================================================

  async getWitnessConsortiumStatus(): Promise<{ consortium: API.WitnessConsortiumStatus }> {
    if (shouldUseMockData()) {
      // No real customer-visible data when demo mode is on; return an
      // empty consortium so the page renders the empty-state path.
      return {
        consortium: {
          activeWitnesses: 0,
          witnesses: [],
          totalObservations: 0,
          totalMismatches: 0,
          recentMismatches: [],
        },
      };
    }
    return this.get<{ consortium: API.WitnessConsortiumStatus }>(
      '/api/audit-witness/gossip/consortium'
    );
  }

  async listInsuranceFeeds(): Promise<{ items: API.InsuranceFeedRecord[] }> {
    if (shouldUseMockData()) {
      // Six months of demo feeds covering all three signature-mode tiers
      // and one ANOMALIES month so the alert/UX paths actually render.
      // Ordering matches the production response: newest first.
      //
      // Per-month design intent:
      //   Apr 2026 — d-trust-qseal, clean (the "ready for underwriter" state)
      //   Mar 2026 — d-trust-qseal, clean
      //   Feb 2026 — d-trust-qseal, clean
      //   Jan 2026 — cosign-keyless (Sigstore fallback — when D-Trust temporarily unavailable)
      //   Dec 2025 — d-trust-qseal, ANOMALIES (high-severity drift event)
      //   Nov 2025 — hmac-placeholder (commercial-procurement-gate pre-activation)
      const mkChainIntegrity = (
        opts: {
          heartbeatPassRate: number;
          anomaliesDetected: number;
          anomaliesHighSeverity: number;
          qsealSignaturesIssued: number;
          qtspTimestampsReceived: number;
          witnessConsortiumSize: number;
          witnessMismatches: number;
          decisionEntries: number;       // per-month Decision-Chain-Einträge — passt zu coverageTriggers.totalDecisionEvents
        }
      ): API.InsuranceFeedRecord['chainIntegrity'] => {
        // Deterministisch (reload-stabil): Decision-Chain-Einträge = die für den
        // Monat gemeldeten Entscheidungs-Events; Operations-Chain ~27 % davon.
        const decisionChainEntries = opts.decisionEntries;
        const operationsChainEntries = Math.round(opts.decisionEntries * 0.27);
        const heartbeatPassed = 2_880;
        const heartbeatFailed = Math.round(heartbeatPassed * (1 - opts.heartbeatPassRate));
        return {
          decisionChainEntries,
          operationsChainEntries,
          dailyAnchorsProduced: 30,
          otsProofsSubmitted: 30,
          otsProofsUpgraded: 28,
          qtspTimestampsReceived: opts.qtspTimestampsReceived,
          qsealSignaturesIssued: opts.qsealSignaturesIssued,
          heartbeatVerificationsPassed: heartbeatPassed,
          heartbeatVerificationsFailed: heartbeatFailed,
          heartbeatPassRate: opts.heartbeatPassRate,
          witnessConsortiumSize: opts.witnessConsortiumSize,
          witnessMismatches: opts.witnessMismatches,
          anomaliesDetected: opts.anomaliesDetected,
          anomaliesHighSeverity: opts.anomaliesHighSeverity,
        };
      };

      const mkCoverageTriggers = (
        decisionEvents: number,
        hallucinations: number,
        drift: number,
        mechanical: number,
        slaBreaches: number,
      ): NonNullable<API.InsuranceFeedRecord['coverageTriggers']> => ({
        totalDecisionEvents: decisionEvents,
        hallucinationEvents: hallucinations,
        modelDriftEvents: drift,
        mechanicalFailureEvents: mechanical,
        slaBreachEvents: slaBreaches,
      });

      // Deterministic 64-char hash from a seed — reload-stable.
      const mkHash = (seed: string): string => {
        let h = 0x811c9dc5;
        for (let i = 0; i < seed.length; i += 1) {
          h = Math.imul(h ^ seed.charCodeAt(i), 0x01000193) >>> 0;
        }
        const hex8 = (h.toString(16) + '00000000').slice(0, 8);
        return hex8.repeat(8).slice(0, 64);
      };

      const items: API.InsuranceFeedRecord[] = [
        // ─── April 2026 — clean qSeal — current "ready" state ──────────
        {
          _id: 'feed_demo_2026_04',
          organizationId: 'org_demo',
          periodStart: '2026-04-01T00:00:00.000Z',
          periodEnd: '2026-04-30T23:59:59.999Z',
          chainIntegrity: mkChainIntegrity({
            heartbeatPassRate: 0.9997,
            anomaliesDetected: 0,
            anomaliesHighSeverity: 0,
            qsealSignaturesIssued: 30,
            qtspTimestampsReceived: 30,
            witnessConsortiumSize: 4,
            witnessMismatches: 0,
            decisionEntries: 14_412,
          }),
          feedPayloadHash: mkHash('feed-apr-2026'),
          signatureMode: 'd-trust-qseal',
          methodologyVersion: 'v2026.04',
          generatedAt: '2026-05-05T00:05:00.000Z',
          coverageTriggers: mkCoverageTriggers(14_412, 11, 0, 0, 3),
          witnessAcknowledgments: 4,
          underwriterAcknowledgments: [
            {
              partner: 'Munich Re aiSure (Discovery)',
              acknowledgedAt: '2026-05-06T09:14:00.000Z',
              ackHash: mkHash('ack-munich-apr-2026'),
            },
          ],
          linkedIncidents: [],
          linkedErasureCount: 0,
          bundleSizeBytes: 1_842_113,
          partnerEndpointDelivered: true,
        },
        // ─── March 2026 — clean qSeal ──────────────────────────────────
        {
          _id: 'feed_demo_2026_03',
          organizationId: 'org_demo',
          periodStart: '2026-03-01T00:00:00.000Z',
          periodEnd: '2026-03-31T23:59:59.999Z',
          chainIntegrity: mkChainIntegrity({
            heartbeatPassRate: 0.9994,
            anomaliesDetected: 0,
            anomaliesHighSeverity: 0,
            qsealSignaturesIssued: 31,
            qtspTimestampsReceived: 31,
            witnessConsortiumSize: 4,
            witnessMismatches: 0,
            decisionEntries: 13_904,
          }),
          feedPayloadHash: mkHash('feed-mar-2026'),
          signatureMode: 'd-trust-qseal',
          methodologyVersion: 'v2026.04',
          generatedAt: '2026-04-05T00:05:00.000Z',
          coverageTriggers: mkCoverageTriggers(13_904, 7, 1, 0, 2),
          witnessAcknowledgments: 4,
          underwriterAcknowledgments: [
            {
              partner: 'Munich Re aiSure (Discovery)',
              acknowledgedAt: '2026-04-06T08:22:00.000Z',
              ackHash: mkHash('ack-munich-mar-2026'),
            },
          ],
          linkedIncidents: [],
          linkedErasureCount: 1,
          bundleSizeBytes: 1_802_487,
          partnerEndpointDelivered: true,
        },
        // ─── February 2026 — clean qSeal ───────────────────────────────
        {
          _id: 'feed_demo_2026_02',
          organizationId: 'org_demo',
          periodStart: '2026-02-01T00:00:00.000Z',
          periodEnd: '2026-02-28T23:59:59.999Z',
          chainIntegrity: mkChainIntegrity({
            heartbeatPassRate: 0.9991,
            anomaliesDetected: 0,
            anomaliesHighSeverity: 0,
            qsealSignaturesIssued: 28,
            qtspTimestampsReceived: 28,
            witnessConsortiumSize: 4,
            witnessMismatches: 0,
            decisionEntries: 12_804,
          }),
          feedPayloadHash: mkHash('feed-feb-2026'),
          signatureMode: 'd-trust-qseal',
          methodologyVersion: 'v2026.04',
          generatedAt: '2026-03-05T00:05:00.000Z',
          coverageTriggers: mkCoverageTriggers(12_804, 14, 0, 0, 4),
          witnessAcknowledgments: 4,
          underwriterAcknowledgments: [],
          linkedIncidents: [],
          linkedErasureCount: 0,
          bundleSizeBytes: 1_741_032,
          partnerEndpointDelivered: true,
        },
        // ─── January 2026 — Sigstore fallback (D-Trust briefly unavailable) ─
        {
          _id: 'feed_demo_2026_01',
          organizationId: 'org_demo',
          periodStart: '2026-01-01T00:00:00.000Z',
          periodEnd: '2026-01-31T23:59:59.999Z',
          chainIntegrity: mkChainIntegrity({
            heartbeatPassRate: 0.9988,
            anomaliesDetected: 0,
            anomaliesHighSeverity: 0,
            qsealSignaturesIssued: 0,
            qtspTimestampsReceived: 0,
            witnessConsortiumSize: 4,
            witnessMismatches: 0,
            decisionEntries: 13_211,
          }),
          feedPayloadHash: mkHash('feed-jan-2026'),
          signatureMode: 'cosign-keyless',
          methodologyVersion: 'v2026.01',
          generatedAt: '2026-02-05T00:05:00.000Z',
          coverageTriggers: mkCoverageTriggers(13_211, 9, 0, 1, 5),
          witnessAcknowledgments: 3,
          underwriterAcknowledgments: [],
          linkedIncidents: [],
          linkedErasureCount: 2,
          bundleSizeBytes: 1_698_220,
          partnerEndpointDelivered: false,
        },
        // ─── December 2025 — qSeal but ANOMALIES (drift detected) ──────
        {
          _id: 'feed_demo_2025_12',
          organizationId: 'org_demo',
          periodStart: '2025-12-01T00:00:00.000Z',
          periodEnd: '2025-12-31T23:59:59.999Z',
          chainIntegrity: mkChainIntegrity({
            heartbeatPassRate: 0.9942,
            anomaliesDetected: 3,
            anomaliesHighSeverity: 1,
            qsealSignaturesIssued: 31,
            qtspTimestampsReceived: 31,
            witnessConsortiumSize: 4,
            witnessMismatches: 0,
            decisionEntries: 11_807,
          }),
          feedPayloadHash: mkHash('feed-dec-2025'),
          signatureMode: 'd-trust-qseal',
          methodologyVersion: 'v2026.01',
          generatedAt: '2026-01-05T00:05:00.000Z',
          coverageTriggers: mkCoverageTriggers(11_807, 22, 4, 0, 9),
          witnessAcknowledgments: 4,
          underwriterAcknowledgments: [
            {
              partner: 'Munich Re aiSure (Discovery)',
              acknowledgedAt: '2026-01-08T11:42:00.000Z',
              ackHash: mkHash('ack-munich-dec-2025'),
            },
          ],
          linkedIncidents: [
            {
              incidentId: 'inc_2025_12_14',
              severity: 'high',
              occurredAt: '2025-12-14T03:21:00.000Z',
              summary: 'Confidence-score drift > 5σ over rolling 7-day window — Art-72 post-market monitoring notice filed; provider notified per Art-73.',
            },
            {
              incidentId: 'inc_2025_12_22',
              severity: 'medium',
              occurredAt: '2025-12-22T17:08:00.000Z',
              summary: 'Heartbeat verification triple-failure during 17:00-18:00 UTC; chain integrity preserved via on-call rollback.',
            },
          ],
          linkedErasureCount: 1,
          bundleSizeBytes: 1_577_882,
          partnerEndpointDelivered: true,
        },
        // ─── November 2025 — pre-qSeal placeholder ─────────────────────
        {
          _id: 'feed_demo_2025_11',
          organizationId: 'org_demo',
          periodStart: '2025-11-01T00:00:00.000Z',
          periodEnd: '2025-11-30T23:59:59.999Z',
          chainIntegrity: mkChainIntegrity({
            heartbeatPassRate: 0.9982,
            anomaliesDetected: 0,
            anomaliesHighSeverity: 0,
            qsealSignaturesIssued: 0,
            qtspTimestampsReceived: 0,
            witnessConsortiumSize: 3,
            witnessMismatches: 0,
            decisionEntries: 10_412,
          }),
          feedPayloadHash: mkHash('feed-nov-2025'),
          signatureMode: 'hmac-placeholder',
          methodologyVersion: 'v2025.11',
          generatedAt: '2025-12-05T00:05:00.000Z',
          coverageTriggers: mkCoverageTriggers(10_412, 5, 0, 0, 3),
          witnessAcknowledgments: 2,
          underwriterAcknowledgments: [],
          linkedIncidents: [],
          linkedErasureCount: 0,
          bundleSizeBytes: 1_421_006,
          partnerEndpointDelivered: false,
        },
      ];
      return { items };
    }
    return this.get<{ items: API.InsuranceFeedRecord[] }>('/api/insurance-feed');
  }

  async listNotaryAttestations(): Promise<{ items: API.NotaryAttestation[] }> {
    if (shouldUseMockData()) {
      // Three months of demo attestations: April + March verified, February
      // ANOMALIES so the badge-tone differentiation is visible. All in
      // `placeholder` signatureMode so the amber qSeal-pending disclosure
      // row renders. periodEnd is the last-second of the month so the
      // formatted "→ end-of-month" subtitle reads correctly.
      const monthlyEntries = (verified: boolean, anomalies: number) => ({
        decisionChainEntries: 12_847 - (verified ? 0 : 412),
        operationsChainEntries: 3_421 - (verified ? 0 : 87),
        dailyAnchorsProduced: 30,
        otsProofsSubmitted: 30,
        otsProofsUpgraded: 28,
        qtspTimestampsReceived: 0,
        heartbeatVerificationsPassed: 2_880,
        heartbeatVerificationsFailed: verified ? 0 : 3,
        witnessSthsObserved: 0,
        anomaliesDetected: anomalies,
      });
      return {
        items: [
          {
            _id: 'att_demo_2026_04',
            organizationId: 'org_demo',
            periodStart: '2026-04-01T00:00:00.000Z',
            periodEnd: '2026-04-30T23:59:59.999Z',
            cadence: 'monthly',
            stats: monthlyEntries(true, 0),
            anomalies: [],
            verified: true,
            pdfHash: 'a3b9c8d1e4f7029b' + '0'.repeat(48),
            pdfByteSize: 2_543_812,
            signatureMode: 'placeholder',
            methodologyVersion: 'v1.0',
            generatedAt: '2026-05-01T00:05:00.000Z',
          },
          {
            _id: 'att_demo_2026_03',
            organizationId: 'org_demo',
            periodStart: '2026-03-01T00:00:00.000Z',
            periodEnd: '2026-03-31T23:59:59.999Z',
            cadence: 'monthly',
            stats: monthlyEntries(true, 0),
            anomalies: [],
            verified: true,
            pdfHash: '7f2e1c5a9d3b8401' + '0'.repeat(48),
            pdfByteSize: 2_412_096,
            signatureMode: 'placeholder',
            methodologyVersion: 'v1.0',
            generatedAt: '2026-04-01T00:05:00.000Z',
          },
          {
            _id: 'att_demo_2026_02',
            organizationId: 'org_demo',
            periodStart: '2026-02-01T00:00:00.000Z',
            periodEnd: '2026-02-28T23:59:59.999Z',
            cadence: 'monthly',
            stats: monthlyEntries(false, 2),
            anomalies: [
              {
                occurredAt: '2026-02-14T03:21:00.000Z',
                type: 'heartbeat-verification-failed',
                scope: 'decisionChain',
                details: 'Three consecutive heartbeat verifications failed during the 03:00-04:00 UTC anchor window; the daily anchor recovered on the next run.',
                resolvedAt: '2026-02-14T04:05:00.000Z',
              },
              {
                occurredAt: '2026-02-22T11:47:00.000Z',
                type: 'sequence-gap-detected',
                scope: 'operationsChain',
                details: 'A single-entry sequence gap (412 entries skipped due to a transient idempotency lock); chain integrity preserved via the on-call rollback procedure.',
                resolvedAt: '2026-02-22T12:30:00.000Z',
              },
            ],
            verified: false,
            pdfHash: 'c1d4a7b2e5f80936' + '0'.repeat(48),
            pdfByteSize: 2_678_234,
            signatureMode: 'placeholder',
            methodologyVersion: 'v1.0',
            generatedAt: '2026-03-01T00:05:00.000Z',
          },
        ],
      };
    }
    return this.get<{ items: API.NotaryAttestation[] }>('/api/notary-attestation');
  }

  async getAuditorView(): Promise<API.AuditorViewData> {
    if (shouldUseMockData()) {
      // Demo data — realistic policy-engine audit surface for prospect
      // walkthroughs. PwC + BaFin examiners landing on this view should
      // see a credible distribution: live policies, immutable versions,
      // recent transcripts (mostly approve, some block, one flag), an
      // approval workflow in mid-state, effectiveness snapshots with
      // realistic precision/recall figures.
      const nowIso = new Date().toISOString();
      const tIso = (offsetMin: number) => new Date(Date.now() - offsetMin * 60_000).toISOString();
      const randHash = () => 'sha256:' + Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
      return {
        generatedAt: nowIso,
        policyVersionCount: 47,
        policies: [
          // lifecycleState values match the real PolicyVersion enum:
          // draft / shadow / canary / live / retired (per backend/models/PolicyVersion.js).
          // The auditorView controller now derives this from the latest
          // non-retired PolicyVersion per policy.
          { id: 'pol_demo_kyc', name: 'KYC — DACH retail onboarding', lifecycleState: 'live', createdAt: '2026-01-15T09:00:00Z', updatedAt: '2026-04-22T14:30:00Z' },
          { id: 'pol_demo_aml', name: 'AML — high-risk jurisdiction screen', lifecycleState: 'live', createdAt: '2026-02-03T11:20:00Z', updatedAt: '2026-05-08T08:15:00Z' },
          { id: 'pol_demo_credit', name: 'Credit decisioning — consumer < €25k', lifecycleState: 'canary', createdAt: '2026-03-10T13:45:00Z', updatedAt: '2026-05-19T16:42:00Z' },
          { id: 'pol_demo_sanctions', name: 'Sanctions list — EU + OFAC + UK', lifecycleState: 'live', createdAt: '2025-11-01T09:00:00Z', updatedAt: '2026-04-30T10:00:00Z' },
          { id: 'pol_demo_pii', name: 'PII residency — EU only', lifecycleState: 'shadow', createdAt: '2026-05-12T15:30:00Z', updatedAt: '2026-05-18T11:20:00Z' },
        ],
        recentTranscripts: Array.from({ length: 50 }, (_, i) => {
          const decisions = ['approve', 'approve', 'approve', 'approve', 'approve', 'flag_for_review', 'flag_for_review', 'block', 'approve', 'approve'];
          const policies = ['pol_demo_kyc', 'pol_demo_aml', 'pol_demo_credit', 'pol_demo_sanctions'];
          return {
            _id: `tr_demo_${String(i).padStart(4, '0')}`,
            traceId: `trace_${(Date.now() - i * 1000).toString(36)}${i.toString(36).padStart(4, '0')}`,
            policyId: policies[i % policies.length],
            policyVersionHash: randHash(),
            decision: decisions[i % decisions.length],
            viaBreakGlass: i === 7 || i === 23,
            timestamp: tIso(i * 23 + Math.floor(Math.random() * 17)),
          };
        }),
        recentApprovals: [
          { _id: 'ar_demo_1', policyId: 'pol_demo_credit', policyVersionHash: randHash(), workflowState: 'awaiting_enforcer', targetLifecycleState: 'published', viaBreakGlass: false, createdAt: tIso(180), completedAt: null },
          { _id: 'ar_demo_2', policyId: 'pol_demo_aml', policyVersionHash: randHash(), workflowState: 'approved', targetLifecycleState: 'published', viaBreakGlass: false, createdAt: tIso(1440), completedAt: tIso(1380) },
          { _id: 'ar_demo_3', policyId: 'pol_demo_sanctions', policyVersionHash: randHash(), workflowState: 'approved', targetLifecycleState: 'published', viaBreakGlass: true, createdAt: tIso(2880), completedAt: tIso(2870) },
          { _id: 'ar_demo_4', policyId: 'pol_demo_kyc', policyVersionHash: randHash(), workflowState: 'approved', targetLifecycleState: 'published', viaBreakGlass: false, createdAt: tIso(4320), completedAt: tIso(4200) },
          { _id: 'ar_demo_5', policyId: 'pol_demo_pii', policyVersionHash: randHash(), workflowState: 'awaiting_reviewer', targetLifecycleState: 'in_review', viaBreakGlass: false, createdAt: tIso(60), completedAt: null },
        ],
        recentEffectiveness: [
          { _id: 'ef_demo_1', policyId: 'pol_demo_kyc', policyVersionHash: randHash(), precision: 0.962, recall: 0.918, f1: 0.940, fpr: 0.024, sampleSize: 1842, windowEnd: tIso(60 * 24) },
          { _id: 'ef_demo_2', policyId: 'pol_demo_aml', policyVersionHash: randHash(), precision: 0.987, recall: 0.873, f1: 0.927, fpr: 0.008, sampleSize: 956, windowEnd: tIso(60 * 24) },
          { _id: 'ef_demo_3', policyId: 'pol_demo_sanctions', policyVersionHash: randHash(), precision: 0.998, recall: 0.991, f1: 0.994, fpr: 0.002, sampleSize: 2103, windowEnd: tIso(60 * 24) },
          { _id: 'ef_demo_4', policyId: 'pol_demo_credit', policyVersionHash: randHash(), precision: 0.889, recall: 0.812, f1: 0.849, fpr: 0.034, sampleSize: 487, windowEnd: tIso(60 * 24) },
        ],
      };
    }
    const response = await this.get<API.AuditorViewData>('/api/policies/audit/auditor-view');
    return response;
  }

  async generateNotaryAttestation(input: {
    periodStart: string;
    periodEnd: string;
    cadence?: 'monthly' | 'biweekly' | 'on-demand';
  }): Promise<{ created: boolean; attestation: API.NotaryAttestation }> {
    if (shouldUseMockData()) {
      // Demo mode: synthesize a plausible "just-generated" attestation
      // that mirrors the production response shape. Used by the ad-hoc
      // generate + re-issue UI so the founder + prospects can see the
      // happy-path flow end-to-end.
      const now = new Date().toISOString();
      return {
        created: true,
        attestation: {
          _id: `att_adhoc_${Date.now()}`,
          organizationId: 'org_demo',
          periodStart: input.periodStart,
          periodEnd: input.periodEnd,
          cadence: input.cadence ?? 'on-demand',
          stats: {
            decisionChainEntries: 8421,
            operationsChainEntries: 2104,
            dailyAnchorsProduced: 14,
            otsProofsSubmitted: 14,
            otsProofsUpgraded: 12,
            qtspTimestampsReceived: 0,
            heartbeatVerificationsPassed: 1344,
            heartbeatVerificationsFailed: 0,
            witnessSthsObserved: 0,
            anomaliesDetected: 0,
          },
          anomalies: [],
          verified: true,
          pdfHash: `${Math.floor(Math.random() * 1e16).toString(16).padStart(16, '0')}${'0'.repeat(48)}`,
          pdfByteSize: 2_456_789,
          signatureMode: 'placeholder',
          methodologyVersion: 'v1.0',
          generatedAt: now,
        },
      };
    }
    return this.post<{ created: boolean; attestation: API.NotaryAttestation }>(
      '/api/notary-attestation/generate',
      input,
    );
  }

  async listWitnesses(): Promise<{ items: API.WitnessRegistryEntry[] }> {
    if (shouldUseMockData()) return { items: [] };
    return this.get<{ items: API.WitnessRegistryEntry[] }>('/api/witness-admin');
  }

  async registerWitness(input: {
    name: string;
    contactEmail: string;
    organisation: string | null;
    country: string;
  }): Promise<{
    witness: {
      _id: string;
      name: string;
      organisation: string | null;
      country: string;
      registeredAt: string;
    };
    sharedSecret: string;
  }> {
    if (shouldUseMockData()) {
      // Demo mode: never mint a real shared secret. Returning a clearly
      // non-functional placeholder keeps the page's success-state UI
      // testable without producing a value someone could misuse.
      return {
        witness: {
          _id: 'mock-witness-id',
          name: input.name,
          organisation: input.organisation,
          country: input.country.toUpperCase(),
          registeredAt: new Date().toISOString(),
        },
        sharedSecret: 'mock-shared-secret-DO-NOT-USE-FOR-REAL-WITNESSES',
      };
    }
    return this.post('/api/witness-admin/register', input);
  }

  async deactivateWitness(id: string): Promise<{ deactivated: true }> {
    if (shouldUseMockData()) return { deactivated: true };
    return this.delete<{ deactivated: true }>(`/api/witness-admin/${encodeURIComponent(id)}`);
  }

  async executeErasure(input: {
    traceIds: string[];
    acknowledgment: API.ErasureLedgerAcknowledgment;
    reason: string;
  }): Promise<{ result: API.ErasureLedgerExecuteResponse }> {
    if (shouldUseMockData()) {
      // Demo mode never touches real audit chains. Mirror the shape so the
      // success card renders for testing without nullifying anything.
      return {
        result: {
          erasureSeq: 0,
          erasureHash: '0'.repeat(64),
          customerAcknowledgmentHash: '0'.repeat(64),
          nullifiedCount: input.traceIds.length,
        },
      };
    }
    return this.post<{ result: API.ErasureLedgerExecuteResponse }>(
      '/api/erasure/acknowledge-and-execute',
      input
    );
  }

  async getErasureStatus(): Promise<{ status: API.ErasureLedgerStatus }> {
    if (shouldUseMockData()) {
      // Persona Nordlicht (~9 Mon.): 6 historische Löschungen (DSGVO Art. 17)
      // gestreut über die Monate; letzte vor ~9 Tagen, 7 Traces nullifiziert.
      return {
        status: {
          totalEntries: 6,
          lastErasureSeq: 6,
          lastErasureHash: '7f3a9c2e' + '4b81d05607a3' + '0'.repeat(44) + '8c12',
          lastErasureAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
          lastErasedTraceCount: 7,
        },
      };
    }
    return this.get<{ status: API.ErasureLedgerStatus }>('/api/erasure/status');
  }

  async verifyErasureLedger(): Promise<{ result: API.ErasureLedgerVerifyResult }> {
    if (shouldUseMockData()) {
      return {
        result: {
          ok: true,
          totalChecked: 3,
          durationMs: 12,
          lastValidSeq: 3,
          brokenAtSeq: null,
          brokenReason: null,
        },
      };
    }
    return this.get<{ result: API.ErasureLedgerVerifyResult }>('/api/erasure/verify');
  }

  async getErasureForTrace(traceId: string): Promise<{ entry: API.ErasureLedgerEntry | null }> {
    if (shouldUseMockData()) {
      // Demo: traces starting with "demo_erased_" return a fake entry,
      // others return null. Lets prospects type any value and see both
      // the "found" + "not found" UX paths.
      const found = traceId.toLowerCase().startsWith('demo_erased_');
      return {
        entry: found
          ? {
              _id: 'el-006',
              organizationId: 'org1',
              erasureSeq: 6,
              prevErasureHash: '2bc1f8a4' + '07d3e591' + '0'.repeat(48),
              erasureHash: '7f3a9c2e' + '4b81d05607a3' + '0'.repeat(44) + '8c12',
              timestamp: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
              erasedTraceIds: [traceId, 'demo_erased_other_1', 'demo_erased_other_2'],
              erasureReason: 'right-to-erasure',
              customerAcknowledgmentHash: '4d8e1b6f' + '9a02c7d4' + '0'.repeat(48),
              executingActorId: 'dsb@nordlicht.example',
            }
          : null,
      };
    }
    return this.get<{ entry: API.ErasureLedgerEntry | null }>(
      `/api/erasure/trace/${encodeURIComponent(traceId)}`
    );
  }

  async generatePortableBundle(input: {
    periodStart: string;
    periodEnd: string;
    reason?: string;
  }): Promise<API.PortableBundleGenerateResponse> {
    if (shouldUseMockData()) {
      // Synthetic but realistic preview — lets demo viewers see what a
      // generated bundle's success card looks like (hash, size, counts,
      // coverage, expiry, files). downloadUrl is empty in mock so the
      // Download button surfaces a "preview only" toast in the component
      // instead of 404-ing through the API client.
      const days = Math.max(
        1,
        Math.round(
          (new Date(input.periodEnd).getTime() - new Date(input.periodStart).getTime()) /
            86_400_000
        ) + 1
      );
      const decisionEntries = days * 412;
      const operationsEntries = days * 18;
      const anchors = days;
      const erasureRedactions = 0;
      // Stable demo hash — deterministic per (start,end) so reloads look consistent.
      const seed = `${input.periodStart}|${input.periodEnd}`;
      let h = 0x811c9dc5;
      for (let i = 0; i < seed.length; i += 1) {
        h = Math.imul(h ^ seed.charCodeAt(i), 0x01000193) >>> 0;
      }
      const hex = (h.toString(16) + '00000000').slice(0, 8);
      const bundleHash = (hex.repeat(8)).slice(0, 64);
      // ~3.1KB per decision entry, ~1.8KB per ops entry, ~0.6KB per anchor — realistic gzip range.
      const sizeBytes =
        decisionEntries * 3_100 + operationsEntries * 1_800 + anchors * 600 + 65_536;
      const generatedAt = new Date().toISOString();
      const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      return {
        bundleHash,
        sizeBytes,
        generatedAt,
        expiresAt,
        generatedBy: {
          userId: 'usr_demo',
          email: 'demo.officer@adjudon.example',
          name: 'Demo Compliance Officer',
        },
        reason: input.reason?.trim() || null,
        manifest: {
          bundleVersion: 'v1.0.0',
          methodologyVersion: '2026.04',
          generatedAt,
          organization: { id: 'org_demo', name: 'Demo Organization', plan: 'governance' },
          period: { start: input.periodStart, end: input.periodEnd },
          counts: {
            decisionChainEntries: decisionEntries,
            operationsChainEntries: operationsEntries,
            merkleAnchors: anchors,
            otsProofs: 0,
            erasureLedgerEntries: erasureRedactions,
          },
          files: [
            'manifest.json',
            'decision-chain.jsonl',
            'operations-chain.jsonl',
            'merkle-anchors.jsonl',
            'erasure-ledger.jsonl',
            'methodology.md',
            'verify.sh',
            'README.md',
          ],
        },
        coverage: {
          decisionChainEntries: decisionEntries,
          operationsChainEntries: operationsEntries,
          erasureRedactions,
          complete: true,
        },
        downloadUrl: '',
      };
    }
    return this.post<API.PortableBundleGenerateResponse>('/api/audit-bundle/generate', input);
  }

  async listPortableBundles(): Promise<API.PortableBundleListResponse> {
    if (shouldUseMockData()) {
      // 3 demo bundles for the recent-history table. Stable hashes so reloads
      // don't shuffle the visual. Counts scale with the period length.
      // Expiry = generatedAt + 7d — backend's BUNDLE_CACHE_TTL_MS.
      const TTL_MS = 7 * 24 * 60 * 60 * 1000;
      const now = new Date();
      const mkRecord = (
        offsetDaysFromNow: number,
        periodStartISO: string,
        periodEndISO: string,
        hashSeed: string,
        decisionEntries: number,
        operationsEntries: number,
        anchors: number,
        reason: string | null,
        generatorName: string,
        generatorEmail: string,
        erasureRedactions: number = 0,
      ): API.PortableBundleListItem => {
        let h = 0x811c9dc5;
        for (let i = 0; i < hashSeed.length; i += 1) {
          h = Math.imul(h ^ hashSeed.charCodeAt(i), 0x01000193) >>> 0;
        }
        const hex = (h.toString(16) + '00000000').slice(0, 8);
        const bundleHash = hex.repeat(8).slice(0, 64);
        const sizeBytes = decisionEntries * 3_100 + operationsEntries * 1_800 + anchors * 600 + 65_536;
        const generatedAt = new Date(now.getTime() - offsetDaysFromNow * 86_400_000).toISOString();
        const expiresAt = new Date(new Date(generatedAt).getTime() + TTL_MS).toISOString();
        return {
          bundleHash,
          sizeBytes,
          generatedAt,
          expiresAt,
          generatedBy: {
            userId: 'usr_demo',
            email: generatorEmail,
            name: generatorName,
          },
          reason,
          manifest: {
            bundleVersion: 'v1.0.0',
            methodologyVersion: '2026.04',
            generatedAt,
            organization: { id: 'org_demo', name: 'Demo Organization', plan: 'governance' },
            period: { start: periodStartISO, end: periodEndISO },
            counts: {
              decisionChainEntries: decisionEntries,
              operationsChainEntries: operationsEntries,
              merkleAnchors: anchors,
              otsProofs: 0,
              erasureLedgerEntries: erasureRedactions,
            },
            files: [
              'manifest.json',
              'decision-chain.jsonl',
              'operations-chain.jsonl',
              'merkle-anchors.jsonl',
              'erasure-ledger.jsonl',
              'methodology.md',
              'verify.sh',
              'README.md',
            ],
          },
          coverage: {
            decisionChainEntries: decisionEntries,
            operationsChainEntries: operationsEntries,
            erasureRedactions,
            complete: true,
          },
          downloadUrl: '',
        };
      };
      // Demo-Persona „Nordlicht Versicherung AG": 3 Quartals-Exporte über ~9
      // Monate (15.12.2025 · 15.03.2026 · 10.06.2026). Nur der jüngste liegt
      // innerhalb des 7-Tage-Cache-TTL; die zwei älteren sind abgelaufen (die
      // History-Tabelle zeigt sie mit „abgelaufen"-Badge — reales 9-Monats-Bild).
      // Entscheidungs-Zahl wächst mit dem Quartal (~1.100/Tag · 90 Tage).
      return {
        bundles: [
          mkRecord(2, '2026-04-01T00:00:00.000Z', '2026-06-09T23:59:59.999Z', 'q2-2026',
            76_320, 612, 70, 'Q2 2026 BaFin-Beweispaket — Quartalsmeldung KI-Entscheidungs-Schicht', 'Compliance-Team', 'compliance@nordlicht.example', 1),
          mkRecord(89, '2026-01-01T00:00:00.000Z', '2026-03-14T23:59:59.999Z', 'q1-2026',
            71_840, 548, 73, 'Q1 2026 BaFin-Beweispaket + DORA-Vorfallsbezug (Fail-Open-Nacht)', 'CISO', 'ciso@nordlicht.example', 2),
          mkRecord(180, '2025-09-15T00:00:00.000Z', '2025-12-14T23:59:59.999Z', 'q4-2025',
            58_100, 421, 91, 'Q4 2025 Erst-Beweispaket — interne Revision (Onboarding-Quartal)', 'Interne Revision', 'revision@nordlicht.example'),
        ],
      };
    }
    return this.get<API.PortableBundleListResponse>('/api/audit-bundle');
  }

  async getAuditChainSnapshot(): Promise<API.AuditIntegritySnapshot & {
    cedarMetrics?: {
      cedarRuns: number;
      cedarOk: number;
      cedarCompileErrors: number;
      cedarEvalErrors: number;
      legacyFallbackRuns: number;
    };
  }> {
    if (shouldUseMockData()) {
      // Minimal mock that lets the page render the empty-data path.
      return {
        chainStatus: {
          totalEntries: 0,
          lastSequence: 0,
          lastChainHash: null,
          lastEntryAt: null,
          anchorCount: 0,
          lastAnchorAt: null,
          lastVerifiedAt: null,
          lastVerificationOk: null,
          algorithm: 'sha256',
          canonicalization: 'jcs-rfc8785',
          appendLatencyP95Ms: null,
        } as API.AuditIntegrityChainStatus,
        heartbeat: {
          recentVerifications: [],
          global: {
            totalSmokeRuns: 0,
            totalFullRuns: 0,
            totalFailures: 0,
            lastFailureAt: null,
            smokeTailSize: 0,
            orgsTracked: 0,
          } as API.AuditIntegrityHeartbeatGlobal,
        },
        otsAnchor: { liveMode: false, configuredCalendars: 0 } as API.OtsAnchorStatus,
        tesseraWitness: { mode: 'mock', available: false } as API.TesseraStatus,
        methodology: { version: '1.0', url: '#' },
      };
    }
    return this.get('/api/audit-integrity/snapshot');
  }

  // ── Heartbeat History (Council F-30-C02 — Timeline of past verifications) ──
  /**
   * Past chain-verification runs for the calling org (smoke + full).
   * Newest first. Used by the AuditChainIntegrityPage Heartbeat Timeline.
   */
  async getHeartbeatHistory(limit = 30): Promise<{
    history: Array<{
      verifiedAt: string;
      ok: boolean;
      durationMs: number;
      totalChecked: number;
      lastValidSequence: number | null;
      brokenAt: number | null;
      brokenReason: string | null;
      scope: 'smoke' | 'full';
    }>;
  }> {
    const safeLimit = Math.min(Math.max(limit, 1), 100);
    if (shouldUseMockData()) {
      const now = Date.now();
      // Demo data: 12 successful smoke runs, 1 broken event, 4 more ok runs
      const history = Array.from({ length: 17 }, (_, i) => {
        const verifiedAt = new Date(now - i * 30 * 60_000).toISOString();
        const broken = i === 4; // a single failure 2 hours ago
        return {
          verifiedAt,
          ok: !broken,
          durationMs: 30 + Math.round(Math.random() * 80),
          totalChecked: 250 + Math.round(Math.random() * 50),
          lastValidSequence: broken ? 247 : null,
          brokenAt: broken ? 248 : null,
          brokenReason: broken ? 'hash mismatch at sequence 248' : null,
          scope: i % 4 === 0 ? ('full' as const) : ('smoke' as const),
        };
      });
      return Promise.resolve({ history });
    }
    return this.get<{ history: any[] }>(`/api/audit-integrity/heartbeat/history?limit=${safeLimit}`);
  }

  // ── Regulator Replay (Council F-26 — Standalone UI for audit/replay) ───
  /**
   * Build a signed regulator-replay artifact for a single trace.
   * Enterprise+ via `cpiFeedbackIngest` plan-gate.
   */
  async buildRegulatorReplay(traceId: string): Promise<Record<string, unknown>> {
    if (shouldUseMockData()) {
      return Promise.resolve({
        artifactType: 'regulator-replay-v1',
        organizationId: 'org_demo',
        traceId,
        agentId: 'agt_demo',
        decisionType: 'classification',
        emitted: {
          confidenceScore: 0.87,
          score_uncalibrated: 0.91,
          score_calibrated: 0.87,
          status: 'approved',
          tags: ['demo'],
          timestamp: new Date().toISOString(),
        },
        decisionTimeSignals: {
          signals: { pillar1: 0.9, pillar2: 0.85, pillar3: 0.88 },
          calibrationMapVersion: 'isotonic-v1.2',
          calibratedScore: 0.87,
          rawScore: 0.91,
          timestamp: new Date().toISOString(),
        },
        currentCalibrationMap: {
          mapType: 'isotonic',
          methodologyVersion: 'isotonic-v1.2',
          shrinkageWeight: 0.3,
          sampleSize: 1247,
          ece: 0.018,
          brier: 0.092,
          lastRefitAt: new Date(Date.now() - 7 * 24 * 3600_000).toISOString(),
        },
        replayedAt: new Date().toISOString(),
        methodologyVersion: 'isotonic-v1.2',
        signature: 'MOCK-HMAC-DEMO-' + traceId,
        signature_algorithm: 'HMAC-SHA256-RFC8785',
      });
    }
    return this.get<Record<string, unknown>>(`/api/audit/replay/${encodeURIComponent(traceId)}`);
  }

  // ── DPA Templates (Council F-43 — GDPR Art. 33 Notification) ───────────
  /**
   * List available EU DPAs for the country-selector dropdown.
   * Returns `{ dpas: DpaEntry[] }` where each entry has code/name/country/
   * region/submissionUrl/email/language.
   */
  async listDpaRegistry(): Promise<{ dpas: API.DpaRegistryEntry[] }> {
    if (shouldUseMockData()) return mockData.listDpaRegistry();
    return this.get('/api/dpa-templates');
  }

  /**
   * Vorbefüllte Art.-33-Meldungs-Vorlage (Markdown) für einen Vorfall + eine
   * Aufsichtsbehörde. Endpoint liefert text/markdown (KEIN JSON-Envelope) —
   * gleicher Raw-Fetch-Pfad wie getDoraIncidentReport.
   */
  async getDpaIncidentTemplate(incidentId: string, dpaCode: string): Promise<string> {
    if (shouldUseMockData()) return mockData.getDpaIncidentTemplate(incidentId, dpaCode);
    const baseUrl = ENV.API_BASE_URL || '';
    const token = localStorage.getItem('adjudon_token');
    const res = await fetch(
      `${baseUrl}/api/dpa-templates/incident/${encodeURIComponent(incidentId)}/${encodeURIComponent(dpaCode)}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!res.ok) throw new Error(`Vorlage fehlgeschlagen: ${res.status}`);
    return res.text();
  }

  // ── Regulator Replay (Council F-26 — auditor power-tool) ───────────────
  /**
   * Build the regulator-replay artifact for a single trace. Returns the
   * signed JSON containing decision-time signal vector + currently-active
   * calibration map for offline replay verification. Enterprise+ gated
   * (backend `cpiFeedbackIngest` plan-feature).
   */
  async getRegulatorReplay(traceId: string): Promise<API.RegulatorReplayBundle> {
    return this.get(`/api/audit/replay/${encodeURIComponent(traceId)}`);
  }

  // ── TEE Attestation (Council F-16 — Confidential-compute status) ──────
  /**
   * Get the TEE attestation status snapshot for the current org. Returns
   * provider availability flags (aws/azure/gcp), configured provider name,
   * and mode (live vs. mock). Custom-plan gated.
   */
  async getTeeAttestationStatus(): Promise<API.TeeAttestationProviderStatus> {
    return this.get('/api/tee/status');
  }

  // ── Compliance-Maturity Registry (Council F-46 Sprint-Zero) ────────────
  /**
   * List all compliance-maturity registry entries (Adjudon-platform-wide,
   * not per-org). Supports family/status filtering for the admin matrix.
   */
  async listComplianceMaturity(params?: { family?: string; status?: string; includeArchived?: boolean }): Promise<{ entries: API.ComplianceMaturityEntry[]; count: number }> {
    return this.get('/api/compliance-maturity/entries', params as Record<string, unknown> | undefined);
  }

  /**
   * Aggregate summary — counts by family, status, certificationStatus,
   * plus `claimableWithoutDisclosure` (entries where operationalStatus is
   * mock/scaffold but marketingClaim lacks a honesty disclosure word).
   */
  async getComplianceMaturitySummary(): Promise<API.ComplianceMaturitySummary> {
    return this.get('/api/compliance-maturity/summary');
  }

  async getComplianceMaturityEntry(featureKey: string): Promise<API.ComplianceMaturityEntry> {
    return this.get(`/api/compliance-maturity/entries/${encodeURIComponent(featureKey)}`);
  }

  /**
   * Pre-push CI hook lookup — substring match a JSX-rendered claim string
   * against the active registry. Returns `{ found: true, match }` or
   * `{ found: false, match: null }`.
   */
  async lookupComplianceClaim(claim: string): Promise<{ found: boolean; match: API.ComplianceMaturityEntry | null }> {
    return this.get('/api/compliance-maturity/lookup', { claim });
  }

  // ── 1. CONSENT MANAGEMENT ─────────────────────────────────────────────
  async listConsents(params?: { purpose?: string; limit?: number }): Promise<API.ConsentListResponse> {
    if (shouldUseMockData()) return mockData.listConsents(params);
    return this.get('/api/consent', params);
  }

  async getConsent(subjectRef: string): Promise<API.ConsentDetailResponse> {
    if (shouldUseMockData()) return mockData.getConsent(subjectRef);
    return this.get(`/api/consent/${encodeURIComponent(subjectRef)}`);
  }

  async createConsent(subjectRef: string, purposes: any[]): Promise<API.ConsentDetailResponse> {
    if (shouldUseMockData()) return mockData.createConsent(subjectRef, purposes);
    return this.post(`/api/consent/${encodeURIComponent(subjectRef)}`, { purposes });
  }

  async withdrawConsent(subjectRef: string, purpose: string): Promise<API.ConsentDetailResponse> {
    if (shouldUseMockData()) return mockData.withdrawConsent(subjectRef, purpose);
    return this.post(`/api/consent/${encodeURIComponent(subjectRef)}/withdraw`, { purpose });
  }

  // ── 2. DATA SUBJECT ACCESS REQUESTS (DSAR) ────────────────────────────
  async listDsars(params?: { status?: string; subjectRef?: string; limit?: number }): Promise<API.DsarListResponse> {
    if (shouldUseMockData()) return mockData.listDsars(params);
    return this.get('/api/dsar', params);
  }

  async getDsar(id: string): Promise<API.DsarDetailResponse> {
    if (shouldUseMockData()) return mockData.getDsar(id);
    return this.get(`/api/dsar/${encodeURIComponent(id)}`);
  }

  async createDsar(data: any): Promise<API.DsarDetailResponse> {
    if (shouldUseMockData()) return mockData.createDsar(data);
    return this.post('/api/dsar', data);
  }

  async updateDsar(id: string, data: any): Promise<API.DsarDetailResponse> {
    if (shouldUseMockData()) return mockData.updateDsar(id, data);
    return this.put(`/api/dsar/${encodeURIComponent(id)}`, data);
  }

  async transitionDsarStatus(id: string, status: string, data?: any): Promise<API.DsarDetailResponse> {
    if (shouldUseMockData()) return mockData.transitionDsarStatus(id, status, data);
    return this.post(`/api/dsar/${encodeURIComponent(id)}/transition`, { status, ...data });
  }

  async extendDsarDeadline(id: string, notes?: string): Promise<API.DsarDetailResponse> {
    if (shouldUseMockData()) return mockData.extendDsarDeadline(id, notes);
    return this.post(`/api/dsar/${encodeURIComponent(id)}/extend`, { notes });
  }

  async deleteDsar(id: string): Promise<{ deleted: boolean }> {
    if (shouldUseMockData()) return mockData.deleteDsar(id);
    return this.delete(`/api/dsar/${encodeURIComponent(id)}`);
  }

  // ── 3. SUB-PROCESSOR REGISTRY ─────────────────────────────────────────
  async listSubProcessors(params?: { status?: string; category?: string; limit?: number }): Promise<API.SubProcessorListResponse> {
    if (shouldUseMockData()) return mockData.listSubProcessors(params);
    return this.get('/api/subprocessors', params);
  }

  async getSubProcessor(id: string): Promise<API.SubProcessorDetailResponse> {
    if (shouldUseMockData()) return mockData.getSubProcessor(id);
    return this.get(`/api/subprocessors/${encodeURIComponent(id)}`);
  }

  async createSubProcessor(data: any): Promise<API.SubProcessorDetailResponse> {
    if (shouldUseMockData()) return mockData.createSubProcessor(data);
    return this.post('/api/subprocessors', data);
  }

  async updateSubProcessor(id: string, data: any): Promise<API.SubProcessorDetailResponse> {
    if (shouldUseMockData()) return mockData.updateSubProcessor(id, data);
    return this.put(`/api/subprocessors/${encodeURIComponent(id)}`, data);
  }

  async terminateSubProcessor(id: string): Promise<API.SubProcessorDetailResponse> {
    if (shouldUseMockData()) return mockData.terminateSubProcessor(id);
    return this.post(`/api/subprocessors/${encodeURIComponent(id)}/terminate`, {});
  }

  // Art. 28(3)(d): controller objects to / resolves an objection on a sub-processor.
  async objectSubProcessor(id: string, reason: string): Promise<API.SubProcessorDetailResponse> {
    if (shouldUseMockData()) return mockData.objectSubProcessor(id, reason);
    return this.post(`/api/subprocessors/${encodeURIComponent(id)}/object`, { reason });
  }

  async resolveSubProcessorObjection(id: string, approve: boolean): Promise<API.SubProcessorDetailResponse> {
    if (shouldUseMockData()) return mockData.resolveSubProcessorObjection(id, approve);
    return this.post(`/api/subprocessors/${encodeURIComponent(id)}/resolve-objection`, { approve });
  }

  // ── 3b. SIGNED DPA (Art. 28(3)/(9)) ──────────────────────────────────
  // Persona Nordlicht: aktuelle DPA v2.1 angenommen 20.11.2025 von der CCO;
  // ältere v2.0 (Onboarding 15.09.2025) historisch angenommen + abgelöst.
  // Keine Re-Acceptance fällig → ruhiges Normalbild.
  async getCurrentDpa(): Promise<API.SignedDpaCurrentResponse> {
    if (shouldUseMockData()) {
      return { dpa: { id: 'dpa-v2-1', organizationId: 'org1', dpaVersion: 'v2.1', templateHash: '7f3c9a2e1b4d8056'.repeat(4), signedByRef: 'u-cco', signedByName: 'Dr. K. Sander, CCO', signedByEmail: 'cco@nordlicht.example', signedAt: new Date('2025-11-20T09:30:00Z').toISOString(), status: 'signed', notes: '' } };
    }
    return this.get('/api/signed-dpa/current');
  }

  async listDpas(): Promise<API.SignedDpaListResponse> {
    if (shouldUseMockData()) {
      return { dpas: [
        { id: 'dpa-v2-1', organizationId: 'org1', dpaVersion: 'v2.1', templateHash: '7f3c9a2e1b4d8056'.repeat(4), signedByRef: 'u-cco', signedByName: 'Dr. K. Sander, CCO', signedByEmail: 'cco@nordlicht.example', signedAt: new Date('2025-11-20T09:30:00Z').toISOString(), status: 'signed', notes: '' },
        { id: 'dpa-v2-0', organizationId: 'org1', dpaVersion: 'v2.0', templateHash: 'a18d4e02c5f96b37'.repeat(4), signedByRef: 'u-cco', signedByName: 'Dr. K. Sander, CCO', signedByEmail: 'cco@nordlicht.example', signedAt: new Date('2025-09-15T11:00:00Z').toISOString(), status: 'superseded', notes: '' },
      ] };
    }
    return this.get('/api/signed-dpa');
  }

  async getDpaReacceptanceStatus(): Promise<API.SignedDpaReacceptanceStatus> {
    if (shouldUseMockData()) {
      return { reacceptanceRequired: false, currentVersion: 'v2.1', acceptedVersion: 'v2.1' };
    }
    return this.get('/api/signed-dpa/reacceptance-status');
  }

  async acceptCurrentDpa(data: { signedByName?: string; signedByEmail?: string }): Promise<API.SignedDpaCurrentResponse> {
    return this.post('/api/signed-dpa/accept-current', data);
  }

  // ── 3c. DATA RESIDENCY (Art. 44-49) ──────────────────────────────────
  // Persona Nordlicht: alle Endpunkte EU/Frankfurt; region-opake Hosts (Atlas,
  // Resend) wurden per DATA_RESIDENCY_REGION als eu-central-1 deklariert →
  // „EU-aktiv". Overall grün, keine Drittland-Übermittlung. TIA-Vorlage vorhanden.
  async getDataResidency(): Promise<API.DataResidencyResponse> {
    if (shouldUseMockData()) {
      return {
        posture: {
          overall: 'active',
          endpoints: [
            { name: 'MongoDB Atlas (Primärdatenbank)', host: 'cluster0.mongodb.net', status: 'active', classification: 'eu-declared', purpose: 'Primärdatenspeicher', declaredRegion: 'eu-central-1' },
            { name: 'TEI Embedding-Sidecar', host: 'adjudon-tei.internal', status: 'active', classification: 'private-mesh', purpose: 'Vektor-Embeddings', declaredRegion: 'eu-central-1' },
            { name: 'SMTP / Resend', host: 'api.resend.com', status: 'active', classification: 'eu-declared', purpose: 'Transaktions-E-Mail', declaredRegion: 'eu-west-1' },
            { name: 'Sentry (Error-Tracking)', host: 'de.sentry.io', status: 'active', classification: 'eu-host', purpose: 'Fehler-Telemetrie', declaredRegion: 'eu-central-1' },
            { name: 'Product Analytics (PostHog)', host: null, status: 'not-configured', classification: 'not-configured', purpose: 'Produkt-Analytics' },
          ],
          counts: { active: 4, violation: 0, unknown: 0, 'not-configured': 1 },
          configured: 4,
        },
        subProcessors: [],
      };
    }
    return this.get('/api/v1/data-residency');
  }

  // ── 3d. TOM REGISTER (Art. 32) ───────────────────────────────────────
  // Persona Nordlicht (~9 Mon.): 14-Maßnahmen-Register, 13/14 aktiv (1 offen =
  // Feld-Verschlüsselung „verfügbar/inaktiv"); Wirksamkeits-Tests gestreut
  // Jan–Mai 2026 (s. listTomEffectiveness). Zuletzt geprüft = gestern (Self-Test).
  async getTomRegister(): Promise<API.TomRegisterResponse> {
    if (shouldUseMockData()) {
      return {
        register: {
          generatedAt: new Date().toISOString(),
          article: 'GDPR Art. 32 (Sicherheit der Verarbeitung)',
          residency: { status: 'eu_only', note: 'Frankfurt (eu-central-1); keine Nicht-EU-Sub-Prozessoren.' },
          measures: [
            { id: 'A1', article: 'Art. 32(1)(a)', category: 'pseudonymisation', measure: 'Pseudonymisierung / PII-Scrubbing', status: 'active', standDerTechnik: 'SDM; ISO 27001 A.8.11' },
            { id: 'A2', article: 'Art. 32(1)(a)', category: 'encryption', measure: 'Verschlüsselung at rest (AES-256)', status: 'active-infra', standDerTechnik: 'BSI TR-02102-1; ISO 27001 A.8.24' },
            { id: 'A3', article: 'Art. 32(1)(a)', category: 'encryption', measure: 'Verschlüsselung in transit (TLS 1.3)', status: 'active-infra', standDerTechnik: 'BSI TR-02102-2; ISO 27001 A.8.24' },
            { id: 'A4', article: 'Art. 32(1)(a)', category: 'encryption', measure: 'Feld-Verschlüsselung (Capability + KEK-Rotation)', status: 'capability', standDerTechnik: 'BSI TR-02102-1' },
            { id: 'A5', article: 'Art. 32(1)(a)', category: 'access-control', measure: 'Rollenbasierte Zugriffskontrolle (RBAC) + SSO/SCIM', status: 'active', standDerTechnik: 'ISO 27001 A.8.2; A.8.3' },
            { id: 'A6', article: 'Art. 32(1)(a)', category: 'access-control', measure: 'Mandanten-Isolation (organizationId auf jeder Query)', status: 'active', standDerTechnik: 'ISO 27001 A.8.31' },
            { id: 'B1', article: 'Art. 32(1)(b)', category: 'availability', measure: 'Verfügbarkeit / Fail-Open-Pass-Through (SDK)', status: 'active', standDerTechnik: 'ISO 27001 A.8.14' },
            { id: 'B2', article: 'Art. 32(1)(b)', category: 'integrity', measure: 'Eingabe-Validierung (Schema, System-Grenzen)', status: 'active', standDerTechnik: 'ISO 27001 A.8.26' },
            { id: 'B3', article: 'Art. 32(1)(b)', category: 'integrity', measure: 'Hash-Ketten-Integrität (Audit-Trail)', status: 'active', standDerTechnik: 'ISO 27001 A.8.15' },
            { id: 'B4', article: 'Art. 32(1)(b)', category: 'integrity', measure: 'Ketten-Heartbeat-Verifikation', status: 'active', standDerTechnik: 'ISO 27001 A.8.16' },
            { id: 'C1', article: 'Art. 32(1)(c)', category: 'restore', measure: 'Wiederherstellbarkeit (Atlas-Backups)', status: 'active-infra', standDerTechnik: 'ISO 27001 A.8.13' },
            { id: 'C2', article: 'Art. 32(1)(c)', category: 'restore', measure: 'Geographische Redundanz (eu-central-1)', status: 'active-infra', standDerTechnik: 'ISO 27001 A.8.14' },
            { id: 'D1', article: 'Art. 32(1)(d)', category: 'review', measure: 'Automatische Self-Test-Batterie (regelmäßig)', status: 'active', standDerTechnik: 'ISO 27001 A.8.16; A.5.36' },
            { id: 'D2', article: 'Art. 32(1)(d)', category: 'logging', measure: 'Sicherheits-Telemetrie & Anomalie-Alarme', status: 'active', standDerTechnik: 'ISO 27001 A.8.16' },
          ],
        },
        lastSelfTest: { _id: 'tom-self-latest', kind: 'automated_self_test', source: 'adjudon-platform', runAt: new Date(Date.now() - 86400000).toISOString(), overallStatus: 'pass' },
      };
    }
    return this.get('/api/v1/tom-register');
  }

  // Persona: ~11 Wirksamkeits-Prüfungen gestreut Jan–Mai 2026 — monatliche
  // automatische Self-Tests (bestanden, 1 Warnung im März) + 4 manuelle
  // Kontroll-Prüfungen des Verantwortlichen (Pentest/Restore/Zugriff/Scan).
  async listTomEffectiveness(params?: { kind?: string; limit?: number }): Promise<API.TomEffectivenessListResponse> {
    if (shouldUseMockData()) {
      const tests: API.TomEffectivenessTest[] = [
        // Monatliche automatische Self-Tests (jüngste zuerst).
        { _id: 'tom-self-2026-05', kind: 'automated_self_test', source: 'adjudon-platform', runAt: new Date('2026-05-28T03:00:00Z').toISOString(), overallStatus: 'pass' },
        { _id: 'tom-self-2026-04', kind: 'automated_self_test', source: 'adjudon-platform', runAt: new Date('2026-04-28T03:00:00Z').toISOString(), overallStatus: 'pass' },
        { _id: 'tom-self-2026-03', kind: 'automated_self_test', source: 'adjudon-platform', runAt: new Date('2026-03-28T03:00:00Z').toISOString(), overallStatus: 'warn', notes: 'Ketten-Heartbeat 1 Lauf verspätet (Fail-Open-Nacht 18.02.); seither überwacht.' },
        { _id: 'tom-self-2026-02', kind: 'automated_self_test', source: 'adjudon-platform', runAt: new Date('2026-02-28T03:00:00Z').toISOString(), overallStatus: 'pass' },
        { _id: 'tom-self-2026-01', kind: 'automated_self_test', source: 'adjudon-platform', runAt: new Date('2026-01-28T03:00:00Z').toISOString(), overallStatus: 'pass' },
        // Quartalsweise Ketten-Verifikation.
        { _id: 'tom-chain-2026-05', kind: 'chain_verification', source: 'adjudon-platform', runAt: new Date('2026-05-15T06:00:00Z').toISOString(), overallStatus: 'pass' },
        { _id: 'tom-chain-2026-02', kind: 'chain_verification', source: 'adjudon-platform', runAt: new Date('2026-02-15T06:00:00Z').toISOString(), overallStatus: 'pass' },
        // Manuelle Kontroll-Prüfungen des Verantwortlichen (Nordlicht).
        { _id: 'tom-pentest-2026-04', kind: 'penetration_test', source: 'controller', runAt: new Date('2026-04-22T00:00:00Z').toISOString(), overallStatus: 'pass', evidenceUrl: 'https://nordlicht.example/sicherheit/pentest-q2-2026.pdf', notes: 'OWASP-ASVS L2, Scope = Dashboard + Ingestion-API; keine kritischen Findings.' },
        { _id: 'tom-restore-2026-03', kind: 'restore_drill', source: 'controller', runAt: new Date('2026-03-10T00:00:00Z').toISOString(), overallStatus: 'pass', evidenceUrl: 'https://nordlicht.example/sicherheit/restore-drill-2026-03.pdf', notes: 'Wiederherstellung aus Atlas-Backup, RTO 47 min (Ziel 60 min).' },
        { _id: 'tom-access-2026-02', kind: 'access_review', source: 'controller', runAt: new Date('2026-02-05T00:00:00Z').toISOString(), overallStatus: 'pass', notes: 'Quartals-Zugriffs-Review, 2 verwaiste Konten deaktiviert.' },
        { _id: 'tom-scan-2026-01', kind: 'vulnerability_scan', source: 'controller', runAt: new Date('2026-01-18T00:00:00Z').toISOString(), overallStatus: 'pass', evidenceUrl: 'https://nordlicht.example/sicherheit/scan-2026-01.pdf', notes: 'Authenticated Scan, 0 hoch/kritisch.' },
      ];
      const limited = params?.limit ? tests.slice(0, params.limit) : tests;
      const filtered = params?.kind ? limited.filter((t) => t.kind === params.kind) : limited;
      return { tests: filtered };
    }
    return this.get('/api/v1/tom-register/effectiveness', params);
  }

  async runTomSelfTest(): Promise<{ result: API.TomEffectivenessTest }> {
    return this.post('/api/v1/tom-register/effectiveness/self-test', {});
  }

  async recordTomManualTest(data: { kind: string; overallStatus: string; evidenceUrl?: string; notes?: string }): Promise<{ test: API.TomEffectivenessTest }> {
    return this.post('/api/v1/tom-register/effectiveness/manual', data);
  }

  // ── 3e. COOKIE CONSENT (§ 25 TDDDG) ──────────────────────────────────
  // Persona Nordlicht: 12 aufgezeichnete Einwilligungen über die Monate —
  // 8 aktiv, 3 widerrufen, 1 abgelaufen (sichtbarer „abgelaufen"-Flag/amber).
  // 1-Jahr-Frist; erteilt gestreut über die letzten ~11 Monate.
  async listCookieConsents(params?: { subjectRef?: string; withdrawn?: string; limit?: number }): Promise<API.CookieConsentListResponse> {
    if (shouldUseMockData()) {
      const day = 86400000;
      const year = 365 * day;
      const iso = (ms: number) => new Date(Date.now() - ms).toISOString();
      const isoF = (ms: number) => new Date(Date.now() + ms).toISOString();
      const consents: API.CookieConsentRecord[] = [
        // Aktiv, jüngst erteilt — volle Einwilligung.
        { id: 'cc-01', organizationId: 'org1', subjectRef: 'visitor_a1b2c3', categories: [{ name: 'necessary', granted: true }, { name: 'functional', granted: true }, { name: 'analytics', granted: true }, { name: 'marketing', granted: true }], consentString: null, consentedAt: iso(6 * day), expiresAt: isoF(year - 6 * day), withdrawnAt: null, recordedByEmail: '', createdAt: null, updatedAt: null },
        // Aktiv — nur erforderlich + funktional (Analyse/Marketing abgelehnt).
        { id: 'cc-02', organizationId: 'org1', subjectRef: 'visitor_d4e5f6', categories: [{ name: 'necessary', granted: true }, { name: 'functional', granted: true }, { name: 'analytics', granted: false }, { name: 'marketing', granted: false }], consentString: null, consentedAt: iso(14 * day), expiresAt: isoF(year - 14 * day), withdrawnAt: null, recordedByEmail: '', createdAt: null, updatedAt: null },
        // Aktiv — erforderlich + Analyse.
        { id: 'cc-03', organizationId: 'org1', subjectRef: 'visitor_07a8b9', categories: [{ name: 'necessary', granted: true }, { name: 'analytics', granted: true }, { name: 'marketing', granted: false }], consentString: null, consentedAt: iso(28 * day), expiresAt: isoF(year - 28 * day), withdrawnAt: null, recordedByEmail: '', createdAt: null, updatedAt: null },
        // Aktiv — nur erforderlich.
        { id: 'cc-04', organizationId: 'org1', subjectRef: 'visitor_1c2d3e', categories: [{ name: 'necessary', granted: true }, { name: 'functional', granted: false }, { name: 'analytics', granted: false }, { name: 'marketing', granted: false }], consentString: null, consentedAt: iso(45 * day), expiresAt: isoF(year - 45 * day), withdrawnAt: null, recordedByEmail: '', createdAt: null, updatedAt: null },
        // Widerrufen — Betroffener hat Analyse zurückgezogen.
        { id: 'cc-05', organizationId: 'org1', subjectRef: 'visitor_4f5a6b', categories: [{ name: 'necessary', granted: true }, { name: 'analytics', granted: true }], consentString: null, consentedAt: iso(70 * day), expiresAt: isoF(year - 70 * day), withdrawnAt: iso(12 * day), recordedByEmail: '', createdAt: null, updatedAt: null },
        // Aktiv — funktional + Analyse.
        { id: 'cc-06', organizationId: 'org1', subjectRef: 'visitor_7c8d9e', categories: [{ name: 'necessary', granted: true }, { name: 'functional', granted: true }, { name: 'analytics', granted: true }, { name: 'marketing', granted: false }], consentString: null, consentedAt: iso(95 * day), expiresAt: isoF(year - 95 * day), withdrawnAt: null, recordedByEmail: '', createdAt: null, updatedAt: null },
        // Widerrufen.
        { id: 'cc-07', organizationId: 'org1', subjectRef: 'visitor_0f1a2b', categories: [{ name: 'necessary', granted: true }, { name: 'marketing', granted: true }], consentString: null, consentedAt: iso(120 * day), expiresAt: isoF(year - 120 * day), withdrawnAt: iso(40 * day), recordedByEmail: '', createdAt: null, updatedAt: null },
        // Aktiv — volle Einwilligung.
        { id: 'cc-08', organizationId: 'org1', subjectRef: 'visitor_3c4d5e', categories: [{ name: 'necessary', granted: true }, { name: 'functional', granted: true }, { name: 'analytics', granted: true }, { name: 'marketing', granted: true }], consentString: null, consentedAt: iso(150 * day), expiresAt: isoF(year - 150 * day), withdrawnAt: null, recordedByEmail: '', createdAt: null, updatedAt: null },
        // Aktiv — nur erforderlich.
        { id: 'cc-09', organizationId: 'org1', subjectRef: 'visitor_6f7a8b', categories: [{ name: 'necessary', granted: true }], consentString: null, consentedAt: iso(190 * day), expiresAt: isoF(year - 190 * day), withdrawnAt: null, recordedByEmail: '', createdAt: null, updatedAt: null },
        // Widerrufen.
        { id: 'cc-10', organizationId: 'org1', subjectRef: 'visitor_9c0d1e', categories: [{ name: 'necessary', granted: true }, { name: 'analytics', granted: true }, { name: 'marketing', granted: true }], consentString: null, consentedAt: iso(240 * day), expiresAt: isoF(year - 240 * day), withdrawnAt: iso(80 * day), recordedByEmail: '', createdAt: null, updatedAt: null },
        // Aktiv — funktional.
        { id: 'cc-11', organizationId: 'org1', subjectRef: 'visitor_2f3a4b', categories: [{ name: 'necessary', granted: true }, { name: 'functional', granted: true }, { name: 'analytics', granted: false }, { name: 'marketing', granted: false }], consentString: null, consentedAt: iso(300 * day), expiresAt: isoF(year - 300 * day), withdrawnAt: null, recordedByEmail: '', createdAt: null, updatedAt: null },
        // Abgelaufen — vor >1 Jahr erteilt, nie erneuert (sichtbarer amber-Flag).
        { id: 'cc-12', organizationId: 'org1', subjectRef: 'visitor_5c6d7e', categories: [{ name: 'necessary', granted: true }, { name: 'analytics', granted: true }], consentString: null, consentedAt: iso(340 * day), expiresAt: iso(8 * day), withdrawnAt: null, recordedByEmail: '', createdAt: null, updatedAt: null },
      ];
      const filtered = params?.subjectRef
        ? consents.filter((c) => c.subjectRef === params.subjectRef)
        : consents;
      const limited = params?.limit ? filtered.slice(0, params.limit) : filtered;
      return { consents: limited };
    }
    return this.get('/api/v1/cookie-consent', params);
  }

  async withdrawCookieConsent(subjectRef: string): Promise<API.CookieConsentDetailResponse> {
    if (shouldUseMockData()) {
      // Demo: nur withdrawnAt setzen; Kategorien gespiegelt aus der Liste.
      return { consent: { id: `cc-${subjectRef}`, organizationId: 'org1', subjectRef, categories: [{ name: 'necessary', granted: true }], consentString: null, consentedAt: new Date(Date.now() - 30 * 86400000).toISOString(), expiresAt: null, withdrawnAt: new Date().toISOString(), recordedByEmail: '', createdAt: null, updatedAt: null } };
    }
    return this.post(`/api/v1/cookie-consent/${encodeURIComponent(subjectRef)}/withdraw`, {});
  }

  // ── 4. DATA ACCESS REQUESTS (EU Data Act Art. 4–6) ───────────────────
  async listDataAccessRequests(params?: { status?: string; requesterType?: string; search?: string; limit?: number }): Promise<API.DataAccessListResponse> {
    if (shouldUseMockData()) return mockData.listDataAccessRequests(params);
    return this.get('/api/v1/data-access', params);
  }

  async getDataAccessRequest(id: string): Promise<API.DataAccessDetailResponse> {
    if (shouldUseMockData()) return mockData.getDataAccessRequest(id);
    return this.get(`/api/v1/data-access/${encodeURIComponent(id)}`);
  }

  async createDataAccessRequest(data: any): Promise<API.DataAccessDetailResponse> {
    if (shouldUseMockData()) return mockData.createDataAccessRequest(data);
    return this.post('/api/v1/data-access', data);
  }

  // Backend `decide` expects { decision: 'approve' | 'deny' } — NOT { status }.
  async decideDataAccessRequest(id: string, decision: 'approve' | 'deny', decisionReason?: string): Promise<API.DataAccessDetailResponse> {
    if (shouldUseMockData()) return mockData.decideDataAccessRequest(id, decision, decisionReason);
    return this.post(`/api/v1/data-access/${encodeURIComponent(id)}/decide`, { decision, decisionReason });
  }

  async revokeDataAccessRequest(id: string, decisionReason?: string): Promise<API.DataAccessDetailResponse> {
    if (shouldUseMockData()) return mockData.revokeDataAccessRequest(id);
    return this.post(`/api/v1/data-access/${encodeURIComponent(id)}/revoke`, { decisionReason });
  }

  // ── 4a. ACCESS JOURNAL (EU Data Act "Zugänglichkeits-Log" — Art. 4–5 / FiDA Art. 8) ──
  // Read-only projection over the immutable, hash-chained audit log. The /export
  // endpoint returns a raw file (json-ld/csv) — use downloadFile() from apiClient.
  async getDataAccessJournal(params?: { action?: string; dateFrom?: string; dateTo?: string; search?: string; page?: number; limit?: number }): Promise<API.DataAccessJournalResponse> {
    if (shouldUseMockData()) return mockData.getDataAccessJournal(params);
    return this.get('/api/v1/data-access-journal', params);
  }

  async getDataAccessJournalSummary(): Promise<API.DataAccessJournalSummaryResponse> {
    if (shouldUseMockData()) return mockData.getDataAccessJournalSummary();
    return this.get('/api/v1/data-access-journal/summary');
  }

  // ── 4b. THIRD-PARTY DATA-SHARING CONSENT (EU Data Act + FiDA) ─────────
  // Auto-Revoke surface: withdrawing a consent immediately revokes every
  // approved data-access request for that recipient (DSGVO Art. 7 Abs. 3 /
  // EU Data Act Art. 6 / FiDA Art. 8 — Widerruf sofort wirksam).
  async listDataAccessConsents(params?: { status?: string; recipientRef?: string; search?: string; limit?: number }): Promise<API.DataAccessConsentListResponse> {
    if (shouldUseMockData()) return mockData.listDataAccessConsents(params);
    return this.get('/api/v1/data-access-consent', params);
  }

  async getDataAccessConsent(id: string): Promise<API.DataAccessConsentDetailResponse> {
    if (shouldUseMockData()) return mockData.getDataAccessConsent(id);
    return this.get(`/api/v1/data-access-consent/${encodeURIComponent(id)}`);
  }

  async createDataAccessConsent(data: any): Promise<API.DataAccessConsentDetailResponse> {
    if (shouldUseMockData()) return mockData.createDataAccessConsent(data);
    return this.post('/api/v1/data-access-consent', data);
  }

  async withdrawDataAccessConsent(id: string, withdrawalReason?: string): Promise<API.DataAccessConsentWithdrawResponse> {
    if (shouldUseMockData()) return mockData.withdrawDataAccessConsent(id, withdrawalReason);
    return this.post(`/api/v1/data-access-consent/${encodeURIComponent(id)}/withdraw`, { withdrawalReason });
  }

  // No deleteDataAccessConsent: a consent is append-only Art. 5/6 evidence —
  // revoke via withdrawDataAccessConsent (the backend has no hard-delete route).

  // ── 5. BIAS STRATIFICATION AUDITS ─────────────────────────────────────
  async listBiasAudits(params?: { agentId?: string; period?: string; status?: string; limit?: number }): Promise<API.BiasStratificationListResponse> {
    if (shouldUseMockData()) return mockData.listBiasAudits(params);
    return this.get('/api/bias-audit', params);
  }

  async getBiasAudit(id: string): Promise<API.BiasStratificationDetailResponse> {
    if (shouldUseMockData()) return mockData.getBiasAudit(id);
    return this.get(`/api/bias-audit/${encodeURIComponent(id)}`);
  }

  async createBiasAudit(data: any): Promise<API.BiasStratificationDetailResponse> {
    if (shouldUseMockData()) return mockData.createBiasAudit(data);
    return this.post('/api/bias-audit', data);
  }

  // ── 6. DATA QUALITY VALIDATIONS ──────────────────────────────────────
  async listDataQualityValidations(params?: { limit?: number }): Promise<API.DataQualityListResponse> {
    return this.get('/api/data-quality', params);
  }

  async getDataQualityValidation(id: string): Promise<API.DataQualityDetailResponse> {
    return this.get(`/api/data-quality/${encodeURIComponent(id)}`);
  }

  async createDataQualityValidation(data: any): Promise<API.DataQualityDetailResponse> {
    return this.post('/api/data-quality', data);
  }

  // ── 7. ARTICLE 22 EXPLANATIONS ───────────────────────────────────────
  async listArt22Explanations(params?: { appealStatus?: string; limit?: number }): Promise<API.Art22ListResponse> {
    if (shouldUseMockData()) return mockData.listArt22Explanations(params);
    return this.get('/api/explanations', params);
  }

  async getArt22Explanation(id: string): Promise<API.Art22DetailResponse> {
    if (shouldUseMockData()) return mockData.getArt22Explanation(id);
    return this.get(`/api/explanations/${encodeURIComponent(id)}`);
  }

  async getArt22ExplanationForTrace(traceId: string): Promise<API.Art22DetailResponse> {
    if (shouldUseMockData()) return mockData.getArt22ExplanationForTrace(traceId);
    return this.get(`/api/explanations/trace/${encodeURIComponent(traceId)}`);
  }

  async requestArt22Appeal(id: string, dataSubjectStatement?: string): Promise<API.Art22DetailResponse> {
    if (shouldUseMockData()) return mockData.requestArt22Appeal(id, dataSubjectStatement);
    // Art. 22(3)/§ 37 BDSG: the data subject may submit their point of view when contesting.
    return this.post(`/api/explanations/${encodeURIComponent(id)}/appeal`, { dataSubjectStatement });
  }

  async reviewArt22Appeal(id: string, outcome: string, reviewNotes?: string): Promise<API.Art22DetailResponse> {
    if (shouldUseMockData()) return mockData.reviewArt22Appeal(id, outcome, reviewNotes);
    // Backend route is POST /:id/appeal/resolve, controller reads { outcome, reviewNotes }
    // (was POST /:id/appeal-review with { status, notes } — a 3-way mismatch → 404).
    return this.post(`/api/explanations/${encodeURIComponent(id)}/appeal/resolve`, { outcome, reviewNotes });
  }

  // ── 8. KYC DECISIONS (GwG) ───────────────────────────────────────────
  async listKycDecisions(params?: { status?: string; sorgfaltsstufe?: string; customerRef?: string; limit?: number }): Promise<API.KycListResponse> {
    if (shouldUseMockData()) return mockData.listKycDecisions(params);
    return this.get('/api/kyc', params);
  }

  async getKycDecision(id: string): Promise<API.KycDetailResponse> {
    if (shouldUseMockData()) return mockData.getKycDecision(id);
    return this.get(`/api/kyc/${encodeURIComponent(id)}`);
  }

  async createKycDecision(data: any): Promise<API.KycDetailResponse> {
    if (shouldUseMockData()) return mockData.createKycDecision(data);
    return this.post('/api/kyc', data);
  }

  async updateKycDecision(id: string, data: any): Promise<API.KycDetailResponse> {
    if (shouldUseMockData()) return mockData.updateKycDecision(id, data);
    // Backend-Route ist PATCH (PUT war stilles 404).
    return this.patch(`/api/kyc/${encodeURIComponent(id)}`, data);
  }

  async verifyKycDecision(id: string): Promise<API.KycDetailResponse> {
    if (shouldUseMockData()) return mockData.verifyKycDecision(id);
    return this.post(`/api/kyc/${encodeURIComponent(id)}/verify`, {});
  }

  async rejectKycDecision(id: string, reason?: string): Promise<API.KycDetailResponse> {
    if (shouldUseMockData()) return mockData.rejectKycDecision(id, reason);
    return this.post(`/api/kyc/${encodeURIComponent(id)}/reject`, { reason });
  }

  async markKycReviewDue(id: string): Promise<API.KycDetailResponse> {
    if (shouldUseMockData()) return mockData.markKycReviewDue(id);
    // Backend-Route ist /review-due (/mark-review-due war stilles 404).
    return this.post(`/api/kyc/${encodeURIComponent(id)}/review-due`, {});
  }

  // ── 9. SUSPICION CASES (GwG §43) ──────────────────────────────────────
  async listSuspicionCases(params?: { investigationStatus?: string; decision?: string; limit?: number }): Promise<API.SuspicionListResponse> {
    if (shouldUseMockData()) return mockData.listSuspicionCases(params);
    return this.get('/api/suspicion-cases', params);
  }

  async getSuspicionCase(id: string): Promise<API.SuspicionDetailResponse> {
    if (shouldUseMockData()) return mockData.getSuspicionCase(id);
    return this.get(`/api/suspicion-cases/${encodeURIComponent(id)}`);
  }

  async createSuspicionCase(data: any): Promise<API.SuspicionDetailResponse> {
    if (shouldUseMockData()) return mockData.createSuspicionCase(data);
    return this.post('/api/suspicion-cases', data);
  }

  async updateSuspicionCase(id: string, data: any): Promise<API.SuspicionDetailResponse> {
    if (shouldUseMockData()) return mockData.updateSuspicionCase(id, data);
    // Backend route is PATCH /:id (not PUT).
    return this.patch(`/api/suspicion-cases/${encodeURIComponent(id)}`, data);
  }

  async transitionSuspicionStatus(id: string, status: string): Promise<API.SuspicionDetailResponse> {
    if (shouldUseMockData()) return mockData.transitionSuspicionStatus(id, status);
    // Backend reads req.body.investigationStatus (not `status`).
    return this.post(`/api/suspicion-cases/${encodeURIComponent(id)}/transition`, { investigationStatus: status });
  }

  async decideSuspicionCase(
    id: string,
    decision: string,
    decisionRationale?: string,
    fiuReferenceNumber?: string,
  ): Promise<API.SuspicionDetailResponse> {
    if (shouldUseMockData()) return mockData.decideSuspicionCase(id, decision, decisionRationale, fiuReferenceNumber);
    // Backend reads decisionRationale + fiuReferenceNumber (not `rationale`).
    return this.post(`/api/suspicion-cases/${encodeURIComponent(id)}/decide`, {
      decision,
      decisionRationale,
      fiuReferenceNumber,
    });
  }

  // ── 10. FAIRNESS REMEDIATION ─────────────────────────────────────────
  async listFairnessRemediations(params?: { status?: string; agentId?: string; dimension?: string; limit?: number }): Promise<API.FairnessRemediationListResponse> {
    if (shouldUseMockData()) return mockData.listFairnessRemediations(params);
    return this.get('/api/fairness-remediation', params);
  }

  async getFairnessRemediation(id: string): Promise<API.FairnessRemediationDetailResponse> {
    if (shouldUseMockData()) return mockData.getFairnessRemediation(id);
    return this.get(`/api/fairness-remediation/${encodeURIComponent(id)}`);
  }

  async createFairnessRemediation(data: any): Promise<API.FairnessRemediationDetailResponse> {
    if (shouldUseMockData()) return mockData.createFairnessRemediation(data);
    return this.post('/api/fairness-remediation', data);
  }

  async decideFairnessRemediation(id: string, status: string, rejectionReason?: string): Promise<API.FairnessRemediationDetailResponse> {
    if (shouldUseMockData()) return mockData.decideFairnessRemediation(id, status, rejectionReason);
    // Backend contract (fairnessRemediationController.decide) reads `decision`,
    // not `status`. Sending `{ status }` made decision undefined → 400. The
    // caller still passes the chosen value as `status` ('accepted'|'rejected').
    return this.post(`/api/fairness-remediation/${encodeURIComponent(id)}/decide`, { decision: status, rejectionReason });
  }

  async markFairnessRemediationImplemented(id: string): Promise<API.FairnessRemediationDetailResponse> {
    if (shouldUseMockData()) return mockData.markFairnessRemediationImplemented(id);
    return this.post(`/api/fairness-remediation/${encodeURIComponent(id)}/implement`, {});
  }

  async revertFairnessRemediation(id: string, rejectionReason?: string): Promise<API.FairnessRemediationDetailResponse> {
    if (shouldUseMockData()) return mockData.revertFairnessRemediation(id, rejectionReason);
    return this.post(`/api/fairness-remediation/${encodeURIComponent(id)}/revert`, { rejectionReason });
  }

  // ── 11. AUTOMATED DECISION REGISTER ──────────────────────────────────
  async listAutomatedDecisions(params?: { status?: string; decisionType?: string; limit?: number }): Promise<API.AutomatedDecisionListResponse> {
    if (shouldUseMockData()) return mockData.listAutomatedDecisions(params);
    return this.get('/api/automated-decisions', params);
  }

  async getAutomatedDecision(id: string): Promise<API.AutomatedDecisionDetailResponse> {
    if (shouldUseMockData()) return mockData.getAutomatedDecision(id);
    return this.get(`/api/automated-decisions/${encodeURIComponent(id)}`);
  }

  async createAutomatedDecision(data: any): Promise<API.AutomatedDecisionDetailResponse> {
    if (shouldUseMockData()) return mockData.createAutomatedDecision(data);
    return this.post('/api/automated-decisions', data);
  }

  async updateAutomatedDecision(id: string, data: any): Promise<API.AutomatedDecisionDetailResponse> {
    if (shouldUseMockData()) return mockData.updateAutomatedDecision(id, data);
    // Backend route is PATCH /:id (was this.put → PUT, a method mismatch → 404).
    return this.patch(`/api/automated-decisions/${encodeURIComponent(id)}`, data);
  }

  async retireAutomatedDecision(id: string, reason?: string): Promise<API.AutomatedDecisionDetailResponse> {
    if (shouldUseMockData()) return mockData.retireAutomatedDecision(id, reason);
    return this.post(`/api/automated-decisions/${encodeURIComponent(id)}/retire`, { reason });
  }
}

// Export singleton instance
export const api = new AdjudonAPI();